/**
 * Touch-first HUD + input sampler — "Sea of Solana" premium naval skin.
 * Left thumb steers (auto-centering helm pad), a compact right-thumb action cluster fires the
 * broadsides (dominant) with smaller utility taps (sail throttle / anchor / bail / ammo).
 * Everything is also keyboard-mapped (desktop + headless Chrome); sampleInput() is read at the
 * fixed input rate.
 *
 * Visual language: dark translucent glass panels, gold/bronze trim, Solana cyan→purple accents.
 * NO backdrop-filter (it repaints the blurred region over the live WebGL canvas every frame —
 * costly on mobile); the frosted look is faked with layered gradients + inner highlights + shadows.
 * Layout is vw-clamped so the helm (bottom-left) and the action cluster (bottom-right) never collide
 * on a phone, and the heading compass lives in the CHART corner (no centered panel to overlap).
 */
import { AOI_RADIUS, Btn, CANNON, CLAN_MARKERS, ECONOMY, GRAPPLE, INPUT_HZ, SHIP, TROPHY, UX_HINTS, WORLD_SIZE, AMMO, AMMO_COUNT, AMMO_ROUND_MAX, AmmoType, ShipFlag, TERRITORY, buffOf, packAmmoBits, materialById, fishSpeciesRarity, FISH_RARITY_COLOR, type FishRarity, type SelfState } from '@piratas/protocol';
import { itemIcon } from './itemicon.js';
import { warehouseChipView } from './uxhints.js';
import { CARGO_CHIP_HINT, CHEST_CHIP_HINT, HOLD_CHIP_EMPTY } from './carrystate.js';
import { fmtResetIn, msUntilUtcMidnight } from './dailyreset.js';
import { helmAutoSail, helmWeighAnchor } from './helmsail.js';
import { bindHudLayoutAutoApply } from './hudlayout.js';
import { formatKnots, msToKnots, pointOfSail, wrapPi } from './navmath.js';
import type { InterpShip } from './net.js';
import { injectParchmentCss, parchmentTexture } from './parchment.js';

/** 🏆 Renown tap-explainer — the prestige/ranking score (NOT the wall figureheads). Numbers are read straight
 *  from TROPHY so the copy can never drift from the server rule: sink a captain = +KILL, get sunk = DEATH,
 *  floored at 0. Owner ask: players didn't know what it was, why it dropped, or that it can't go negative. */
const RENOWN_HINT = `🏆 Renown — your pirate prestige. +${TROPHY.KILL} for every captain you sink, ${TROPHY.DEATH} when you're sunk (never below 0). Raid bases for more. Climb the Top Captains ladder.`;

/** HUD labels for the ammo cycle button (index = AmmoType). */
const AMMO_LABELS = ['ROUND', 'CHAIN', 'GRAPE'] as const;
/** Raster ammo icons (GPT-authored, alpha-cut) — read from the shared AMMO table so the HUD button,
 *  the base resupply rows and the foundry craft rows all point at the same asset. */
const AMMO_ICONS = [AMMO[AmmoType.ROUND].icon, AMMO[AmmoType.CHAIN].icon, AMMO[AmmoType.GRAPE].icon] as const;
/** First-use explainer per ammo type (owner: "round/chain/grape — o que é?"). Verified against the AMMO
 *  table + fireBroadside/hitShip: round = full-damage free default; chain = half hull dmg but tangles rigging
 *  (mobility debuff) + longer range; grape = a short-range spray of double pellets, brutal point-blank. */
const AMMO_HINTS = [
  '● Round shot — solid iron, full hull damage. Powder is finite: buy or forge more at any port.',
  '⛓ Chain shot — shreds rigging to slow your quarry; lighter on the hull. Buy it at your dock or any port.',
  '✸ Grape shot — a short-range spray of pellets, brutal point-blank. Buy it at your dock or any port.',
] as const;
/** Discrete sail throttle steps for the compact tap-cycle button (keyboard W/S stays analog). */
const SAIL_STEPS = [0, 0.5, 1] as const;
const SAIL_NAMES = ['FURLED', 'HALF', 'FULL'] as const;
/** Inline cannon glyph for the FIRE buttons (barrel + carriage wheels), points to PORT by default. */
const CANNON_SVG = `<svg class="cic" viewBox="0 0 30 20" aria-hidden="true"><g fill="currentColor"><rect x="7" y="4.5" width="16" height="5.2" rx="2" transform="rotate(-13 15 7)"/><circle cx="10" cy="15" r="3.1"/><circle cx="19" cy="15.4" r="2.4"/><rect x="6" y="14" width="16" height="2.4" rx="1.2"/></g></svg>`;

/** ⚑ Armada chart-mark glyph set (index = protocol MarkIcon 0..5). Hand-inked SVGs for the picker buttons —
 *  the SAME silhouettes drawMarkGlyph strokes onto the canvas pins, so button and stamp read as one seal.
 *  Never raw emoji: these are chart stamps in the Armada's teal ink. */
const MARK_GLYPHS: { name: string; svg: string }[] = [
  { name: 'DANGER', svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="9.4" r="5.6"/><circle cx="9.8" cy="9" r="1.05" fill="currentColor" stroke="none"/><circle cx="14.2" cy="9" r="1.05" fill="currentColor" stroke="none"/><line x1="12" y1="12.6" x2="12" y2="14.4"/><line x1="6.4" y1="16.6" x2="17.6" y2="21"/><line x1="17.6" y1="16.6" x2="6.4" y2="21"/></svg>` },
  { name: 'RALLY', svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="4.6" r="1.9"/><line x1="12" y1="6.5" x2="12" y2="17.5"/><line x1="7.8" y1="9.6" x2="16.2" y2="9.6"/><path d="M5.4 14.5c.4 3.4 3 5.6 6.6 5.6s6.2-2.2 6.6-5.6"/><path d="M5.4 14.5l-1.7 1.7M18.6 14.5l1.7 1.7"/></svg>` },
  { name: 'LOOT', svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4.5 11h15v8h-15z"/><path d="M4.5 11c0-3.2 2.2-5.4 7.5-5.4S19.5 7.8 19.5 11"/><circle cx="12" cy="14.6" r="1.5" fill="currentColor" stroke="none"/></svg>` },
  { name: 'FISHING', svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.5 12c2.6-3.4 6-5 9.5-5 2.6 0 4.6 1.6 5.8 3.4-1.2 1.8-3.2 3.4-5.8 3.4-3.5 0-6.9-1.6-9.5-5z" transform="translate(0,3.2)"/><path d="M18.8 12.1l3 -2.6v6l-3-2.6" transform="translate(0,1.4)"/><circle cx="7.6" cy="14.4" r="0.95" fill="currentColor" stroke="none"/></svg>` },
  { name: 'SIGHTED', svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" aria-hidden="true"><path d="M2.8 12c2.8-4.2 6-6.2 9.2-6.2S18.4 7.8 21.2 12c-2.8 4.2-6 6.2-9.2 6.2S5.6 16.2 2.8 12z"/><circle cx="12" cy="12" r="2.6"/><circle cx="12" cy="12" r="0.9" fill="currentColor" stroke="none"/></svg>` },
  { name: 'MARK', svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>` },
];

export interface InputState { rudder: number; sailTarget: number; anchor: boolean; buttons: number; elevation: number; }
export interface MinimapData {
  selfX: number; selfY: number; selfHeading: number;
  /** Global wind direction (radians, world frame) — drives the wind rose folded into the chart ring. */
  windDir?: number;
  ships: InterpShip[];
  allyIds?: Set<number>; // ship ids of clan-mates — drawn teal instead of enemy-red
  chests?: { x: number; y: number }[]; // treasure chests — gold glints on the chart (decluttered: near-only + zones)
  shoals?: { x: number; y: number }[]; // fishing shoals — calm teal ripples so a spot is discoverable (v43)
  cursed?: { x: number; y: number } | null; // the active Cursed Treasure — a pulsing purple skull marker
  territory?: Map<number, string>; // regionId → controlling clan tag
  myTag?: string;        // my clan tag (to tint my regions vs rivals')
  rally?: { x: number; y: number } | null; // clan rally point flag
  /** Clan chart markers (v54) — persistent Armada intel pins, server-synced to members only. `mine` =
   *  authored by this captain (author-remove right); moderation (officer/leader) rides canModerateMarks. */
  clanMarks?: { id: number; icon: number; x: number; y: number; label: string; author: string; mine: boolean }[];
  /** True when this captain may remove ANY clan mark (officer/leader) — authors can always remove their own. */
  canModerateMarks?: boolean;
  /** True when the captain is in a clan — gates the long-press mark gesture with a friendly hint otherwise. */
  inClan?: boolean;
  islands: { x: number; y: number; radius?: number; id?: number }[];
  outposts: { x: number; y: number; radius: number; name?: string; capital?: boolean }[];
  /** Trade-wind lanes (outpost→outpost segments) drawn as faint dashed currents on the chart. */
  tradeLanes?: { ax: number; ay: number; bx: number; by: number }[];
  /** Sinks P3: composed "sight" multiplier (1 = none) — the active viewMul oil (Spyglass Tonic / Wraithlight)
   *  × the Lookout's Spyglass charm's +minimap range. > 1 draws a wider watch ring + larger ship blips (a
   *  client-only render cue — NEVER the network AOI). */
  sightMul?: number;
  /** Wave 3.1: drifting Messages in a Bottle — faint green glints to sail toward. */
  bottles?: { x: number; y: number }[];
  /** Wave 3.5: the White Whale marker (null = not hunting) — a pulsing blue-white spout blip. */
  whale?: { x: number; y: number } | null;
  /** v54 Wraithlight ghost-sight — SERVER-decided spectral whispers while the draught burns: rich wrecks
   *  (kind 0) + Ghost Fleet ships (kind 1), as coarse bearing+distance from SELF. Drawn as cold green wisps. */
  spectral?: { kind: number; id: number; bearing: number; dist: number }[];
  /** Wave 3.8: the Eye of the Storm calm zone (null = none) — a soft cyan ring. */
  eye?: { x: number; y: number } | null;
  /** Wave 3.9: an active treasure-map dig site (null = none) — a red X to steer to. */
  digX?: { x: number; y: number } | null;
  /** The player's OWN base (null until S_BASES arrives) — a gold home marker, always on the chart, so a
   *  lost captain can always see (and, via the HOME button, auto-sail to) their harbour. */
  homeBase?: { x: number; y: number } | null;
  /** CARRYING a chest → the NEAREST outpost to run it to (null when not carrying). Drawn as a bold pulsing
   *  gold "SELL" ring with a course line from the ship — the exact "onde vendo?" answer, at the moment of
   *  need. Computed in main.ts from self.chestCount + the outpost list (client-only, no wire). */
  cashInAt?: { x: number; y: number } | null;
}

const CSS = `
#hud {
  position:fixed; inset:0; pointer-events:none; color:var(--ink); font-family:inherit;
  --ink:#eef6ff; --ink-dim:rgba(225,239,255,.62);
  --gold:#e8c074; --gold-soft:#f2d79b; --bronze:#8a6328;
  --cyan:#4fe6da; --purple:#9d82ff; --ember:#ff7a3c; --danger:#ff5060; --good:#54e6a0;
  --glass:linear-gradient(165deg, rgba(16,30,46,.86), rgba(6,14,26,.9));
  --edge:rgba(120,180,220,.16); --trim:rgba(232,192,116,.34);
}
#hud .panel { position:absolute; pointer-events:auto; }
.glass {
  background:var(--glass); border:1px solid var(--edge); border-radius:16px;
  box-shadow:0 8px 26px rgba(0,8,16,.5), inset 0 1px 0 rgba(255,255,255,.07), inset 0 0 0 1px rgba(0,0,0,.18);
  position:relative;
}
.glass::before { content:''; position:absolute; inset:0; border-radius:inherit; padding:1px; pointer-events:none;
  background:linear-gradient(160deg, var(--trim), rgba(232,192,116,0) 42%, rgba(157,130,255,0) 66%, rgba(157,130,255,.16));
  -webkit-mask:linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0); -webkit-mask-composite:xor; mask-composite:exclude; }
/* the CHART's own paper as a DOM surface: aged sheet + ink neat-line + brass corner studs — the same
   language renderChart bakes, so a .parch panel reads as a chart-card, never a generic cardbox. */
.parch { border-radius:10px; color:#3b2712;
  background:var(--pg-paper) center/cover, #e4cf9b;
  border:1px solid rgba(58,40,16,.6);
  box-shadow:0 8px 22px rgba(0,6,12,.5), inset 0 0 0 2.5px rgba(58,40,16,.15), inset 0 0 26px rgba(70,44,18,.18); }
.parch::before, .parch::after { content:''; position:absolute; width:6px; height:6px; border-radius:50%; pointer-events:none; z-index:2;
  background:radial-gradient(circle at 35% 32%, #f0d48a, #c99a3a 58%, #7a5416 100%); box-shadow:0 0 0 1px rgba(50,32,8,.7); }
.parch::before { top:5px; left:5px; } .parch::after { top:5px; right:5px; }

/* ----- TOP-LEFT: captain status card — an aged chart-card (paper, ink, brass); HEALTH is the hero ----- */
#topbar { top:calc(env(safe-area-inset-top,8px) + 6px); left:calc(env(safe-area-inset-left,0px) + 10px);
  width:min(46vw,180px); padding:8px 10px 7px; display:flex; flex-direction:column; gap:6px; }
/* engraved HULL gauge: inked trough on the paper, ink-washed fill */
#hpwrap { position:relative; height:22px; border-radius:5px; overflow:hidden; background:rgba(74,52,24,.16);
  border:1px solid rgba(58,40,16,.55); box-shadow:inset 0 1px 3px rgba(40,24,6,.4); }
#hp { height:100%; width:100%; border-radius:4px; transition:width .18s ease, background .25s, box-shadow .25s;
  background:linear-gradient(90deg,#2e7a3f,#5cab6a 60%,#8fcf98); box-shadow:inset 0 1px 0 rgba(255,248,220,.35), inset 0 -2px 3px rgba(40,24,6,.3); }
#hpwrap .hp-txt { position:absolute; left:9px; top:0; bottom:0; display:flex; align-items:center; gap:4px;
  font-size:10px; font-weight:800; letter-spacing:1.5px; color:#2c1c08; text-shadow:0 1px 0 rgba(247,236,208,.55); }
#hpwrap .hp-pct { position:absolute; right:9px; top:0; bottom:0; display:flex; align-items:center;
  font-size:11px; font-weight:800; color:#2c1c08; text-shadow:0 1px 0 rgba(247,236,208,.55); font-variant-numeric:tabular-nums; }
#hp.full { box-shadow:inset 0 1px 0 rgba(255,248,220,.5), 0 0 9px rgba(120,220,150,.45); }
#hp.low { background:linear-gradient(90deg,#a2231d,#d24a38); animation:hpLow 1s ease-in-out infinite; }
#hpwrap.hit { animation:hpHit .34s ease-out; }
@keyframes hpLow { 0%,100%{ box-shadow:0 0 5px rgba(210,60,44,.45) } 50%{ box-shadow:0 0 15px rgba(210,60,44,.9) } }
@keyframes hpHit { 0%{ box-shadow:0 0 0 2px rgba(190,42,36,.95), inset 0 0 0 1px rgba(255,248,220,.5) } 100%{ box-shadow:inset 0 1px 3px rgba(40,24,6,.4) } }
#topbar .chips { display:flex; gap:6px; align-items:center; flex-wrap:wrap; }
/* counters as stamped aged-brass seals (the chart's .pg-seal voice) */
#topbar .chip { display:flex; align-items:center; gap:4px; padding:2px 8px 2px 6px; border-radius:4px;
  background:linear-gradient(180deg, rgba(222,186,102,.5), rgba(150,110,40,.3)); border:1px solid rgba(90,60,16,.5);
  box-shadow:inset 0 1px 0 rgba(255,246,214,.5), inset 0 -1px 2px rgba(60,38,10,.2); font-weight:800; font-size:13px; line-height:1; }
#topbar .chip .ic { font-size:11px; filter:drop-shadow(0 1px 1px rgba(60,38,10,.5)); }
#topbar .chip.coin { color:#7a4e12; } #topbar .chip.trophy { color:#6a4515; }
#topbar .chip.sun { color:#7a5a1a; opacity:.9; } #topbar .chip.sun.full { color:#a2481d; opacity:1; }
#topbar .chip.cargo { color:#1c5c48; display:none; }
/* Cargo Runs weight chip: a 💨 speed glyph makes "−11%" read plainly as SPEED lost, and the number degrades
   neutral → amber → red as the laden penalty climbs toward its −40% max. */
#topbar .chip .cr-pen { font-variant-numeric:tabular-nums; }
#topbar .chip .cr-pen.amber { color:#a2641d; }
#topbar .chip .cr-pen.red { color:#a2231d; }
#topbar .chip.hold { color:#4f671d; display:none; }
#topbar .chip.chest { color:#8a5c10; display:none; }
/* 🏭 warehouse-fill chip: a progressive bar behind the "NN%" that fills + warms as passive cargo piles up,
   flipping to the strong "HAUL READY" at the top. The bar sits UNDER the icon/label, clipped to the pill. */
#topbar #haulchip { position:relative; overflow:hidden; display:none; }
#topbar #haulchip .ic, #topbar #haulchip #hault { position:relative; z-index:1; }
#topbar #haulchip #hault { font-variant-numeric:tabular-nums; }
#topbar #haulchip .hw-fill { position:absolute; left:0; top:0; bottom:0; width:0; z-index:0; border-radius:4px 0 0 4px;
  transition:width .35s ease; background:linear-gradient(90deg, rgba(200,144,30,.2), rgba(200,144,30,.38)); }
#topbar #haulchip.wh-lo  { color:#7a5a1a; opacity:.92; }
#topbar #haulchip.wh-lo  .hw-fill { background:linear-gradient(90deg, rgba(150,120,60,.16), rgba(150,120,60,.28)); }
#topbar #haulchip.wh-mid { color:#8a5c14; opacity:1; }
#topbar #haulchip.wh-mid .hw-fill { background:linear-gradient(90deg, rgba(190,130,40,.22), rgba(190,130,40,.38)); }
#topbar #haulchip.wh-hi  { color:#a2481d; opacity:1; }
#topbar #haulchip.wh-hi  .hw-fill { background:linear-gradient(90deg, rgba(190,90,40,.26), rgba(190,90,40,.44)); }
#topbar #haulchip.full { color:#a2481d; opacity:1; animation:haulRdy 1.5s ease-in-out infinite; }
#topbar #haulchip.full .hw-fill { width:100% !important; background:linear-gradient(90deg, rgba(200,100,40,.34), rgba(210,120,40,.52)); }
@keyframes haulRdy { 0%,100%{ box-shadow:0 0 7px -3px rgba(190,110,40,.5) } 50%{ box-shadow:0 0 14px -1px rgba(200,120,40,.9) } }
/* Fix 4 frente 2: tappable carry chips (mobile has no hover) — an invisible halo grows the touch
   target to ~44px tall without changing the visual; ±3px horizontal so neighbours never overlap. */
#topbar .chip.tap { position:relative; cursor:pointer; }
#topbar .chip.tap::after { content:''; position:absolute; inset:-13px -3px; }
#topbar .kd { font-size:11px; font-weight:700; color:#6a4c22; letter-spacing:.3px; margin-left:1px; }
#topbar .badges { display:flex; flex-wrap:wrap; gap:5px; }
/* state badges read as inked STAMPS pressed on the paper */
#topbar .badge { display:none; font-size:10px; font-weight:800; letter-spacing:.5px; padding:3px 7px; border-radius:4px; border:1px solid currentColor; }
#rigwarn { color:#f7e0d8; background:rgba(150,40,30,.88); border-color:rgba(90,20,14,.8) !important; }
#grappleind { color:#f7ecd0; background:rgba(140,95,25,.9); border-color:rgba(90,56,14,.8) !important; }
#dockind { color:#e0f2fa; background:rgba(32,92,122,.88); border-color:rgba(16,52,72,.8) !important; }
#statline { display:none; font-size:10px; color:rgba(90,64,30,.75); letter-spacing:.4px; font-variant-numeric:tabular-nums; }
#hud.debug #statline { display:block; }

/* ----- TOP-RIGHT: the nautical CHART. The canvas draws its OWN wood band + gilt rope + brass studs, so
   the DOM wrapper is a bare easel — NO cardbox around the chart, ever. Its instruments (open the world
   chart / sail home) are 44px brass medallions on a rail hanging off the chart's port side. ----- */
#mapwrap { top:calc(env(safe-area-inset-top,8px) + 6px); right:calc(env(safe-area-inset-right,0px) + 8px);
  width:132px; padding:0; pointer-events:none; }
#minimap { display:block; width:132px; height:132px; cursor:crosshair; pointer-events:auto;
  background:transparent; filter:drop-shadow(0 8px 20px rgba(0,6,12,.55)); }
#mapwrap .map-rail { position:absolute; top:1px; right:100%; margin-right:7px; display:flex; flex-direction:column; gap:8px; }
/* a struck-brass instrument button: engraved ink glyph + a tiny Cinzel label, ≥44px touch */
.chart-btn { pointer-events:auto; cursor:pointer; width:44px; height:44px; border-radius:50%;
  display:flex; flex-direction:column; align-items:center; justify-content:center; gap:0; padding:0;
  color:#2c1c08; border:1px solid rgba(50,32,8,.85);
  background:radial-gradient(circle at 36% 30%, #f4da92, #cba043 52%, #8a5c1a 88%, #6b4211);
  box-shadow:0 4px 10px rgba(0,6,12,.55), inset 0 1px 0 rgba(255,248,214,.65), inset 0 -3px 5px rgba(60,38,10,.45); }
.chart-btn svg { width:19px; height:19px; margin-top:1px; filter:drop-shadow(0 1px 0 rgba(255,248,214,.35)); }
.chart-btn .cb-lbl { font-family:'Cinzel',Georgia,serif; font-size:7px; font-weight:800; letter-spacing:.12em; line-height:1.1;
  color:rgba(44,28,8,.92); text-shadow:0 1px 0 rgba(255,248,214,.4); pointer-events:none; }
.chart-btn:active { transform:scale(.92); }
/* HOME hides until the base is known; while the home course is engaged the medallion is LIT gold */
.chart-btn.map-home { display:none; }
.chart-btn.map-home.shown { display:flex; }
.chart-btn.map-home.active { border-color:#e8c074;
  box-shadow:0 4px 10px rgba(0,6,12,.55), 0 0 14px rgba(242,215,155,.75), inset 0 1px 0 rgba(255,248,214,.75), inset 0 -3px 5px rgba(60,38,10,.4); }
/* the chart's one-line whisper ("tap to sail" / "sell at the lit port") — an aged paper tag under the sheet */
#mapwrap .map-hint { position:absolute; top:100%; margin-top:5px; right:2px; max-width:128px; pointer-events:none;
  padding:2px 8px 3px; border-radius:3px; font:italic 700 9.5px Georgia,'Times New Roman',serif; color:#5a3a12; text-align:center;
  background:var(--pg-paper) center/cover, #e4cf9b; border:1px solid rgba(58,40,16,.5);
  box-shadow:0 3px 8px rgba(0,6,12,.4), inset 0 0 0 1.5px rgba(58,40,16,.14); }
#mapwrap .map-hint.selling { color:#7a4e12; font-style:normal; font-weight:800; animation:hintSell 1.2s ease-in-out infinite; }
@keyframes hintSell { 0%,100%{ opacity:.75 } 50%{ opacity:1 } }
/* full-world chart overlay (M / 🗺). The blown-up canvas draws the SAME wood/rope/brass frame as the
   minimap — so the overlay adds NO cardbox around it: a deep-sea dusk backdrop (no backdrop-filter — it
   repaints over the live WebGL every frame), a parchment title CARTOUCHE pinned across the top band
   (classic sea-chart title block) and a brass ✕ medallion sunk into the frame's corner. */
#bigmap { position:fixed; inset:0; z-index:60; display:none; align-items:center; justify-content:center; pointer-events:auto;
  background:radial-gradient(120% 95% at 50% 44%, rgba(10,24,34,.6) 0%, rgba(4,12,20,.85) 62%, rgba(1,5,10,.93) 100%); }
#bigmap.open { display:flex; }
#bigmapinner { position:relative; }
#bigmapcanvas { display:block; width:min(88vw,86vh); height:min(88vw,86vh); background:transparent;
  filter:drop-shadow(0 22px 55px rgba(0,4,10,.7)); cursor:crosshair; touch-action:none; }
#bigmaphd { position:absolute; top:0; left:50%; transform:translate(-50%,-46%); z-index:2; pointer-events:none;
  display:flex; flex-direction:column; align-items:center; padding:5px 18px 6px; border-radius:4px; text-align:center;
  background:var(--pg-paper) center/cover, #e4cf9b; border:1.5px solid rgba(58,40,16,.8);
  box-shadow:0 5px 14px rgba(0,4,10,.55), inset 0 0 0 2.5px rgba(58,40,16,.18), inset 0 0 14px rgba(70,44,18,.2); }
#bigmaphd .bm-t { font-family:'Cinzel',Georgia,serif; font-size:14px; font-weight:900; letter-spacing:.22em; color:#4a2f0e;
  text-shadow:0 1px 0 rgba(255,248,225,.6); white-space:nowrap; }
#bigmaphd .bm-sub { font:italic 700 9.5px Georgia,'Times New Roman',serif; color:#6a4c22; white-space:nowrap; }
#bigmapclose { position:absolute; top:-9px; right:-9px; z-index:2; width:44px; height:44px; border-radius:50%;
  display:flex; align-items:center; justify-content:center; font-size:17px; font-weight:900; color:#2c1c08; cursor:pointer; pointer-events:auto;
  border:1px solid rgba(50,32,8,.85); background:radial-gradient(circle at 36% 30%, #f4da92, #cba043 52%, #8a5c1a 88%, #6b4211);
  box-shadow:0 4px 10px rgba(0,6,12,.6), inset 0 1px 0 rgba(255,248,214,.65), inset 0 -3px 5px rgba(60,38,10,.45); }
#bigmapclose:active { transform:scale(.9); }
/* ⚑ Armada chart-mark overlays (v54): the glyph picker (long-press on the big chart) and a pin's paper
   tag (tap a pin). Both are parchment scraps pinned ON the chart — the exact paper/ink voice of the
   title cartouche (--pg-paper + Cinzel + sepia ink), never a generic glass popover. */
#markpick, #markinfo { position:absolute; z-index:3; display:none; flex-direction:column; gap:6px;
  padding:9px 11px; border-radius:5px; color:#3b2712;
  background:var(--pg-paper) center/cover, #e4cf9b; border:1.5px solid rgba(58,40,16,.8);
  box-shadow:0 9px 22px rgba(0,4,10,.6), inset 0 0 0 2px rgba(58,40,16,.16), inset 0 0 12px rgba(70,44,18,.18); }
#markpick.open, #markinfo.open { display:flex; }
#markpick .mp-t, #markinfo .mi-t { font-family:'Cinzel',Georgia,serif; font-size:10px; font-weight:900;
  letter-spacing:.14em; color:#4a2f0e; text-transform:uppercase; white-space:nowrap; }
#markpick .mp-row { display:flex; gap:6px; }
#markpick button.mp-g { width:44px; height:44px; border-radius:50%; border:1.5px solid rgba(46,110,84,.75);
  cursor:pointer; display:flex; align-items:center; justify-content:center; padding:0; color:#1e5c46;
  background:radial-gradient(circle at 38% 30%, rgba(96,196,160,.30), rgba(52,160,120,.14) 62%, rgba(30,110,84,.24)),
             var(--pg-paper) center/cover, #e4cf9b;
  box-shadow:0 2px 6px rgba(0,4,10,.35), inset 0 1px 0 rgba(255,248,214,.5); }
#markpick button.mp-g:active { transform:scale(.9); }
#markpick button.mp-g svg { width:26px; height:26px; }
#markpick input { width:100%; box-sizing:border-box; border:0; border-bottom:1.5px dashed rgba(58,40,16,.55);
  background:transparent; font:italic 700 13px Georgia,serif; color:#3b2712; padding:3px 1px; outline:none; border-radius:0; }
#markpick input::placeholder { color:rgba(74,47,14,.5); }
#markinfo { max-width:210px; }
#markinfo .mi-l { font:italic 700 13px Georgia,serif; color:#3b2712; overflow-wrap:anywhere; }
#markinfo .mi-by { font:700 10px Georgia,serif; color:#6a4c22; }
#markinfo button.mi-rm { margin-top:2px; min-height:34px; border-radius:5px; cursor:pointer;
  border:1.5px solid rgba(122,42,28,.8); font-family:'Cinzel',Georgia,serif; font-size:10px; font-weight:900;
  letter-spacing:.1em; color:#7a2a1c; background:rgba(190,72,52,.14); }
#markinfo button.mi-rm:active { transform:scale(.95); }
/* while the big chart is up, the world floaters AND the sea-HUD furniture yield the stage — the chart
   is a captain's table moment (mirror of body.bk-open for the body-level floaters) */
body.bigmap-open #eventchip, body.bigmap-open #enterportbtn, body.bigmap-open #enterhousebtn,
body.bigmap-open #portpopchip, body.bigmap-open #profxp, body.bigmap-open #opps-btn,
body.bigmap-open #bktoggle, body.bigmap-open .cc-chat-toggle, body.bigmap-open .cc-chat-peek { display:none !important; }
body.bigmap-open #topbar, body.bigmap-open #mapwrap, body.bigmap-open #actions, body.bigmap-open #steer,
body.bigmap-open #speedo, body.bigmap-open #buffbtn, body.bigmap-open #buffpop, body.bigmap-open #killfeed { visibility:hidden; }
/* Top Captains, daily Contracts and the Sea Journal have NO sea-HUD panel on ANY device (owner order:
   "HUD limpo, nada de painel morando na tela") — all three live in the Captain's Book → Ship's Log. */

/* ----- BOTTOM-LEFT: helm joystick (glass-trimmed, slightly smaller) ----- */
#steer { left:calc(env(safe-area-inset-left,0px) + 4%); bottom:calc(env(safe-area-inset-bottom,0px) + 6%);
  width:clamp(92px,26vw,114px); aspect-ratio:1; border-radius:50%; touch-action:none; display:grid; place-items:center;
  background:radial-gradient(circle at 50% 42%, rgba(26,52,72,.5), rgba(5,14,24,.72) 74%);
  border:1px solid var(--edge); box-shadow:0 8px 24px rgba(0,8,16,.5), inset 0 0 34px rgba(79,230,218,.07); }
#steer::before { content:''; position:absolute; inset:0; border-radius:50%; padding:1px; pointer-events:none;
  background:linear-gradient(160deg, var(--trim), rgba(232,192,116,0) 50%, rgba(79,230,218,.16)); -webkit-mask:linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0); -webkit-mask-composite:xor; mask-composite:exclude; }
#steer::after { content:''; position:absolute; left:15%; right:15%; top:50%; height:4px; transform:translateY(-50%); border-radius:2px; background:linear-gradient(90deg, rgba(79,230,218,0), rgba(79,230,218,.26), rgba(79,230,218,0)); }
#steer .helm-lbl { position:absolute; top:10px; left:0; right:0; text-align:center; font-size:9px; font-weight:800; letter-spacing:2px; color:rgba(160,225,235,.6); }
#steer .ar { position:absolute; top:50%; transform:translateY(-50%); font-size:12px; color:rgba(160,225,235,.4); }
#steer .ar.l { left:12px; } #steer .ar.r { right:12px; }
/* forward-throttle affordance: push the knob UP to raise sail (mobile "push forward = go"). Hidden on desktop (W/S). */
#steer .ar.u { top:23px; left:0; right:0; text-align:center; transform:none; font-size:8px; font-weight:800; letter-spacing:1.2px; color:rgba(120,220,180,.55); }
#hud.desktop #steer .ar.u { display:none; }
#steerknob { position:relative; width:38%; aspect-ratio:1; border-radius:50%;
  background:radial-gradient(circle at 38% 32%, #e2f6ff, #46a3c4 64%, #245d78);
  box-shadow:0 4px 12px rgba(0,0,0,.55), 0 0 0 4px rgba(79,230,218,.12), 0 0 0 7px rgba(157,130,255,.08), inset 0 -3px 8px rgba(0,0,0,.3);
  transition:box-shadow .12s; left:0; }
#steer.dragging #steerknob { box-shadow:0 4px 18px rgba(0,0,0,.6), 0 0 26px rgba(79,230,218,.6), 0 0 0 5px rgba(79,230,218,.24), 0 0 0 9px rgba(157,130,255,.16), inset 0 -3px 8px rgba(0,0,0,.3); }
#steer .helm-kh { display:none; }
#hud.desktop #steer .helm-kh { display:block; position:absolute; left:0; right:0; bottom:9px; text-align:center; font-size:9px; font-weight:800; letter-spacing:1.5px; color:rgba(160,225,235,.55); pointer-events:none; }

/* ----- BOTTOM-RIGHT: compact action cluster (FIRE dominant; util as a 2×2 of small taps) ----- */
/* HUD Forge: the container is a hit-test GHOST (pointer-events:none) — only the buttons themselves
   (.nav-btn, pointer-events:auto) take touches, so a relocated fire/util group never leaves a dead zone. */
#hud #actions { right:calc(env(safe-area-inset-right,0px) + 12px); bottom:calc(env(safe-area-inset-bottom,0px) + 6%);
  display:flex; flex-direction:column; align-items:flex-end; gap:9px; pointer-events:none; }
#actions .util-grid { display:grid; grid-template-columns:repeat(2, auto); gap:8px; justify-items:end; }
#actions .fire-row { display:flex; gap:11px; align-items:flex-end; }

.nav-btn { pointer-events:auto; position:relative; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:1px;
  border-radius:15px; border:1px solid rgba(120,180,220,.2); color:var(--ink); font-weight:800; touch-action:none;
  background:linear-gradient(168deg, rgba(20,38,56,.92), rgba(8,18,30,.95));
  box-shadow:0 5px 16px rgba(0,8,16,.5), inset 0 1px 0 rgba(255,255,255,.08); transition:transform .08s, box-shadow .14s, filter .14s; }
.nav-btn::before { content:''; position:absolute; inset:0; border-radius:inherit; padding:1px; pointer-events:none;
  background:linear-gradient(160deg, var(--trim), rgba(232,192,116,0) 55%); -webkit-mask:linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0); -webkit-mask-composite:xor; mask-composite:exclude; }
.nav-btn:active { transform:scale(.93); }
.nav-btn .lbl { font-size:10px; letter-spacing:.4px; opacity:.85; } /* audit: 8.5px was sub-legible on the labels new players learn from */
.nav-btn .ic { font-size:17px; line-height:1; filter:drop-shadow(0 1px 2px #000); }
.nav-btn .kh { display:none; }
#hud.desktop .nav-btn .kh { display:block; position:absolute; top:-7px; right:-6px; font-size:9px; font-weight:800; color:#04141e; background:var(--gold); border-radius:5px; padding:1px 4px; box-shadow:0 1px 3px rgba(0,0,0,.5); letter-spacing:.3px; }

/* secondary utility buttons (sail / anchor / bail / ammo) — small rounded squares */
.nav-btn.util { width:clamp(44px,11.5vw,48px); height:clamp(44px,11.5vw,48px); } /* 44px floor — audit: 40px missed on small phones */
.nav-btn.util.on { border-color:rgba(79,230,218,.5); box-shadow:0 5px 16px rgba(0,8,16,.5), 0 0 15px rgba(79,230,218,.35), inset 0 1px 0 rgba(255,255,255,.08); color:#bff6ee; }
/* the AMMO selector (round/chain/grape) — labelled so it reads as an ammo picker, not a mystery button.
   The raster shot icons ride inside the same .ic slot the old glyphs used (medallion language untouched). */
.nav-btn.ammo .ic { font-size:15px; display:flex; align-items:center; justify-content:center; }
.nav-btn.ammo .ammo-ic { width:21px; height:21px; object-fit:contain; display:block; filter:drop-shadow(0 1px 2px #000); }
.nav-btn.ammo .lbl { font-size:8px; letter-spacing:.4px; }
.nav-btn.ammo.empty { opacity:.5; }
.nav-btn.ammo.chain { border-color:rgba(180,200,220,.5); color:#dfe8f2; }
.nav-btn.ammo.grape { border-color:rgba(255,170,110,.5); color:#ffdcc0; }
.nav-btn#bail.needs { border-color:rgba(79,200,255,.55); animation:availPulse 1.3s ease-out infinite; color:#cfeeff; }
/* frente 4: anchor DOWN is an unmistakable warm "moored/held" state (distinct from the cyan util-on) */
.nav-btn#anchor.on { border-color:rgba(255,206,110,.75); color:#ffe6a8;
  background:linear-gradient(168deg, rgba(92,62,22,.96), rgba(52,34,10,.97));
  box-shadow:0 5px 16px rgba(0,8,16,.5), 0 0 18px rgba(255,190,90,.45), inset 0 1px 0 rgba(255,255,255,.08); }
/* compact sail "throttle": a bottom-up fill shows the level; tap cycles furled/half/full.
   frente 6c: NO overflow:hidden on the button (it clipped the desktop W/S key badge) — the fill rounds itself. */
#sailfill { position:absolute; left:0; right:0; bottom:0; border-radius:14px; background:linear-gradient(0deg,#1d6fa8,#3fb0e0); opacity:.5; pointer-events:none; transition:height .15s; }
.nav-btn.sail .ic, .nav-btn.sail .lbl { position:relative; z-index:1; }

/* ----- BOTTOM-CENTER: the ship's log — a brass speed + wind gauge (Sea-of-Thieves-ish showpiece) ----- */
#speedo { position:absolute; left:50%; bottom:calc(env(safe-area-inset-bottom,0px) + 8px); transform:translateX(-50%);
  display:block; width:100px; height:100px; pointer-events:none; z-index:3; filter:drop-shadow(0 5px 14px rgba(0,8,16,.6)); }
@media (pointer:fine) { #speedo { width:112px; height:112px; } }
@media (max-width:379px) { #speedo { width:82px; height:82px; } } /* audit: clears the fire cluster at 360px */

/* primary FIRE buttons — dominant, ember, with a reload ring */
.nav-btn.fire { width:clamp(62px,17vw,78px); height:clamp(54px,15vw,66px); border-radius:16px; color:#ffe9df;
  background:linear-gradient(168deg, rgba(122,42,28,.96), rgba(70,20,16,.97)); border-color:rgba(255,140,90,.34); }
.nav-btn.fire .cic { width:30px; height:20px; filter:drop-shadow(0 1px 2px #000); }
.nav-btn.fire.r .cic { transform:scaleX(-1); }
.nav-btn.fire .lbl { color:#ffd0b8; opacity:.85; }
.nav-btn.fire .ring { position:absolute; inset:-3px; border-radius:inherit; padding:3px; pointer-events:none;
  background:conic-gradient(from -90deg, var(--cyan) calc(var(--rl,1) * 360deg), rgba(255,255,255,.06) 0);
  -webkit-mask:linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0); -webkit-mask-composite:xor; mask-composite:exclude; opacity:.95; }
.nav-btn.fire.ready { animation:fireReady .4s ease-out; }
/* ammo economy: out of powder — the broadside would be denied server-side, so the buttons read dead */
.nav-btn.fire.nopowder { opacity:.35; filter:grayscale(.7); }
.nav-btn.fire.ready .ring { background:conic-gradient(var(--good) 360deg); }
@keyframes fireReady { from { box-shadow:0 0 0 0 rgba(120,255,150,.8) } to { box-shadow:0 0 0 15px rgba(120,255,150,0) } }
@keyframes availPulse { 0%,100%{ box-shadow:0 5px 16px rgba(0,8,16,.5), 0 0 0 0 rgba(79,200,255,.5) } 70%{ box-shadow:0 5px 16px rgba(0,8,16,.5), 0 0 0 12px rgba(79,200,255,0) } }

/* mobile aim hint: floats above the fire buttons while aiming — teaches the slide-for-range gesture */
#hoistbar { position:fixed; left:50%; bottom:calc(env(safe-area-inset-bottom,0px) + 34%); transform:translateX(-50%); z-index:9; display:none;
  width:min(58vw,280px); pointer-events:none; text-align:center; }
#hoistbar.on { display:block; }
#hoistbar .cap { font-size:12.5px; font-weight:800; letter-spacing:.14em; color:#ffe6a8; text-transform:uppercase; margin-bottom:6px; text-shadow:0 2px 8px rgba(0,0,0,.7); }
#hoistbar .track { height:13px; border-radius:8px; background:rgba(10,16,24,.72); border:1px solid rgba(232,192,116,.5); overflow:hidden; box-shadow:0 6px 20px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.08); }
#hoistfill { height:100%; width:0%; border-radius:7px; background:linear-gradient(90deg,#c9942a,#f2d79b,#ffefc6); box-shadow:0 0 12px rgba(242,215,155,.7); transition:width .09s linear; }
#hoistbar .warn { font-size:11px; color:#ffd97a; margin-top:5px; opacity:.85; text-shadow:0 2px 6px rgba(0,0,0,.7); }
#aimhint { position:fixed; right:calc(env(safe-area-inset-right,0px) + 10px); bottom:calc(env(safe-area-inset-bottom,0px) + 150px); z-index:7;
  display:none; align-items:center; gap:5px; padding:5px 11px; border-radius:12px; font-size:12px; font-weight:800; letter-spacing:.3px;
  color:#9dffb4; background:rgba(4,18,12,.85); border:1px solid rgba(90,255,140,.45); box-shadow:0 4px 14px rgba(0,8,16,.5); pointer-events:none; text-shadow:0 1px 3px #042; white-space:nowrap; }
#aimhint.on { display:flex; }
#aimhint b { color:#eafff0; font-variant-numeric:tabular-nums; }
#hud.desktop #aimhint { display:none !important; } /* desktop aims range with W/S — hint is mobile-only */

/* ----- consumable buffs (2b): a quick ⚗ sip menu + active-buff badges ----- */
#topbar .badge.buff { display:none; color:#e8f7e0; background:rgba(58,110,52,.88); border-color:rgba(30,64,26,.8) !important; }
#buffbtn { position:fixed; right:calc(env(safe-area-inset-right,0px) + 16px); bottom:calc(env(safe-area-inset-bottom,0px) + 41%); z-index:8; pointer-events:auto; width:42px; height:42px; border-radius:12px; display:none; cursor:pointer;
  border:1px solid rgba(120,220,160,.42); background:linear-gradient(165deg,rgba(20,50,36,.92),rgba(8,26,18,.95)); color:#c8fff0; font-size:20px; box-shadow:0 5px 16px rgba(0,8,16,.5), inset 0 1px 0 rgba(255,255,255,.07); }
#buffbtn:active { transform:scale(.94); }
#buffpop { position:fixed; right:calc(env(safe-area-inset-right,0px) + 16px); bottom:calc(env(safe-area-inset-bottom,0px) + 47%); z-index:9; pointer-events:auto; display:none; flex-direction:column; gap:5px;
  padding:8px; border-radius:12px; background:linear-gradient(168deg,rgba(16,30,46,.95),rgba(6,14,26,.97)); border:1px solid rgba(120,220,160,.35); box-shadow:0 10px 30px rgba(0,8,16,.6); min-width:158px; }
#buffpop.on { display:flex; }
#buffpop .bph { font-size:9.5px; font-weight:800; letter-spacing:.8px; color:#9fd8b8; text-transform:uppercase; padding:0 2px 2px; }
#buffpop .bp-row { display:flex; align-items:center; gap:8px; padding:7px 9px; border-radius:8px; background:rgba(255,255,255,.05); cursor:pointer; font-size:12.5px; color:#eaf6ff; }
#buffpop .bp-row:active { background:rgba(120,220,160,.2); }
#buffpop .bp-row .bi { font-size:22px; } #buffpop .bp-row .bi img.item-icon { width:30px; height:30px; vertical-align:-8px; } #buffpop .bp-row .bn { flex:1; } #buffpop .bp-row .bl { color:#9fd8b8; font-size:10px; } #buffpop .bp-row .bc { color:#8fdca8; font-weight:800; }

/* contextual: sea-action button (fish/dive) — shown only near a shoal or wreck */
#actions #contextrow { display:none; }
.nav-btn.action { width:min(52vw,180px); height:46px; flex-direction:row; gap:7px; font-size:14px; overflow:hidden;
  background:linear-gradient(168deg, rgba(24,74,68,.95), rgba(10,38,36,.96)); border-color:rgba(90,220,200,.42); color:#c9fff3; }
.nav-btn.action .lbl { font-size:11px; opacity:.9; letter-spacing:.4px; }
.nav-btn.action.urgent { animation:availPulse .7s ease-out infinite; border-color:rgba(120,255,150,.75); color:#e2ffe9; background:linear-gradient(168deg, rgba(30,90,50,.96), rgba(12,48,28,.97)); }
/* 🎣 fishing-polish: the LINE TENSION plate — aged-chart parchment + brass (the .pg-* voice, never
   dark-glass). Waiting = slack sway with the gold PERFECT band already telegraphed; bite = the needle
   sweeps the reel window (gold band → parchment → red tail). Display-only (pointer-events:none). */
#fishrow { display:none; }
#fishplate { position:relative; width:min(58vw,206px); pointer-events:none; padding:7px 10px 6px; border-radius:10px;
  background:#e4cf9b url('/ui/parchment.webp') center/cover; border:2px solid #7a5416;
  box-shadow:0 4px 14px rgba(0,8,16,.45), inset 0 0 0 1px rgba(240,212,138,.55), inset 0 0 18px -6px rgba(70,44,14,.55); }
#fishplate .fp-hd { font-family:'Cinzel',Georgia,serif; font-size:9px; font-weight:900; letter-spacing:.2em; color:#5b3a14; text-align:center; }
#fpgauge { position:relative; height:15px; margin-top:4px; border-radius:4px; overflow:hidden;
  background:linear-gradient(180deg,#d9c28c,#c9ad72); border:1px solid #8a6428; box-shadow:inset 0 1px 3px rgba(66,40,10,.5); }
#fpzone { position:absolute; top:0; bottom:0; left:0; border-right:1px solid rgba(90,58,18,.85);
  background:linear-gradient(180deg,#ffd670,#dfa23a); box-shadow:inset 0 0 7px rgba(255,242,190,.85); }
#fpgauge .fp-danger { position:absolute; top:0; bottom:0; right:0; width:15%; border-left:1px solid rgba(90,30,14,.7);
  background:linear-gradient(180deg,#b8502e,#8e3418); opacity:.85; }
#fpneedle { position:absolute; top:-1px; bottom:-1px; left:0; width:3px; margin-left:-1.5px; background:#2c1a08;
  box-shadow:0 0 4px rgba(255,226,150,.9); }
#fptip { margin-top:4px; font-size:9.5px; font-weight:700; letter-spacing:.04em; color:#6a4c22; text-align:center; font-style:italic; }
#fishplate.bite { animation:fpUrge .55s ease-out infinite; }
#fishplate.bite #fptip { color:#8e3418; font-style:normal; font-weight:800; }
@keyframes fpUrge { 0%{ box-shadow:0 4px 14px rgba(0,8,16,.45), 0 0 0 0 rgba(255,208,100,.7);} 100%{ box-shadow:0 4px 14px rgba(0,8,16,.45), 0 0 0 12px rgba(255,208,100,0);} }
#fpstamp { position:absolute; left:50%; top:-15px; transform:translateX(-50%) scale(.6); opacity:0; pointer-events:none; z-index:2;
  font-family:'Cinzel',Georgia,serif; font-size:13.5px; font-weight:900; letter-spacing:.12em; color:#ffe9ae; white-space:nowrap;
  text-shadow:0 1px 0 #7a5416, 0 2px 8px rgba(0,0,0,.65); }
#fpstamp.go { animation:fpStamp 1.05s cubic-bezier(.2,.9,.25,1) forwards; }
@keyframes fpStamp { 0%{opacity:0; transform:translateX(-50%) scale(.6);} 16%{opacity:1; transform:translateX(-50%) scale(1.18);} 30%{transform:translateX(-50%) scale(1);} 78%{opacity:1;} 100%{opacity:0; transform:translateX(-50%) translateY(-13px) scale(1);} }
/* contextual: break-free mash button (only while boarded) */
#actions #strugglerow { display:none; }
.nav-btn.struggle { width:min(64vw,210px); height:50px; flex-direction:row; gap:7px; overflow:hidden;
  background:linear-gradient(168deg, rgba(120,40,28,.95), rgba(70,20,16,.96)); border-color:rgba(255,150,90,.4); font-size:14px; }
#strugglefill { position:absolute; left:0; top:0; bottom:0; width:0%; background:rgba(125,255,158,.45); transition:width .08s linear; }
.nav-btn.struggle span { position:relative; z-index:1; }

/* ----- LEFT: cannon-elevation gauge (only while aiming; fades) ----- */
#elevwrap { left:calc(env(safe-area-inset-left,0px) + 4%); bottom:calc(env(safe-area-inset-bottom,0px) + 33%);
  display:flex; flex-direction:column; align-items:center; gap:5px; width:62px; padding:9px 8px; touch-action:none;
  opacity:0; transform:translateY(6px); transition:opacity .2s, transform .2s; pointer-events:none; }
#elevwrap.aiming { opacity:1; transform:translateY(0); pointer-events:auto; }
#elevwrap .eh { font-size:8.5px; font-weight:800; letter-spacing:1px; color:var(--gold-soft); display:flex; align-items:center; gap:3px; }
#elevdeg { font-size:16px; font-weight:900; color:#9dffb4; text-shadow:0 1px 4px #042; font-variant-numeric:tabular-nums; }
#elevrow { display:flex; align-items:stretch; gap:4px; }
#elevrow .ticks { display:flex; flex-direction:column; justify-content:space-between; font-size:7.5px; font-weight:700; color:var(--ink-dim); padding:1px 0; }
#elevbar { position:relative; width:30px; height:108px; border-radius:15px; overflow:hidden; background:rgba(2,10,16,.6); border:1px solid rgba(90,255,140,.5); }
#elevfill { position:absolute; bottom:0; left:0; right:0; background:linear-gradient(0deg,#1f8f3a,#7dff9e); }
#elevbar .mark { position:absolute; left:3px; right:3px; height:1px; background:rgba(255,255,255,.18); }

/* respawn + toast */
#respawn { position:absolute; inset:0; display:none; align-items:center; justify-content:center; flex-direction:column;
  background:radial-gradient(ellipse at 50% 45%, rgba(40,6,10,.35), rgba(2,10,16,.7)); pointer-events:none; }
#respawn h1 { font-size:34px; font-weight:900; letter-spacing:2px; color:#ffd2d2; text-shadow:0 3px 14px #000; }
#respawn p { font-size:16px; opacity:.88; margin-top:8px; }
#toast { position:absolute; top:36%; left:50%; transform:translateX(-50%); padding:8px 16px; border-radius:12px; max-width:80vw; text-align:center;
  font-size:17px; font-weight:800; text-shadow:0 2px 6px #000; opacity:0; transition:opacity .3s;
  background:linear-gradient(165deg, rgba(16,30,46,.86), rgba(6,14,26,.9)); border:1px solid var(--trim); box-shadow:0 8px 26px rgba(0,8,16,.5); }
/* ---- kill celebration ---- */
#killbanner { position:absolute; top:25%; left:50%; transform:translateX(-50%) scale(.7); opacity:0; text-align:center; pointer-events:none; z-index:7;
  padding:9px 26px; border-radius:14px; background:linear-gradient(165deg, rgba(58,42,12,.92), rgba(28,18,4,.94));
  border:1.5px solid rgba(255,210,120,.6); box-shadow:0 10px 40px rgba(0,0,0,.5), 0 0 34px -4px rgba(255,190,80,.55); }
#killbanner.go { animation:killpop 1.6s cubic-bezier(.2,.9,.25,1) forwards; }
#killbanner .kb-t { font-size:22px; font-weight:900; letter-spacing:.5px; color:#ffe9b0; text-shadow:0 2px 8px #000; }
#killbanner .kb-g { font-size:14px; font-weight:800; color:#ffd066; margin-top:2px; }
@keyframes killpop { 0%{opacity:0; transform:translateX(-50%) scale(.7);} 11%{opacity:1; transform:translateX(-50%) scale(1.09);} 22%{transform:translateX(-50%) scale(1);} 80%{opacity:1;} 100%{opacity:0; transform:translateX(-50%) scale(1) translateY(-14px);} }
/* ---- Renown floater: a small "+10 🏆 Renown" / "−6 🏆 Renown" pill that rises when your prestige changes,
   so a kill/death's effect on the 🏆 counter is legible in the moment (its own slot — never the toast/kill queue). ---- */
#renownfloat { position:absolute; top:19%; left:50%; transform:translateX(-50%); padding:5px 13px; border-radius:11px; opacity:0; pointer-events:none; z-index:7;
  font-size:15px; font-weight:900; letter-spacing:.3px; text-shadow:0 2px 6px #000; white-space:nowrap;
  background:linear-gradient(165deg, rgba(16,30,46,.9), rgba(6,14,26,.94)); border:1px solid rgba(255,210,120,.5); box-shadow:0 8px 24px rgba(0,8,16,.5); }
#renownfloat.gain { color:#ffe0a0; border-color:rgba(255,210,120,.6); }
#renownfloat.loss { color:#ff9a90; border-color:rgba(255,120,110,.5); }
#renownfloat.go { animation:renownpop 1.5s cubic-bezier(.2,.9,.25,1) forwards; }
@keyframes renownpop { 0%{opacity:0; transform:translateX(-50%) translateY(6px) scale(.8);} 14%{opacity:1; transform:translateX(-50%) translateY(0) scale(1.06);} 26%{transform:translateX(-50%) translateY(0) scale(1);} 72%{opacity:1;} 100%{opacity:0; transform:translateX(-50%) translateY(-20px) scale(1);} }
/* ---- 🎣 Catch reveal (v52) — the fisgada headline turned into an ART card: the species icon (96px), its
   flavor rarity (foam→blue→purple→gold, reusing FISH_RARITY_COLOR), and the buff it grants. Upper-centre so it
   never covers the bottom controls; auto-fades. Legendary (Leviathan's Roe) gets the gold aura + a springier,
   longer entrance — a non-blocking mini-ritual (~2.3s). ---- */
#fishreveal { position:absolute; top:24%; left:50%; transform:translateX(-50%) scale(.86); opacity:0;
  pointer-events:none; z-index:8; width:min(76vw,300px); text-align:center; }
#fishreveal .fr-card { position:relative; padding:15px 22px 14px; border-radius:16px;
  background:linear-gradient(168deg, rgba(14,26,42,.94), rgba(5,12,22,.96)); border:2px solid var(--fr-col,#f0ead6);
  box-shadow:0 14px 44px rgba(0,8,16,.6), 0 0 30px -6px var(--fr-glow,transparent), inset 0 0 22px -9px var(--fr-glow,transparent); }
#fishreveal .fr-img { width:96px; height:96px; object-fit:contain; filter:drop-shadow(0 4px 12px rgba(0,0,0,.65)); }
#fishreveal .fr-tier { font-size:11px; font-weight:800; letter-spacing:2.6px; text-transform:uppercase; color:var(--fr-col,#f0ead6); text-shadow:0 1px 4px #000; margin-top:2px; }
#fishreveal .fr-name { font-family:'Cinzel',Georgia,serif; font-size:20px; font-weight:900; color:#f2f8ff; text-shadow:0 2px 9px #000; margin-top:1px; line-height:1.1; }
#fishreveal .fr-sub { font-size:11.5px; font-weight:700; color:#a7c6e2; margin-top:3px; }
#fishreveal .fr-aura { position:absolute; left:50%; top:42%; width:230px; height:230px; margin:-115px 0 0 -115px; border-radius:50%; z-index:-1; opacity:0; background:radial-gradient(circle, var(--fr-glow,transparent) 0%, transparent 64%); }
#fishreveal.go { animation:frPop var(--fr-dur,1.6s) cubic-bezier(.2,.9,.25,1) forwards; }
@keyframes frPop { 0%{opacity:0; transform:translateX(-50%) scale(.86) translateY(10px);} 12%{opacity:1; transform:translateX(-50%) scale(1.07);} 22%{transform:translateX(-50%) scale(1);} 78%{opacity:1; transform:translateX(-50%) scale(1) translateY(0);} 100%{opacity:0; transform:translateX(-50%) scale(1) translateY(-16px);} }
#fishreveal.leg { animation-name:frPopLeg; }
#fishreveal.leg .fr-aura { animation:frAura var(--fr-dur,2.3s) ease-in-out forwards; }
#fishreveal.leg .fr-img { animation:frFloat var(--fr-dur,2.3s) ease-in-out; }
@keyframes frPopLeg { 0%{opacity:0; transform:translateX(-50%) scale(.7) translateY(16px) rotate(-4deg);} 10%{opacity:1; transform:translateX(-50%) scale(1.12) rotate(2deg);} 20%{transform:translateX(-50%) scale(.98) rotate(-1deg);} 28%{transform:translateX(-50%) scale(1.03);} 36%{transform:translateX(-50%) scale(1);} 82%{opacity:1; transform:translateX(-50%) scale(1) translateY(0);} 100%{opacity:0; transform:translateX(-50%) scale(1) translateY(-20px);} }
@keyframes frAura { 0%{opacity:0; transform:scale(.6);} 24%{opacity:.9;} 60%{opacity:.55; transform:scale(1.15);} 100%{opacity:0; transform:scale(1.3);} }
@keyframes frFloat { 0%{transform:translateY(4px);} 40%{transform:translateY(-4px);} 100%{transform:translateY(0);} }
/* ---- 🤿 Itemized loot reveal (Onda diving-polish) — a parchment manifest of what a haul ADDED to the hold:
   one cell per material (raster icon + ×qty + name), staggered pop-ins on aged paper (.pg-frame/.pg-paper —
   the Dredge card's language, NOT dark glass). Generic on purpose: wreck diving is the first caller. ---- */
#lootreveal { position:absolute; top:15%; left:50%; transform:translateX(-50%); opacity:0;
  pointer-events:none; z-index:9; width:min(88vw,360px); }
#lootreveal.go { animation:lrPop 3.4s cubic-bezier(.2,.9,.25,1) forwards; }
@keyframes lrPop { 0%{opacity:0; transform:translateX(-50%) translateY(14px) scale(.86);} 8%{opacity:1; transform:translateX(-50%) translateY(0) scale(1.03);} 15%{transform:translateX(-50%) scale(1);} 86%{opacity:1;} 100%{opacity:0; transform:translateX(-50%) translateY(-10px) scale(.98);} }
#lootreveal .lr-pad { padding:12px 14px 10px; }
#lootreveal .lr-title { font-family:'Cinzel',Georgia,serif; font-weight:900; font-size:17px; text-align:center; color:#3d2c12; letter-spacing:.6px; }
#lootreveal .lr-sub { text-align:center; font-size:11.5px; color:#6b5638; font-style:italic; margin:1px 0 9px; }
#lootreveal .lr-row { display:flex; justify-content:center; gap:10px; flex-wrap:wrap; }
#lootreveal .lr-item { width:72px; text-align:center; position:relative; opacity:0; animation:lrItem .5s cubic-bezier(.2,1.4,.4,1) forwards; }
@keyframes lrItem { from{opacity:0; transform:scale(.4);} to{opacity:1; transform:scale(1);} }
#lootreveal .lr-img { width:56px; height:56px; object-fit:contain; filter:drop-shadow(0 3px 5px rgba(40,25,8,.45)); }
#lootreveal .lr-ph { font-size:40px; line-height:56px; }
#lootreveal .lr-qty { position:absolute; top:38px; right:2px; background:#8a1e1e; color:#ffe9c4; font-weight:800; font-size:11px; padding:1px 6px; border-radius:9px; border:1px solid rgba(255,233,196,.5); }
#lootreveal .lr-name { font-size:11px; font-weight:700; color:#4a3418; margin-top:2px; line-height:1.15; }
/* ---- v37: the epic "flat earth" edge banner — a longer (~6.5s), heavier moment: momentary screen vignette
   + big two-line text. Its own element (not the kill slot) so the duration is independent of kill pops. ---- */
#edgevignette { position:absolute; inset:0; pointer-events:none; z-index:6; opacity:0;
  background:radial-gradient(125% 95% at 50% 44%, transparent 28%, rgba(6,16,30,.34) 60%, rgba(2,7,16,.74) 100%); }
#edgevignette.go { animation:edgeVig 6.5s ease-in-out forwards; }
@keyframes edgeVig { 0%{opacity:0;} 11%{opacity:1;} 74%{opacity:1;} 100%{opacity:0;} }
#edgebanner { position:absolute; top:33%; left:50%; transform:translateX(-50%) scale(.82); opacity:0; text-align:center;
  pointer-events:none; z-index:8; width:92vw; max-width:640px; padding:0 8px; box-sizing:border-box; }
#edgebanner.go { animation:edgePop 6.5s cubic-bezier(.2,.9,.25,1) forwards; }
#edgebanner .eb-t { font-size:clamp(21px,6.6vw,33px); font-weight:900; letter-spacing:1px; color:#e2f1ff; line-height:1.06;
  text-shadow:0 2px 14px #000, 0 0 28px rgba(120,180,255,.5); }
#edgebanner .eb-g { font-size:clamp(13px,3.6vw,16px); font-weight:800; color:#a9cdf0; margin-top:8px; text-shadow:0 2px 8px #000; }
@keyframes edgePop { 0%{opacity:0; transform:translateX(-50%) scale(.82) translateY(8px);} 8%{opacity:1; transform:translateX(-50%) scale(1.06);}
  16%{transform:translateX(-50%) scale(1);} 82%{opacity:1; transform:translateX(-50%) scale(1);} 100%{opacity:0; transform:translateX(-50%) scale(1) translateY(-18px);} }
/* ---- ⚓ Captain's Orders (v46) — the owner's in-game admin broadcast BANNER. A body-level overlay (NOT inside
   #hud) so it stays visible ashore too, where the sea HUD is hidden. Auto-dismisses (PvP-first): it fades on its
   own after the server-set duration. Level tints the frame + kicker: info=gold, warn=amber, alert=red. ---- */
#cc-announce { position:fixed; top:calc(env(safe-area-inset-top,0px) + 10%); left:50%; transform:translateX(-50%) translateY(-10px);
  opacity:0; pointer-events:none; z-index:60; width:min(92vw,680px); box-sizing:border-box; padding:13px 20px; text-align:center;
  border-radius:14px; background:linear-gradient(180deg, rgba(18,26,44,.95), rgba(9,15,28,.94));
  border:1px solid rgba(255,210,120,.55); box-shadow:0 14px 44px rgba(0,0,0,.6), inset 0 0 40px rgba(255,190,90,.06);
  transition:opacity .38s ease, transform .38s ease; }
#cc-announce.show { opacity:1; transform:translateX(-50%) translateY(0); }
#cc-announce .an-hd { font-size:11px; font-weight:800; letter-spacing:2.5px; text-transform:uppercase; color:#ffd27a;
  margin-bottom:5px; text-shadow:0 1px 4px #000; }
#cc-announce .an-t { font-size:clamp(16px,4.4vw,24px); font-weight:900; line-height:1.18; color:#fff5e0; word-break:break-word;
  text-shadow:0 2px 12px #000; }
#cc-announce.info  { border-color:rgba(255,210,120,.55); }
#cc-announce.warn  { border-color:rgba(255,178,66,.65);  box-shadow:0 14px 44px rgba(0,0,0,.6), inset 0 0 40px rgba(255,150,50,.09); }
#cc-announce.warn  .an-hd { color:#ffbf5a; }
#cc-announce.alert { border-color:rgba(255,92,80,.75);   box-shadow:0 14px 44px rgba(0,0,0,.6), inset 0 0 44px rgba(255,60,50,.12); animation:ccAnnPulse 1.4s ease-in-out infinite; }
#cc-announce.alert .an-hd { color:#ff8a80; } #cc-announce.alert .an-t { color:#ffe7e3; }
@keyframes ccAnnPulse { 0%,100%{ box-shadow:0 14px 44px rgba(0,0,0,.6), 0 0 0 rgba(255,60,50,0); } 50%{ box-shadow:0 14px 44px rgba(0,0,0,.6), 0 0 22px -2px rgba(255,60,50,.7); } }
#killfeed { position:absolute; top:98px; left:50%; transform:translateX(-50%); display:flex; flex-direction:column; align-items:center; gap:3px; pointer-events:none; z-index:5; }
#killfeed .kf { font-size:12px; font-weight:700; color:#dbe4f3; background:rgba(8,14,26,.6); border:1px solid rgba(120,150,190,.18); border-radius:8px; padding:2px 9px; text-shadow:0 1px 3px #000; opacity:0; animation:kfIn .3s ease forwards; transition:opacity .5s; }
#killfeed .kf.mine { color:#ffe1a0; border-color:rgba(255,200,110,.4); background:rgba(40,30,8,.62); }
#killfeed .kf .v { color:#ff8a8a; } #killfeed .kf .k { color:#8fe0b4; } #killfeed .kf.mine .k { color:#ffd066; }
@keyframes kfIn { from{opacity:0; transform:translateY(-6px);} to{opacity:1; transform:none;} }
#hitmarks { position:absolute; inset:0; pointer-events:none; z-index:4; overflow:hidden; }
.hitmark { position:absolute; width:26px; height:26px; margin:-13px 0 0 -13px; opacity:0; }
.hitmark.go { animation:hmPop .42s ease-out forwards; }
.hitmark svg { width:100%; height:100%; filter:drop-shadow(0 1px 2px rgba(0,0,0,.7)); }
@keyframes hmPop { 0%{opacity:0; transform:scale(.4);} 20%{opacity:1; transform:scale(1.15);} 100%{opacity:0; transform:scale(.9);} }
/* parchment note (Message in a Bottle blurbs) — aged paper, wax seal, nautical serif */
#bottlenote { position:absolute; left:50%; bottom:20%; transform:translateX(-50%) translateY(16px); width:min(360px,80vw); opacity:0; pointer-events:none; z-index:8; transition:opacity .5s, transform .5s;
  padding:16px 20px 18px; text-align:center; color:#3a2c14; font-family:Georgia,'Times New Roman',serif;
  background:#e8d9b0 url('/ui/parchment.webp'); background-size:cover; border-radius:5px;
  border:1px solid #b79b62; box-shadow:0 14px 40px rgba(0,0,0,.55), inset 0 0 60px rgba(120,85,30,.25);
  -webkit-mask-image:radial-gradient(120% 120% at 50% 50%, #000 82%, transparent 100%); }
#bottlenote.go { opacity:1; transform:translateX(-50%) translateY(0); }
#bottlenote .bn-seal { position:absolute; top:-14px; left:50%; transform:translateX(-50%); width:30px; height:30px; line-height:30px; border-radius:50%; background:radial-gradient(circle at 40% 35%, #b23b2e, #7d241a); color:#f4dcae; font-size:15px; box-shadow:0 3px 8px rgba(0,0,0,.5); }
#bottlenote .bn-hd { font-size:12px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; color:#7d5a24; margin-bottom:7px; }
#bottlenote .bn-body { font-size:15px; line-height:1.5; font-style:italic; }
#bottlenote.coin .bn-seal { background:radial-gradient(circle at 40% 35%, #e6b545, #a9791c); }
/* WANTED — a notorious captain's own red badge + the map-wide alert banner (wave 3.6) */
#wantedbadge.badge.wanted { display:none; background:linear-gradient(160deg,rgba(150,20,18,.92),rgba(90,8,6,.94)); border:1px solid rgba(255,90,80,.7); color:#ffd9d4; animation:wantedPulse 1.1s ease-in-out infinite; }
#wantedbadge.badge.wanted.on { display:inline-flex; }
@keyframes wantedPulse { 0%,100%{box-shadow:0 0 0 rgba(255,60,50,0);} 50%{box-shadow:0 0 12px -1px rgba(255,60,50,.85);} }
/* merchant-convoy escort offer (wave 3.7) */
#escortbtn { position:absolute; left:50%; top:20%; transform:translateX(-50%); display:none; z-index:8; pointer-events:auto;
  padding:8px 16px; border-radius:20px; border:1px solid rgba(120,200,255,.6); color:#dff1ff; font-weight:800; font-size:13px; letter-spacing:.5px;
  background:linear-gradient(160deg,rgba(30,70,110,.92),rgba(14,40,70,.94)); box-shadow:0 6px 20px rgba(0,0,0,.5); cursor:pointer; }
#escortbtn.on { display:block; animation:escortPulse 1.4s ease-in-out infinite; }
@keyframes escortPulse { 0%,100%{box-shadow:0 6px 20px rgba(0,0,0,.5);} 50%{box-shadow:0 6px 24px rgba(90,180,255,.6);} }
/* ----- LANDSCAPE PHONE (short viewport): the portrait anchors stack fatally — the action cluster
   climbs over the chart, buttons pile mid-screen. Re-flow: panels spread HORIZONTALLY along the top
   edge, gauges shrink, and the bottom strip belongs to controls only. */
@media (orientation: landscape) and (max-height: 540px) {
  #topbar { width:min(26vw,180px); } /* real gap to the MENU pill at left 208 (owner: "barrinha em cima dos status") */
  #mapwrap { width:108px; }
  #minimap { width:108px; height:108px; }
  #speedo { width:78px; height:78px; bottom:calc(env(safe-area-inset-bottom,0px) + 4px); }
  #steer { left:calc(env(safe-area-inset-left,0px) + 10px); bottom:calc(env(safe-area-inset-bottom,0px) + 10px); }
  #actions { bottom:calc(env(safe-area-inset-bottom,0px) + 10px); }
  #elevwrap { left:calc(env(safe-area-inset-left,0px) + 150px); bottom:calc(env(safe-area-inset-bottom,0px) + 8px); }
  #aimhint { right:auto; left:50%; transform:translateX(-50%); bottom:calc(env(safe-area-inset-bottom,0px) + 92px); }
  #hoistbar { bottom:auto; top:16%; }
  #buffbtn { right:calc(env(safe-area-inset-right,0px) + 196px); bottom:calc(env(safe-area-inset-bottom,0px) + 96px); }
  #buffpop { right:calc(env(safe-area-inset-right,0px) + 196px); bottom:calc(env(safe-area-inset-bottom,0px) + 146px); }
}

/* carpenter repair-plank count badge on the BAIL button (wave 3.10) */
#bail { position:relative; }
.plankbadge { display:none; position:absolute; top:-4px; right:-4px; font-size:10px; font-weight:800; color:#f0e0b0; background:rgba(90,60,20,.95); border:1px solid rgba(200,160,90,.7); border-radius:8px; padding:0 4px; line-height:14px; }
.plankbadge.on { display:block; }
`;

export class Hud {
  rudder = 0; sail = 0; anchor = false; bail = false;
  ammo = 0; // AmmoType (round/chain/grape) — rides in the input button bits
  private actionReq = false; // momentary: one contextual sea-action (cast/reel/dive) edge
  private grappleRole: 'plundering' | 'boarded' | null = null;
  private fireL = false; private fireR = false;
  /** Ammo economy: the latest self-state stocks (server-authoritative; -1 = no snapshot yet). Drive the
   *  ×N counter on the AMMO selector, the dimmed FIRE buttons + "out of powder" toast at 0 round, the
   *  one-time low-powder warning, and the one-time chain/grape→round fallback notice. */
  private roundStockV = -1; private chainStockV = -1; private grapeStockV = -1;
  private lowPowderArmed = true;   // re-armed every resupply above 25% — the warning fires once per depletion
  private fallbackNoticed = 0;     // bitmask per AmmoType — the fallback toast fires once per special depletion
  private noPowderMs = 0;          // throttle for the "out of powder" toast (fire-button mashing)
  private struggle = false; // momentary: each tap/keypress = one mash-to-escape edge
  private aimingSide = -1;  // 0=port, 1=stbd broadside being aimed (held); -1 = none. Drives the bearing guide.
  private aimStartY = 0;    // touch Y when the aim began — sliding up/down from here sets the elevation (range)
  private aimStartElev: number = CANNON.ELEV_DEFAULT;
  private elevation: number = CANNON.ELEV_DEFAULT; // 0..1 cannon elevation (W/S while aiming, or the ELEV slider); drives ball range
  private steerId = -1;
  /** Mobile onboarding: the helm knob's FORWARD axis (-1..1, +1 = pushed up). Pushing forward auto-hoists the
   *  sails from furled (helmsail.ts) so "shove the joystick forward = the ship goes", mirroring desktop W. */
  private helmForward = 0;
  private el: Record<string, HTMLElement> = {};
  private minimapCtx: CanvasRenderingContext2D;
  private bigmapCtx: CanvasRenderingContext2D | null = null; // full-world chart overlay (M / 🗺); rendered by the same renderChart()
  private bigmapOpen = false;
  /** Cached procedural PARCHMENT texture (aged paper: sepia wash + mottling + grain + tea-stains + burnt
   *  edges), generated ONCE and blitted every frame — the per-pixel grain never re-runs. See parchmentTex(). */
  private _parch: HTMLCanvasElement | null = null;
  /** Cached STATIC chart layer (parchment + sea + islands + ports + rose + frame), per target pixel size.
   *  Rebuilt only when the world data or size changes; blitted each frame so only the live markers redraw. */
  private _chartBase = new Map<number, { canvas: HTMLCanvasElement; sig: string }>();
  private raidPing: { x: number; y: number; until: number } | null = null;
  private cursedPing: { x: number; y: number; until: number } | null = null;
  private clanPings: { x: number; y: number; until: number }[] = [];
  onMinimapPing: ((x: number, y: number) => void) | null = null; // set by main → sends a clan ping (long-press)
  /** Frente 6d: tap-to-sail destination in WORLD coords (null = none). Read by main to synthesise the
   *  autopilot rudder each input tick; drawn as a dotted voyage line + a stamped red X on the chart. */
  waypoint: { x: number; y: number } | null = null;
  onWaypointSet: ((x: number, y: number) => void) | null = null; // set by main → SFX/haptic feedback
  /** Map-UX: transient "course plotted" stamp FX — ink ripple + rubber-stamped red X + ink specks + a
   *  compass-needle sweep onto the new bearing. ONE shared state drawn by renderChart on both canvases. */
  private plotFx: { x: number; y: number; t0: number; bearing: number } | null = null;
  /** Last self position (world coords), cached each drawMinimap — seeds the plot FX bearing at tap time. */
  private lastSelf = { x: WORLD_SIZE / 2, y: WORLD_SIZE / 2 };
  /** Auto-sail route hook (autosail.ts): returns the REMAINING waypoints (world coords, in sail order) of
   *  the planned passage — the voyage line threads through them (rounding islands) instead of running
   *  straight, and the X stamps on the LAST one (the water-projected mark). Client-only, zero wire. */
  getRoute: (() => readonly { x: number; y: number }[] | null) | null = null;
  /** ⚑ Armada chart markers (v54): the live pin list (cached each drawMinimap for tap hit-testing), this
   *  captain's moderation right (officer/leader) and clan membership (gates the long-press gesture). */
  private clanMarks: NonNullable<MinimapData['clanMarks']> = [];
  private canModMarks = false;
  private inClanNow = false;
  /** Pending mark placement (world coords) while the glyph picker is open — a pulsing ink spot on the chart. */
  private markSel: { x: number; y: number } | null = null;
  /** The pin whose paper tag is open (null = none) — that pin pulses so tag↔pin read as one thing. */
  private markInfoId: number | null = null;
  onMarkPlace: ((x: number, y: number, icon: number, label: string) => void) | null = null; // set by main → C_MARK PLACE
  onMarkRemove: ((id: number) => void) | null = null;                                       // set by main → C_MARK REMOVE
  private bigmapCloseTimer: ReturnType<typeof setTimeout> | null = null; // delayed roll-up so the stamp lands on-screen
  /** Last-known OWN base position (cached from drawMinimap) — powers the HOME button's "sail there" course. */
  private homeBase: { x: number; y: number } | null = null;
  // eased speedometer readouts (premium smoothing so the needle/number glide, never snap)
  private speedoKn = 0; private speedoWind = 0; private speedoWindInit = false;
  private hp01 = 1;            // cached hull integrity (low/full glow + bail attention)
  private lastKills = -1; private lastDeaths = -1; // dirty-check so kd isn't rewritten every frame
  private lastCargoT = ''; // dirty-check for the ⚖ Cargo Runs weight chip (avoid per-frame innerHTML churn)
  private lastHaul = '';   // dirty-check for the 🏭 warehouse-fill chip (label/bar/tint only touch DOM on a % cross)
  private maxReloadL: number = CANNON.RELOAD; private maxReloadR: number = CANNON.RELOAD; // adaptive ring scale (class/ammo reload mults)
  private hud: HTMLElement;

  /** Wave 6: hide the entire sea HUD while ashore in a port (the port has its own HUD). */
  setVisible(v: boolean): void { this.hud.style.display = v ? '' : 'none'; }

  constructor(root: HTMLElement) {
    injectParchmentCss(); // .parch panels + the MENU/chat pills paint on the chart's exact paper (--pg-paper)
    const style = document.createElement('style'); style.textContent = CSS; document.head.appendChild(style);
    const hud = document.createElement('div'); hud.id = 'hud';
    this.hud = hud;
    const elevMax = Math.round(CANNON.ELEV_MAX_ANGLE * 180 / Math.PI);
    hud.innerHTML = `
      <div id="topbar" class="panel parch">
        <div id="hpwrap"><div id="hp"></div><span class="hp-txt">⛨ HULL</span><span class="hp-pct" id="hppct">100%</span></div>
        <div class="chips">
          <div class="chip coin"><span class="ic">🪙</span><span id="gold">0</span></div>
          <div class="chip sun tap" id="dailyearn" title="Daily full-rate hold — earnings taper past 5k, never zero"><span class="ic">☀</span><span id="dailyearnt">0/5k</span></div>
          <div class="chip trophy tap" id="trophychip" title="Renown — your pirate prestige. Sink captains to earn it; you lose some when you're sunk (never below 0)."><span class="ic">🏆</span><span id="trophies">0</span></div>
          <div class="chip chest tap" id="chestchip"><span class="ic">💰</span><span id="chestn">CHEST</span></div>
          <div class="chip cargo tap" id="cargochip"><span class="ic">📦</span><span id="cargo">0</span></div>
          <div class="chip hold tap" id="holdchip"><span class="ic">🧺</span><span id="holdn">0</span></div>
          <div class="chip cargo tap" id="cargorunchip"><span class="ic">⚖</span><span id="cargorunt">0</span></div>
          <div class="chip sun tap" id="haulchip"><span class="hw-fill" id="haulfill"></span><span class="ic">🏭</span><span id="hault">HAUL READY</span></div>
          <span class="kd" id="kd">⚔ 0 · ☠ 0</span>
        </div>
        <div class="badges">
          <span id="rigwarn" class="badge">⛓ RIGGING</span>
          <span id="grappleind" class="badge">⛓ GRAPPLE</span>
          <span id="dockind" class="badge">⚓ SAFE HARBOR</span>
          <span id="wantedbadge" class="badge wanted">☠ WANTED</span>
          <span id="buffd" class="badge buff"></span>
          <span id="buffo" class="badge buff"></span>
          <span id="bufff" class="badge buff"></span>
        </div>
        <div id="statline">spd 0 · 0 fps</div>
      </div>

      <div id="mapwrap" class="panel">
        <canvas id="minimap" width="132" height="132"></canvas>
        <div class="map-rail">
          <button id="mapexpand" class="chart-btn" title="Open the full world chart (M)" aria-label="Open the full world chart"><svg viewBox="0 0 22 22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="9.5" cy="9.5" r="5.6"/><line x1="13.8" y1="13.8" x2="19" y2="19"/><line x1="7" y1="9.5" x2="12" y2="9.5"/><line x1="9.5" y1="7" x2="9.5" y2="12"/></svg><span class="cb-lbl">MAP</span></button>
          <button id="homebtn" class="chart-btn map-home" title="Sail home to your base" aria-label="Sail home to your base"><svg viewBox="0 0 22 22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="7" y1="19.5" x2="7" y2="3"/><path d="M7 3.5l9.5 2.6L7 8.8z" fill="currentColor" stroke-width="1.2" stroke-linejoin="round"/><line x1="4.5" y1="19.5" x2="9.5" y2="19.5"/></svg><span class="cb-lbl">HOME</span></button>
        </div>
        <span class="map-hint">tap to sail</span>
      </div>
      <div id="bigmap"><div id="bigmapinner"><canvas id="bigmapcanvas" width="720" height="720"></canvas><div id="bigmaphd"><span class="bm-t">⚓ World Chart</span><span class="bm-sub">tap to plot a course · hold to mark for your Armada · M / Esc closes</span></div><button id="bigmapclose" aria-label="Close chart">✕</button><div id="markpick"><span class="mp-t">⚑ Stamp an Armada mark</span><div class="mp-row" id="mprow0"></div><div class="mp-row" id="mprow1"></div><input id="mplabel" maxlength="${CLAN_MARKERS.LABEL_MAX}" placeholder="label (optional)" autocomplete="off" /></div><div id="markinfo"></div></div></div>

      <div id="steer" class="panel">
        <span class="helm-lbl">HELM</span><span class="ar l">◀</span><span class="ar r">▶</span>
        <span class="ar u">▲ SAIL</span>
        <div id="steerknob"></div>
        <span class="kh helm-kh">A · D</span>
      </div>

      <div id="elevwrap" class="panel glass">
        <div class="eh">🎯 CANNON</div>
        <div id="elevdeg">0°</div>
        <div id="elevrow">
          <div class="ticks"><span>${elevMax}°</span><span>0°</span></div>
          <div id="elevbar"><div id="elevfill"></div></div>
        </div>
      </div>

      <div id="actions" class="panel">
        <div class="util-grid" id="utilgrid">
          <button class="nav-btn util sail" id="sail"><div id="sailfill"></div><span class="ic">⛵</span><span class="lbl">SAIL</span><span class="kh">W/S</span></button>
          <button class="nav-btn util ammo" id="ammo"><span class="ic">${itemIcon(AMMO_ICONS[0], 'ammo-ic')}</span><span class="lbl" id="ammolbl">ROUND</span><span class="kh">X</span></button>
          <button class="nav-btn util" id="anchor"><span class="ic">⚓</span><span class="lbl">ANCHOR</span><span class="kh">F</span></button>
          <button class="nav-btn util" id="bail"><span class="ic">🪣</span><span class="lbl">BAIL</span><span class="kh">SPC</span><span class="plankbadge" id="plankbadge"></span></button>
        </div>
        <div class="util-grid" id="strugglerow"><button class="nav-btn struggle" id="struggle"><div id="strugglefill"></div><span>⛓ BREAK FREE! (mash)</span></button></div>
        <div class="util-grid" id="fishrow"><div id="fishplate"><div id="fpstamp">PERFECT HOOK!</div><div class="fp-hd">— LINE TENSION —</div><div id="fpgauge"><div id="fpzone"></div><div class="fp-danger"></div><div id="fpneedle"></div></div><div id="fptip">Wait for the tug…</div></div></div>
        <div class="util-grid" id="contextrow"><button class="nav-btn action" id="actionbtn"><span class="ic">🎣</span><span class="lbl">CAST</span><span class="kh">R</span></button></div>
        <div class="fire-row" id="firerow">
          <button class="nav-btn fire" id="fireL"><span class="ring"></span>${CANNON_SVG}<span class="lbl">PORT</span><span class="kh">Q</span></button>
          <button class="nav-btn fire r" id="fireR"><span class="ring"></span>${CANNON_SVG}<span class="lbl">STBD</span><span class="kh">E</span></button>
        </div>
      </div>
      <div id="aimhint"></div>
      <canvas id="speedo" width="208" height="208"></canvas>
      <button id="buffbtn">⚗</button>
      <div id="buffpop"></div>
      <button id="escortbtn">⚓ ESCORT CONVOY</button>
      <div id="hoistbar"><div class="cap">⚓ Hoisting treasure…</div><div class="track"><div id="hoistfill"></div></div><div class="warn">Hold still — you're a sitting duck</div></div>

      <div id="respawn"><h1>☠ SUNK</h1><p>Respawning in <span id="respawnt">5</span>s</p></div>
      <div id="bottlenote"><div class="bn-seal">✉</div><div class="bn-hd">Message in a Bottle</div><div class="bn-body" id="bottlenotebody"></div></div>
      <div id="fishreveal"></div>
      <div id="lootreveal"></div>
      <div id="toast"></div>
      <div id="renownfloat"></div>
      <div id="killbanner"></div>
      <div id="edgevignette"></div>
      <div id="edgebanner"></div>
      <div id="killfeed"></div>
      <div id="hitmarks"></div>`;
    root.appendChild(hud);
    for (const id of ['gold', 'dailyearn', 'dailyearnt', 'trophies', 'trophychip', 'renownfloat', 'cargo', 'cargochip', 'chestchip', 'chestn', 'holdchip', 'holdn', 'cargorunchip', 'cargorunt', 'haulchip', 'haulfill', 'hault', 'kd', 'hp', 'hpwrap', 'hppct', 'statline', 'rigwarn', 'grappleind', 'dockind', 'wantedbadge', 'steer', 'steerknob', 'sail', 'sailfill', 'elevwrap', 'elevbar', 'elevfill', 'elevdeg', 'aimhint', 'hoistbar', 'hoistfill', 'anchor', 'bail', 'ammo', 'fireL', 'fireR', 'struggle', 'strugglefill', 'strugglerow', 'contextrow', 'actionbtn', 'fishrow', 'fishplate', 'fpzone', 'fpneedle', 'fptip', 'fpstamp', 'buffd', 'buffo', 'bufff', 'buffbtn', 'buffpop', 'escortbtn', 'plankbadge', 'respawn', 'respawnt', 'toast', 'bottlenote', 'bottlenotebody', 'fishreveal', 'lootreveal', 'killbanner', 'edgevignette', 'edgebanner', 'killfeed', 'hitmarks', 'mapwrap', 'minimap', 'homebtn', 'mapexpand', 'bigmap', 'bigmapcanvas', 'bigmapclose', 'markpick', 'markinfo', 'mprow0', 'mprow1', 'mplabel', 'speedo', 'ammolbl']) {
      this.el[id] = hud.querySelector('#' + id) as HTMLElement;
    }
    // ⚓ Captain's Orders banner — a BODY-level overlay (not inside #hud, which is hidden ashore) so an owner
    // broadcast reaches captains at sea, in port, and in a house alike. Built empty; announceBanner() fills it.
    const ann = document.createElement('div');
    ann.id = 'cc-announce';
    document.body.appendChild(ann);
    this.el.announce = ann;
    this.minimapCtx = (this.el.minimap as HTMLCanvasElement).getContext('2d')!;
    this.bigmapCtx = (this.el.bigmapcanvas as HTMLCanvasElement).getContext('2d');
    // Warm up Cinzel so the nautical chart's labels (Cinzel) draw in the pirate face, not a fallback serif —
    // the chartBase() signature keys on document.fonts.check so the baked layer rebuilds once it lands.
    try { document.fonts?.load('700 12px Cinzel'); document.fonts?.load('900 12px Cinzel'); } catch { /* no-op */ }
    this.bindBigmap();
    this.buildMarkPicker();
    if (window.matchMedia?.('(pointer: fine)').matches) hud.classList.add('desktop');
    // elevation min/max marks on the bar
    const bar = this.el.elevbar as HTMLElement;
    for (const yp of [12, 50, 88]) { const m = document.createElement('div'); m.className = 'mark'; m.style.bottom = `${yp}%`; bar.appendChild(m); }
    // tap the "☀ x/5k" daily-earnings chip to read what it means (mobile has no hover tooltip — same
    // treatment as the world-event chip). It's the cryptic-est topbar counter, so make it self-explain.
    { const de = this.el.dailyearn as HTMLElement; de.style.cursor = 'pointer';
      de.addEventListener('pointerdown', (e) => { e.preventDefault(); this.toast(`☀ Daily earnings: gold from kills, chests & digs mints full-rate up to your 5,000 cap, then tapers (never zero). Resets in ${fmtResetIn(msUntilUtcMidnight())}.`); }); }
    // Fix 4 frente 2 — the carry chips self-explain on tap (owner: "tem que mostrar quanto o navio
    // está carregando"): 💰 chest aboard, 📦 unbanked loot gold, 🧺 the materials-hold breakdown.
    { const c = this.el.chestchip as HTMLElement;
      c.addEventListener('pointerdown', (e) => { e.preventDefault(); this.toast(CHEST_CHIP_HINT); }); }
    { const c = this.el.cargochip as HTMLElement;
      c.addEventListener('pointerdown', (e) => { e.preventDefault(); this.toast(CARGO_CHIP_HINT); }); }
    { const c = this.el.holdchip as HTMLElement;
      c.addEventListener('pointerdown', (e) => { e.preventDefault(); this.toast(this.holdDetail || HOLD_CHIP_EMPTY); }); }
    // 🏆 Renown chip self-explains on tap (mobile has no hover title): what it is + the REAL rule, faithful to
    // TROPHY.KILL/DEATH — so players stop asking "what is this and why did it drop?" (owner: the −102 confusion).
    { const c = this.el.trophychip as HTMLElement;
      c.addEventListener('pointerdown', (e) => { e.preventDefault(); this.toast(RENOWN_HINT); }); }
    // Chart gestures (frente 6d): a short TAP sets a sail-to waypoint (autopilot steers there); tapping the
    // existing mark again cancels it. A LONG-PRESS (≥450 ms) drops a clan ping instead (server ignores it if
    // you're not in an Armada). A drag is ignored so a mis-swipe never sets a course.
    { const mm = this.el.minimap as HTMLCanvasElement; let dx = 0, dy = 0, dt = 0;
      mm.addEventListener('pointerdown', (e) => { dx = e.clientX; dy = e.clientY; dt = performance.now(); });
      mm.addEventListener('pointerup', (e) => {
        if (Math.hypot(e.clientX - dx, e.clientY - dy) > 12) return; // a drag, not a tap
        const rect = mm.getBoundingClientRect();
        const wx = (e.clientX - rect.left) / rect.width * WORLD_SIZE;
        const wy = (e.clientY - rect.top) / rect.height * WORLD_SIZE;
        if (performance.now() - dt >= 450) { this.onMinimapPing?.(wx, wy); return; } // deliberate hold → ping
        // retap on the current mark (within ~16 px on the chart) cancels the course
        if (this.waypoint) {
          const sx = rect.left + this.waypoint.x / WORLD_SIZE * rect.width;
          const sy = rect.top + this.waypoint.y / WORLD_SIZE * rect.height;
          if (Math.hypot(e.clientX - sx, e.clientY - sy) < 16) { this.clearWaypoint('🧭 Course cancelled'); return; }
        }
        this.stampCourse(wx, wy);
        this.toast('🧭 Course set — the helm steers there. Tap the mark, or take the wheel, to cancel.');
      });
    }
    // "Sail home" (feedback: players lose track of their own base) — one tap lays a course to your harbour
    // and the existing tap-to-sail autopilot steers there. Retap (course already on the base) cancels it.
    { const hb = this.el.homebtn as HTMLElement;
      hb.addEventListener('pointerdown', (e) => { e.preventDefault(); this.sailHome(); }); }
    this.setAmmoLabel();
    this.bindSteer();
    this.bindSail();
    this.bindElev();
    this.bindButtons();
    this.bindKeys();
    bindHudLayoutAutoApply(); // HUD Forge: apply the player's saved custom layout (no-op when none saved)
  }

  /** HUD Forge: while the layout editor is open its overlay swallows all touches; this also parks the
   *  keyboard path (Q/E aim, F anchor, space bail…) and clears any held input so nothing fires mid-edit. */
  private editMode = false;
  setEditMode(on: boolean): void {
    this.editMode = on;
    if (on) {
      this.rudder = 0; this.helmForward = 0; this.bail = false; this.aimingSide = -1;
      this.fireL = false; this.fireR = false; this.struggle = false; this.actionReq = false;
      this.heldKeys.clear();
      (this.el.bail as HTMLElement)?.classList.remove('on');
    }
  }

  /** Drag anywhere on the elevation widget to set cannon elevation 0..1 (bottom = flat, top = max lob).
   *  The grab zone is the whole #elevwrap (thumb-friendly), mapped onto the visible #elevbar's height. */
  private bindElev(): void {
    const wrap = this.el.elevwrap as HTMLElement;
    const bar = this.el.elevbar as HTMLElement;
    let active = -1;
    const set = (clientY: number) => {
      const r = bar.getBoundingClientRect();
      this.elevation = Math.max(0, Math.min(1, 1 - (clientY - r.top) / r.height));
    };
    wrap.addEventListener('pointerdown', (e) => { e.preventDefault(); active = e.pointerId; wrap.setPointerCapture(e.pointerId); set(e.clientY); });
    wrap.addEventListener('pointermove', (e) => { if (e.pointerId === active) set(e.clientY); });
    const end = (e: PointerEvent) => { if (e.pointerId === active) active = -1; };
    wrap.addEventListener('pointerup', end); wrap.addEventListener('pointercancel', end);
  }

  private bindSteer(): void {
    const pad = this.el.steer as HTMLElement;
    const knob = this.el.steerknob as HTMLElement;
    // The helm is a 2-D virtual joystick: X = rudder (unchanged), Y = a FORWARD "throttle" intent. Pushing the
    // knob forward (up) auto-hoists the sails from furled (applied in sampleInput via helmAutoSail) so a newcomer
    // who shoves the stick forward actually starts moving — the desktop W behaviour, on the touch helm.
    const set = (clientX: number, clientY: number) => {
      const r = pad.getBoundingClientRect();
      const v = Math.max(-1, Math.min(1, ((clientX - r.left) / r.width) * 2 - 1));
      const f = Math.max(-1, Math.min(1, 1 - ((clientY - r.top) / r.height) * 2)); // up = +1 (forward)
      this.rudder = v;
      this.helmForward = f;
      knob.style.transform = `translate(${v * 40}%, ${-f * 40}%)`;
    };
    pad.addEventListener('pointerdown', (e) => { this.steerId = e.pointerId; pad.setPointerCapture(e.pointerId); pad.classList.add('dragging'); set(e.clientX, e.clientY); });
    pad.addEventListener('pointermove', (e) => { if (e.pointerId === this.steerId) set(e.clientX, e.clientY); });
    const end = (e: PointerEvent) => { if (e.pointerId === this.steerId) { this.steerId = -1; this.rudder = 0; this.helmForward = 0; knob.style.transform = 'translate(0,0)'; pad.classList.remove('dragging'); } };
    pad.addEventListener('pointerup', end); pad.addEventListener('pointercancel', end);
  }

  /** Compact sail "throttle": each tap advances furled → half → full → furled. Keyboard W/S stays analog;
   *  a tap snaps to the nearest step then advances, so the two input paths never fight. */
  private bindSail(): void {
    const btn = this.el.sail as HTMLElement;
    btn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      let cur = 0; for (let i = 1; i < SAIL_STEPS.length; i++) if (Math.abs(SAIL_STEPS[i]! - this.sail) < Math.abs(SAIL_STEPS[cur]! - this.sail)) cur = i;
      this.sail = SAIL_STEPS[(cur + 1) % SAIL_STEPS.length]!;
      this.refreshSail();
    });
  }

  private refreshSail(): void {
    (this.el.sailfill as HTMLElement).style.height = `${this.sail * 100}%`;
    let near = 0; for (let i = 1; i < SAIL_STEPS.length; i++) if (Math.abs(SAIL_STEPS[i]! - this.sail) < Math.abs(SAIL_STEPS[near]! - this.sail)) near = i;
    const lbl = (this.el.sail as HTMLElement).querySelector('.lbl'); if (lbl) lbl.textContent = SAIL_NAMES[near]!;
  }

  private bindButtons(): void {
    // hold a fire button to AIM that broadside (shows the bearing guide + zooms); release to FIRE.
    const fireBtn = (el: HTMLElement, side: 0 | 1) => {
      el.addEventListener('pointerdown', (e) => { e.preventDefault(); el.setPointerCapture(e.pointerId); this.aimingSide = side; this.aimStartY = e.clientY; this.aimStartElev = this.elevation; });
      // hold the fire button + slide the thumb UP to raise the cannon (longer range), DOWN to lower it —
      // one thumb aims, sets range, and fires. The pointer is captured, so the slide keeps tracking off-button.
      el.addEventListener('pointermove', (e) => {
        if (this.aimingSide !== side) return;
        const dy = this.aimStartY - e.clientY; // up = positive
        this.elevation = Math.max(0, Math.min(1, this.aimStartElev + dy / 190));
      });
      el.addEventListener('pointerup', (e) => { e.preventDefault(); if (this.aimingSide === side) { if (side === 0) this.fireL = true; else this.fireR = true; this.aimingSide = -1; } });
      el.addEventListener('pointercancel', () => { if (this.aimingSide === side) this.aimingSide = -1; });
    };
    fireBtn(this.el.fireL as HTMLElement, 0);
    fireBtn(this.el.fireR as HTMLElement, 1);
    (this.el.struggle as HTMLElement).addEventListener('pointerdown', (e) => { e.preventDefault(); this.struggle = true; });
    (this.el.actionbtn as HTMLElement).addEventListener('pointerdown', (e) => { e.preventDefault(); this.actionReq = true; this.fishStrikeStamp(); });
    const bail = this.el.bail as HTMLElement;
    bail.addEventListener('pointerdown', (e) => { e.preventDefault(); this.bail = true; bail.classList.add('on'); });
    const bailUp = () => { this.bail = false; bail.classList.remove('on'); };
    bail.addEventListener('pointerup', bailUp); bail.addEventListener('pointercancel', bailUp); bail.addEventListener('pointerleave', bailUp);
    (this.el.anchor as HTMLElement).addEventListener('pointerdown', (e) => { e.preventDefault(); this.toggleAnchor(); });
    (this.el.ammo as HTMLElement).addEventListener('pointerdown', (e) => { e.preventDefault(); this.cycleAmmo(); });
  }

  private cycleAmmo(next = (this.ammo + 1) % AMMO_COUNT): void {
    this.ammo = next % AMMO_COUNT;
    this.setAmmoLabel();
    this.explainAmmo(this.ammo); // frente 7b: first pick of each shot type → a one-time explainer toast
  }
  private setAmmoLabel(stock = -1): void {
    const btn = this.el.ammo as HTMLElement;
    const ic = btn.querySelector('.ic') as HTMLElement | null;
    if (ic) ic.innerHTML = itemIcon(AMMO_ICONS[this.ammo] ?? AMMO_ICONS[0], 'ammo-ic');
    const lbl = this.el.ammolbl as HTMLElement | undefined;
    if (lbl) lbl.textContent = (AMMO_LABELS[this.ammo] ?? AMMO_LABELS[AmmoType.ROUND]) + (stock >= 0 ? ` ×${stock}` : '');
    btn.classList.toggle('empty', stock === 0);
    btn.classList.toggle('chain', this.ammo === AmmoType.CHAIN);
    btn.classList.toggle('grape', this.ammo === AmmoType.GRAPE);
  }

  /** ---- Ammo economy (round finito) ---- All server-authoritative via the self snapshot; this only mirrors. */
  /** Would the server grant a broadside right now? Loaded special fires itself; otherwise it needs powder
   *  (round) in the magazine. -1 = no snapshot yet — never block before the first self state arrives. */
  private canFire(): boolean {
    if (this.ammo === AmmoType.CHAIN && this.chainStockV > 0) return true;
    if (this.ammo === AmmoType.GRAPE && this.grapeStockV > 0) return true;
    return this.roundStockV !== 0;
  }
  private noPowderToast(): void {
    const now = performance.now();
    if (now - this.noPowderMs < 2500) return; // don't stack while the button is mashed
    this.noPowderMs = now;
    this.toast('🧨 Out of powder! Resupply at any port.');
  }
  /** Per-snapshot powder bookkeeping: the FIRE buttons dim when the server would deny the broadside, the
   *  low-powder warning fires once per depletion (re-armed by any resupply above 25%), and the one-time
   *  chain/grape fallback notice re-arms once that special stock is replenished. */
  private updatePowder(self: SelfState): void {
    this.roundStockV = self.roundStock; this.chainStockV = self.chainStock; this.grapeStockV = self.grapeStock;
    if (self.chainStock > 0) this.fallbackNoticed &= ~(1 << AmmoType.CHAIN);
    if (self.grapeStock > 0) this.fallbackNoticed &= ~(1 << AmmoType.GRAPE);
    const low = AMMO_ROUND_MAX * 0.25;
    if (self.roundStock > low) this.lowPowderArmed = true;
    else if (this.lowPowderArmed && self.roundStock > 0) { this.lowPowderArmed = false; this.toast("🧨 Powder's running low! Resupply at any port."); }
    const dry = !this.canFire();
    (this.el.fireL as HTMLElement | undefined)?.classList.toggle('nopowder', dry);
    (this.el.fireR as HTMLElement | undefined)?.classList.toggle('nopowder', dry);
  }
  /** Called (from the FIRE event stream) when the server fell back from an empty chain/grape stock to round
   *  shot — the one-time diegetic notice that fixes the old SILENT fallback. */
  notifyAmmoFallback(type: number): void {
    const bit = 1 << type;
    if (this.fallbackNoticed & bit) return;
    this.fallbackNoticed |= bit;
    this.toast(type === AmmoType.GRAPE ? '✸ Grape spent — falling back to round shot.' : '⛓ Chain spent — falling back to round shot.');
  }
  /** One-time (persisted) explainer the first time each shot type is selected — kills the "what is round/
   *  chain/grape?" mystery. All three are real, working ammo (verified server-side); nothing is hidden. */
  private ammoSeen = -1;
  private explainAmmo(type: number): void {
    if (this.ammoSeen < 0) { try { this.ammoSeen = Number(localStorage.getItem('sor:ammoSeen')) || 0; } catch { this.ammoSeen = 0; } }
    const bit = 1 << type;
    if (this.ammoSeen & bit) return;
    this.ammoSeen |= bit;
    try { localStorage.setItem('sor:ammoSeen', String(this.ammoSeen)); } catch { /* private mode */ }
    this.toast(AMMO_HINTS[type] ?? AMMO_HINTS[AmmoType.ROUND]);
  }

  /** Frente 3 — one-time (persisted) "wind is your engine" nudge the first time the captain gets underway.
   *  This is the fix for "I upgraded Speed and barely notice it": the biggest swing in live speed is the
   *  WIND, not the upgrade — so we teach the pennant + point of sail once, and the speedo shows the ceiling. */
  private windSeen = false;
  private explainWind(speed: number): void {
    if (this.windSeen || speed < 2.2) return; // wait until actually moving
    this.windSeen = true;
    let seen = false; try { seen = !!localStorage.getItem('sor:windHint'); } catch { /* private mode */ }
    if (seen) return;
    try { localStorage.setItem('sor:windHint', '1'); } catch { /* private mode */ }
    this.toast('🌬 Wind is your engine — mind the pennant. Run WITH the wind (pennant at top) for top speed.');
  }

  setGrappleRole(role: 'plundering' | 'boarded' | null): void {
    this.grappleRole = role;
    const row = this.el.strugglerow as HTMLElement | undefined;
    if (row) row.style.display = role === 'boarded' ? 'grid' : 'none';
  }

  private setAnchorVisual(): void {
    const btn = this.el.anchor as HTMLElement;
    btn.classList.toggle('on', this.anchor);
    const lbl = btn.querySelector('.lbl'); if (lbl) lbl.textContent = this.anchor ? 'ANCHORED' : 'ANCHOR';
  }

  private toggleAnchor(): void {
    this.anchor = !this.anchor;
    this.setAnchorVisual();
    // frente 4: an unmistakable state change — a short toast on every drop/weigh (the button also latches amber)
    this.toast(this.anchor ? '⚓ Anchor down — holding position' : '⚓ Anchor aweigh — under way');
  }

  /** Raise the anchor (SET SAIL from the base panel) — no-op if already raised. */
  raiseAnchor(): void {
    if (!this.anchor) return;
    this.anchor = false;
    this.setAnchorVisual();
  }

  /** Drop the anchor (auto-sail arrival) — no-op if already down. Mirrors {@link raiseAnchor} so the
   *  ANCHOR button latches its amber "ANCHORED" state instead of being flipped behind the visuals. */
  dropAnchor(): void {
    if (this.anchor) return;
    this.anchor = true;
    this.setAnchorVisual();
  }

  /** Full-world chart overlay (M / 🗺): wire the open button, the ✕ / backdrop close, and a tap-to-sail on the
   *  big canvas that reuses the exact same waypoint path the corner minimap uses. */
  private bindBigmap(): void {
    (this.el.mapexpand as HTMLElement)?.addEventListener('pointerdown', (e) => { e.preventDefault(); this.toggleBigmap(); });
    (this.el.bigmapclose as HTMLElement)?.addEventListener('pointerdown', (e) => { e.preventDefault(); this.closeBigmap(); });
    // click the dark backdrop (never the inner panel) to dismiss
    (this.el.bigmap as HTMLElement)?.addEventListener('pointerdown', (e) => { if (e.target === this.el.bigmap) this.closeBigmap(); });
    const bc = this.el.bigmapcanvas as HTMLCanvasElement | undefined;
    if (!bc) return;
    let dx = 0, dy = 0, dt = 0;
    bc.addEventListener('pointerdown', (e) => { dx = e.clientX; dy = e.clientY; dt = performance.now(); });
    bc.addEventListener('contextmenu', (e) => e.preventDefault()); // a mouse long-press must not pop the browser menu
    bc.addEventListener('pointerup', (e) => {
      if (Math.hypot(e.clientX - dx, e.clientY - dy) > 12) return; // a drag/pan, not a tap
      // an open mark overlay swallows this tap: tap-away = dismiss, never an accidental course
      if (this.markSel || this.markInfoId != null) { this.closeMarkOverlays(); return; }
      const rect = bc.getBoundingClientRect();
      const wx = (e.clientX - rect.left) / rect.width * WORLD_SIZE;
      const wy = (e.clientY - rect.top) / rect.height * WORLD_SIZE;
      // ⚑ Armada marks keep their own, unmistakable gestures so plotting a course never collides with
      // marking: a ≥450 ms HOLD (the same deliberate-hold the minimap ping trained) opens the glyph
      // picker; a plain TAP on a pin opens its paper tag; a plain tap on open water stays the course.
      if (performance.now() - dt >= 450) { this.openMarkPicker(wx, wy, e.clientX, e.clientY, rect); return; }
      const hit = this.markAt(e.clientX, e.clientY, rect);
      if (hit) { this.openMarkInfo(hit, rect); return; }
      // retap on the existing mark cancels; otherwise stamp the course, let the X LAND on the big sheet
      // (the juice IS the moment), then roll the chart up so the sea (and the helm) is visible again
      if (this.waypoint) {
        const sx = rect.left + this.waypoint.x / WORLD_SIZE * rect.width;
        const sy = rect.top + this.waypoint.y / WORLD_SIZE * rect.height;
        if (Math.hypot(e.clientX - sx, e.clientY - sy) < 22) { this.clearWaypoint('🧭 Course cancelled'); this.closeBigmap(); return; }
      }
      this.stampCourse(wx, wy);
      if (this.bigmapCloseTimer) clearTimeout(this.bigmapCloseTimer);
      this.bigmapCloseTimer = setTimeout(() => { this.bigmapCloseTimer = null; this.closeBigmap(); }, 700);
      this.toast('🧭 Course set — the helm steers there. Tap the mark, or take the wheel, to cancel.');
    });
  }

  /* ------------------- ⚑ Armada chart markers (v54): picker + paper tag ------------------- */

  /** Fill the glyph picker with the 6 stamp buttons (2 rows × 3, each a 44 px wax-stamp target) and park
   *  the keyboard while the label input is focused (Q/E/M… must not fire ship actions mid-typing). */
  private buildMarkPicker(): void {
    const rows = [this.el.mprow0 as HTMLElement | undefined, this.el.mprow1 as HTMLElement | undefined];
    if (!rows[0] || !rows[1]) return;
    MARK_GLYPHS.forEach((g, i) => {
      const b = document.createElement('button');
      b.className = 'mp-g'; b.title = g.name; b.setAttribute('aria-label', `Stamp ${g.name}`);
      b.innerHTML = g.svg;
      b.addEventListener('pointerdown', (e) => { e.preventDefault(); e.stopPropagation(); this.placeMark(i); });
      rows[i < 3 ? 0 : 1]!.appendChild(b);
    });
    const lbl = this.el.mplabel as HTMLInputElement;
    for (const ev of ['keydown', 'keyup'] as const) {
      lbl.addEventListener(ev, (e) => { e.stopPropagation(); if ((e as KeyboardEvent).key === 'Escape') this.closeMarkOverlays(); });
    }
  }

  /** The pin under a screen point on the big chart (≤ ~9 chart-units ≈ a 44 px touch disc), or null. */
  private markAt(cx: number, cy: number, rect: DOMRect): NonNullable<MinimapData['clanMarks']>[number] | null {
    const ux = (cx - rect.left) / rect.width * 132, uy = (cy - rect.top) / rect.height * 132;
    const k = 132 / WORLD_SIZE;
    let best: NonNullable<MinimapData['clanMarks']>[number] | null = null, bd = 9;
    for (const m of this.clanMarks) {
      const d = Math.hypot(m.x * k - ux, m.y * k - uy);
      if (d < bd) { bd = d; best = m; }
    }
    return best;
  }

  /** Long-press on the big chart: open the diegetic glyph picker at the pressed sea spot (clan members
   *  only — everyone else gets a friendly recruiting hint, the server enforces the real gate anyway). */
  private openMarkPicker(wx: number, wy: number, cx: number, cy: number, rect: DOMRect): void {
    if (!this.inClanNow) { this.toast('⚑ Join an Armada to mark the chart for your crew.'); return; }
    this.closeMarkOverlays();
    this.markSel = { x: wx, y: wy };
    (this.el.mplabel as HTMLInputElement).value = '';
    const mp = this.el.markpick as HTMLElement;
    mp.classList.add('open');
    this.placeOverlay(mp, cx, cy, rect);
  }

  /** A glyph button was stamped: fire the PLACE intent with the optional label and fold the picker away.
   *  The pin appears when the server's S_MARKS broadcast lands — authority stays on the other side. */
  private placeMark(icon: number): void {
    const sel = this.markSel;
    if (!sel) return;
    const label = ((this.el.mplabel as HTMLInputElement).value ?? '').slice(0, CLAN_MARKERS.LABEL_MAX);
    this.closeMarkOverlays();
    this.onMarkPlace?.(sel.x, sel.y, icon, label);
  }

  /** Tap a pin: its paper tag — glyph name, the label in quotes, the author's byline, and (author /
   *  officer / leader only) the remove action. Anchored beside the pin on the sheet. */
  private openMarkInfo(mk: NonNullable<MinimapData['clanMarks']>[number], rect: DOMRect): void {
    this.closeMarkOverlays();
    this.markInfoId = mk.id;
    const g = MARK_GLYPHS[mk.icon] ?? MARK_GLYPHS[MARK_GLYPHS.length - 1]!;
    const mi = this.el.markinfo as HTMLElement;
    const canRemove = mk.mine || this.canModMarks;
    mi.innerHTML = `<span class="mi-t">⚑ ${g.name}</span>` +
      (mk.label ? `<span class="mi-l">“${hudEsc(mk.label)}”</span>` : '') +
      `<span class="mi-by">— ${hudEsc(mk.author)}</span>` +
      (canRemove ? '<button class="mi-rm">✕ SCRAPE IT OFF</button>' : '');
    if (canRemove) {
      (mi.querySelector('.mi-rm') as HTMLElement).addEventListener('pointerdown', (e) => {
        e.preventDefault(); e.stopPropagation();
        const id = this.markInfoId;
        this.closeMarkOverlays();
        if (id != null) this.onMarkRemove?.(id);
      });
    }
    mi.classList.add('open');
    const k = rect.width / WORLD_SIZE;
    this.placeOverlay(mi, rect.left + mk.x * k, rect.top + mk.y * k, rect);
  }

  /** Pin an overlay near a screen point, clamped inside the chart sheet (mobile: it must never spill). */
  private placeOverlay(el: HTMLElement, cx: number, cy: number, rect: DOMRect): void {
    const w = el.offsetWidth || 180, h = el.offsetHeight || 120;
    let x = cx - rect.left + 14, y = cy - rect.top + 10;
    if (x + w > rect.width - 6) x = Math.max(6, cx - rect.left - w - 14);
    if (y + h > rect.height - 6) y = Math.max(6, rect.height - h - 6);
    el.style.left = `${Math.round(x)}px`; el.style.top = `${Math.round(y)}px`;
  }

  /** Fold away both mark overlays (picker + paper tag) and the pending-placement ink spot. */
  private closeMarkOverlays(): void {
    this.markSel = null;
    this.markInfoId = null;
    (this.el.markpick as HTMLElement | undefined)?.classList.remove('open');
    (this.el.markinfo as HTMLElement | undefined)?.classList.remove('open');
  }

  /** Lay a tap-to-sail course AND fire the diegetic plot feedback: the stamp FX state (ink ripple, red X,
   *  specks, compass-needle sweep — drawn by renderChart on both charts) + the host's SFX/haptic hook. */
  private stampCourse(wx: number, wy: number): void {
    this.waypoint = { x: wx, y: wy };
    this.plotFx = { x: wx, y: wy, t0: performance.now(), bearing: Math.atan2(wy - this.lastSelf.y, wx - this.lastSelf.x) };
    this.onWaypointSet?.(wx, wy);
  }
  toggleBigmap(): void { if (this.bigmapOpen) this.closeBigmap(); else this.openBigmap(); }
  openBigmap(): void {
    if (this.bigmapCloseTimer) { clearTimeout(this.bigmapCloseTimer); this.bigmapCloseTimer = null; }
    this.bigmapOpen = true;
    (this.el.bigmap as HTMLElement)?.classList.add('open');
    document.body.classList.add('bigmap-open'); // world floaters (event chip, MENU, chat…) yield the stage
  }
  closeBigmap(): void {
    this.bigmapOpen = false;
    this.closeMarkOverlays(); // the chart rolls up with no orphaned picker/paper-tag left floating
    (this.el.bigmap as HTMLElement)?.classList.remove('open');
    document.body.classList.remove('bigmap-open');
  }

  /** Frente 6d: clear the tap-to-sail course (arrival, manual override, or a retap). */
  clearWaypoint(reason?: string): void {
    if (!this.waypoint) return;
    this.waypoint = null;
    this.plotFx = null;
    if (reason) this.toast(reason);
  }

  /** "Sail home": lay a tap-to-sail course onto the player's OWN base so the autopilot steers there.
   *  Reuses the exact waypoint path a chart tap uses (client-only, zero wire). A retap while already
   *  bound for home cancels the course. No-op until S_BASES has told us where home is. */
  private sailHome(): void {
    const h = this.homeBase;
    if (!h) { this.toast('🏠 Your base position is still loading…'); return; }
    if (this.waypoint && (this.waypoint.x - h.x) ** 2 + (this.waypoint.y - h.y) ** 2 < 1) {
      this.clearWaypoint('🧭 Home course cancelled');
      return;
    }
    this.stampCourse(h.x, h.y);
    this.toast('🏠 Course set for home — the helm steers you back. Take the wheel to cancel.');
  }

  /** True while the player is actively working the helm (drag pad or A/D/←/→) — cancels the autopilot. */
  manualSteerActive(): boolean {
    return this.steerId !== -1 || this.heldKeys.has('a') || this.heldKeys.has('d') ||
      this.heldKeys.has('arrowleft') || this.heldKeys.has('arrowright');
  }

  setDocked(on: boolean): void {
    (this.el.dockind as HTMLElement).style.display = on ? 'inline-block' : 'none';
  }

  /** Wave 2b: the contextual sea-action button (fish/dive). `null` hides it; `urgent` pulses (reel now!). */
  setContextAction(label: string | null, icon = '🎣', urgent = false): void {
    const row = this.el.contextrow as HTMLElement | undefined;
    const btn = this.el.actionbtn as HTMLElement | undefined;
    if (!row || !btn) return;
    if (!label) { row.style.display = 'none'; return; }
    row.style.display = 'grid';
    btn.classList.toggle('urgent', urgent);
    (btn.querySelector('.ic') as HTMLElement).textContent = icon;
    (btn.querySelector('.lbl') as HTMLElement).textContent = label;
  }

  private fishUI = { act: 0, elapsed: 0, perfectS: 0.45 };
  /** 🎣 fishing-polish: the LINE-TENSION plate (parchment/brass). act 1 = line out — the needle sways
   *  slack and the gold PERFECT band is already visible, so the strike is telegraphed BEFORE the tug;
   *  act 2 = the fight — the needle sweeps the reel window (gold = perfect hook, red tail = escape).
   *  Pure presentation each frame; the SERVER still measures the reel reaction on its own clock. */
  setFishing(act: number, elapsedS: number, windowS: number, perfectS: number): void {
    const row = this.el.fishrow as HTMLElement | undefined;
    if (!row) return;
    const fishing = act === 1 || act === 2;
    this.fishUI.act = fishing ? act : 0; this.fishUI.elapsed = elapsedS; this.fishUI.perfectS = perfectS;
    row.style.display = fishing ? 'grid' : 'none';
    if (!fishing) return;
    const plate = this.el.fishplate as HTMLElement, zone = this.el.fpzone as HTMLElement;
    const needle = this.el.fpneedle as HTMLElement, tip = this.el.fptip as HTMLElement;
    zone.style.width = `${Math.round((perfectS / windowS) * 100)}%`;
    plate.classList.toggle('bite', act === 2);
    if (act === 1) {
      needle.style.left = `${(Math.sin(performance.now() / 480) + 1) * 4.5}%`; // slack sway at the gold edge
      tip.textContent = 'Wait for the tug — strike in the gold';
    } else {
      const k = Math.min(1, elapsedS / windowS);
      needle.style.left = `${(k * 100).toFixed(1)}%`;
      tip.textContent = k < perfectS / windowS ? 'NOW — hook it!' : "REEL! It's taking the line!";
    }
  }
  /** The strike verdict, stamped the instant the captain hits REEL inside the gold band (display-only —
   *  the tier is still the server's call; this celebrates the timing skill itself). */
  private fishStrikeStamp(): void {
    if (this.fishUI.act !== 2 || this.fishUI.elapsed > this.fishUI.perfectS) return;
    const st = this.el.fpstamp as HTMLElement | undefined;
    if (!st) return;
    st.classList.remove('go'); void st.offsetWidth; st.classList.add('go');
  }

  /** Wave 2b: called by main to invoke a consumable (sends C_CONSUME). */
  onConsume: ((buffId: number) => void) | null = null;
  /** Wave 3.7: called when the player accepts a convoy escort contract. */
  onEscort: (() => void) | null = null;
  private escortBound = false;
  /** Show/hide the "escort this convoy" offer (a merchant convoy is within range). */
  setEscortAvailable(on: boolean): void {
    const btn = this.el.escortbtn as HTMLElement;
    if (!this.escortBound) { this.escortBound = true; btn.addEventListener('click', () => this.onEscort?.()); }
    btn.classList.toggle('on', on);
  }
  private buffSig = '';
  private buffBound = false;
  /** Populate the ⚗ quick-sip menu from the base-inventory consumables (id/count). Hidden if none. */
  setConsumables(list: { id: number; count: number; icon: string; label: string }[]): void {
    const btn = this.el.buffbtn as HTMLElement | undefined;
    const pop = this.el.buffpop as HTMLElement | undefined;
    if (!btn || !pop) return;
    if (!this.buffBound) { this.buffBound = true; btn.addEventListener('pointerdown', (e) => { e.preventDefault(); pop.classList.toggle('on'); }); }
    if (!list.length) { btn.style.display = 'none'; pop.classList.remove('on'); this.buffSig = ''; return; }
    btn.style.display = 'flex';
    const sig = list.map((c) => `${c.id}:${c.count}`).join(',');
    if (sig === this.buffSig) return; // only rebuild on change (avoids per-frame DOM churn)
    this.buffSig = sig;
    pop.innerHTML = '<div class="bph">⚗ Sip a consumable</div>' + list.map((c) =>
      `<div class="bp-row" data-buff="${c.id}"><span class="bi">${itemIcon(c.icon)}</span><span class="bn">${c.label.split(' — ')[0]}<span class="bl"> ${c.label.includes(' — ') ? c.label.split(' — ')[1] : ''}</span></span><span class="bc">×${c.count}</span></div>`).join('');
    pop.querySelectorAll('[data-buff]').forEach((r) => r.addEventListener('pointerdown', (e) => {
      e.preventDefault(); this.onConsume?.(Number((r as HTMLElement).dataset.buff)); pop.classList.remove('on');
    }));
  }

  /** Show the active drink/oil buff badges (icon + effect + minutes left), plus the SHORT fish buff badge
   *  (seconds left) or its re-sip cooldown when it's cooling down. */
  setBuffs(drink: number, oil: number, buffS: number, fish = 0, fishS = 0, fishCd = 0): void {
    const mins = Math.max(0, Math.ceil(buffS / 60));
    const fmt = (id: number) => { const b = buffOf(id); return b ? `${b.icon} ${b.label} · ${mins}m` : ''; };
    const d = this.el.buffd as HTMLElement, o = this.el.buffo as HTMLElement, f = this.el.bufff as HTMLElement | undefined;
    if (drink) { d.style.display = 'inline-block'; d.textContent = fmt(drink); } else d.style.display = 'none';
    if (oil) { o.style.display = 'inline-block'; o.textContent = fmt(oil); } else o.style.display = 'none';
    if (f) {
      const b = fish ? buffOf(fish) : undefined;
      if (b) { f.style.display = 'inline-block'; f.textContent = `${b.icon} ${b.label} · ${Math.max(0, Math.ceil(fishS))}s`; }
      else if (fishCd > 0) { f.style.display = 'inline-block'; f.textContent = `🐟 cooling · ${Math.max(0, Math.ceil(fishCd))}s`; }
      else f.style.display = 'none';
    }
  }

  /** Fix 4 frente 2: the 🧺 chip's tap-toast breakdown (set by main.ts from the live S_GOODS hold). */
  private holdDetail = '';

  /** Wave 2b: the ship HOLD chip — total gathered materials carried (hidden when empty).
   *  Fix 4 frente 2: also carries the tap-toast breakdown text (what exactly is in the hold). */
  setHold(count: number, detail = ''): void {
    this.holdDetail = detail;
    const chip = this.el.holdchip as HTMLElement | undefined;
    if (!chip) return;
    if (count > 0) { chip.style.display = 'flex'; (this.el.holdn as HTMLElement).textContent = `${count}`; }
    else chip.style.display = 'none';
  }

  /** Fix 4 frente 2: the 💰 CHEST chip — how many treasure chests are aboard (0 hides it). Shows a "×N" count
   *  when a hauler carries more than one (the Fluyt's two). Persistent (the pickup toast fades; this doesn't),
   *  tap → "sell it at an outpost ring". */
  setChest(count: number): void {
    const chip = this.el.chestchip as HTMLElement | undefined;
    if (chip) chip.style.display = count > 0 ? 'flex' : 'none';
    const label = this.el.chestn as HTMLElement | undefined;
    if (label) label.textContent = count > 1 ? `CHEST ×${count}` : 'CHEST';
  }

  /** Cargo Runs (V1): the ⚖ WEIGHT chip (trade goods aboard + the live speed penalty) and the 🏭 HAUL READY cue
   *  (the home warehouse is full — dock to load a run). `penaltyPct` is the top-speed scrub % from the shared
   *  weight curve (computed in main.ts). Both hidden when not relevant. */
  setCargoRun(cargoUnits: number, cargoCap: number, warehouseUnits: number, warehouseCap: number, penaltyPct: number, preview = ''): void {
    const chip = this.el.cargorunchip as HTMLElement | undefined;
    if (chip) {
      if (cargoUnits > 0) {
        chip.style.display = 'flex';
        // 💨 marks the number as SPEED lost (a bare "−11%" confused players); tint it neutral→amber→red as the
        // laden penalty grows toward its ~−40% ceiling. Dirty-checked so we don't re-parse innerHTML each frame.
        const html = penaltyPct > 0
          ? `${cargoUnits} · <span class="cr-pen ${penaltyPct >= 30 ? 'red' : penaltyPct >= 15 ? 'amber' : ''}">💨−${penaltyPct}%</span>`
          : `${cargoUnits}`;
        if (html !== this.lastCargoT) { (this.el.cargorunt as HTMLElement).innerHTML = html; this.lastCargoT = html; }
        const base = `Cargo aboard: ${cargoUnits}/${Math.round(cargoCap)} — a laden hull sails ${penaltyPct}% slower (💨). Deliver at an outpost ring for doubloons (farther port = more pay).`;
        chip.title = preview ? `${base}\nDelivery preview — ${preview}` : base;
      } else chip.style.display = 'none';
    }
    const haul = this.el.haulchip as HTMLElement | undefined;
    if (haul) {
      // 🏭 warehouse-fill cue: from ~SHOW_FRAC up it shows the PARTIAL fill ("🏭 NN%" + a progressive bar) so a
      // captain who lost the passive ⓓ mint can watch the run build; at FULL_FRAC it flips to the strong
      // "HAUL READY". Only while the hold is empty (laden → the ⚖ chip owns the state). Pure view math in uxhints.
      const wv = warehouseChipView(warehouseUnits, warehouseCap, cargoUnits, UX_HINTS.WAREHOUSE);
      haul.style.display = wv.show ? 'flex' : 'none';
      if (wv.show) {
        const key = wv.full ? 'FULL' : `${wv.pct}`; // re-render only when the bucket crosses a whole percent
        if (key !== this.lastHaul) {
          this.lastHaul = key;
          haul.classList.toggle('full', wv.full);
          // color-band the bar/label by how loaded the warehouse is (dim → amber → hot near full).
          haul.classList.remove('wh-lo', 'wh-mid', 'wh-hi');
          haul.classList.add(wv.full ? 'wh-hi' : wv.fill >= 0.66 ? 'wh-hi' : wv.fill >= 0.4 ? 'wh-mid' : 'wh-lo');
          (this.el.hault as HTMLElement).textContent = wv.full ? 'HAUL READY' : `${wv.pct}%`;
          (this.el.haulfill as HTMLElement).style.width = `${Math.round(wv.fill * 100)}%`;
          haul.title = wv.full
            ? `Warehouse full (${Math.round(warehouseUnits)}) — dock at home to load a full cargo run.`
            : `Warehouse ${wv.pct}% full (${Math.round(warehouseUnits)}/${Math.round(warehouseCap)}) — passive trade cargo is piling up. Dock at home to load a run.`;
        }
      } else this.lastHaul = ''; // hidden → reset so a re-show re-renders from scratch
    }
  }

  private bindKeys(): void {
    const keys = new Set<string>();
    window.addEventListener('keydown', (e) => {
      if (this.editMode) return; // HUD Forge open — the sea HUD must not react
      this.hud.classList.add('desktop');
      const k = e.key.toLowerCase();
      if (!keys.has(k)) {
        if (k === 'q') this.aimingSide = 0;
        if (k === 'e') this.aimingSide = 1;
        if (k === 'g') this.struggle = true;
        if (k === 'r') { this.actionReq = true; this.fishStrikeStamp(); } // contextual sea action (cast/reel/dive)
        if (k === 'f') this.toggleAnchor();
        if (k === 'x') this.cycleAmmo();
        if (k === '1') this.cycleAmmo(0);
        if (k === '2') this.cycleAmmo(1);
        if (k === '3') this.cycleAmmo(2);
        if (k === 'f3' || k === '`') this.hud.classList.toggle('debug');
        if (k === 'm') this.toggleBigmap(); // open/close the full-world chart
        if (k === 'escape' && this.bigmapOpen) this.closeBigmap();
      }
      keys.add(k);
      if (k === ' ') this.bail = true;
    });
    window.addEventListener('keyup', (e) => {
      if (this.editMode) return;
      const k = e.key.toLowerCase(); keys.delete(k);
      if (k === ' ') this.bail = false;
      if (k === 'q' && this.aimingSide === 0) { this.fireL = true; this.aimingSide = -1; }
      if (k === 'e' && this.aimingSide === 1) { this.fireR = true; this.aimingSide = -1; }
    });
    const clear = () => { keys.clear(); this.rudder = 0; this.bail = false; this.aimingSide = -1; (this.el.bail as HTMLElement)?.classList.remove('on'); };
    window.addEventListener('blur', clear);
    document.addEventListener('visibilitychange', () => { if (document.hidden) clear(); });
    this.heldKeys = keys;
  }
  private heldKeys: Set<string> = new Set();

  sampleInput(): InputState {
    if (this.heldKeys.has('a') || this.heldKeys.has('arrowleft')) this.rudder = -1;
    else if (this.heldKeys.has('d') || this.heldKeys.has('arrowright')) this.rudder = 1;
    else if (this.steerId === -1) this.rudder = 0;
    const up = this.heldKeys.has('w') || this.heldKeys.has('arrowup');
    const down = this.heldKeys.has('s') || this.heldKeys.has('arrowdown');
    if (this.aimingSide >= 0) {
      const es = CANNON.ELEV_STEP / INPUT_HZ;
      if (up) this.elevation = Math.min(1, this.elevation + es);
      if (down) this.elevation = Math.max(0, this.elevation - es);
    } else {
      if (up || down) { // keyboard analog sail trim -> keep the throttle button's fill in sync
        if (up) this.sail = Math.min(1, this.sail + 0.04);
        if (down) this.sail = Math.max(0, this.sail - 0.04);
        (this.el.sailfill as HTMLElement).style.height = `${this.sail * 100}%`;
      }
    }
    // Mobile onboarding (helmsail.ts): pushing the helm joystick FORWARD auto-hoists the sails from furled so
    // "shove the stick forward = the ship goes" — the desktop-W behaviour on the touch helm. Only ever raises a
    // furled rig (never overrides a deliberate trim), and weighs a dropped anchor so the promise isn't broken.
    if (this.helmForward > 0) {
      if (helmWeighAnchor(this.helmForward, this.anchor)) this.raiseAnchor();
      const hoisted = helmAutoSail(this.helmForward, this.sail);
      if (hoisted !== this.sail) { this.sail = hoisted; (this.el.sailfill as HTMLElement).style.height = `${this.sail * 100}%`; this.refreshSail(); }
    }
    this.updateElevWidget();

    let buttons = 0;
    if (this.anchor) buttons |= Btn.ANCHOR;
    if (this.bail) buttons |= Btn.REPAIR;
    // Ammo economy: dry magazine — mirror the server's deny locally (toast instead of a dead request).
    // One shared gate catches every input path (touch fire buttons AND the Q/E keys).
    if ((this.fireL || this.fireR) && !this.canFire()) { this.fireL = false; this.fireR = false; this.noPowderToast(); }
    if (this.fireL) { buttons |= Btn.FIRE_LEFT; this.fireL = false; }
    if (this.fireR) { buttons |= Btn.FIRE_RIGHT; this.fireR = false; }
    if (this.struggle) { buttons |= Btn.STRUGGLE; this.struggle = false; }
    if (this.actionReq) { buttons |= Btn.ACTION; this.actionReq = false; }
    buttons = packAmmoBits(buttons, this.ammo);
    return { rudder: this.rudder, sailTarget: this.sail, anchor: this.anchor, buttons, elevation: this.elevation };
  }

  /** Show/fade the cannon-elevation gauge while aiming (vertical bar + a degrees readout of the barrel angle). */
  private updateElevWidget(): void {
    const wrap = this.el.elevwrap as HTMLElement | undefined;
    if (!wrap) return;
    const aiming = this.aimingSide >= 0;
    wrap.classList.toggle('aiming', aiming);
    const hint = this.el.aimhint as HTMLElement | undefined;
    if (hint) hint.classList.toggle('on', aiming);
    if (!aiming) return;
    const deg = Math.round(this.elevation * CANNON.ELEV_MAX_ANGLE * 180 / Math.PI);
    (this.el.elevfill as HTMLElement).style.height = `${this.elevation * 100}%`;
    (this.el.elevdeg as HTMLElement).textContent = `${deg}°`;
    if (hint) hint.innerHTML = `slide ↕ · <b>${deg}°</b>`;
  }

  aimSide(): number { return this.aimingSide; }
  ammoIndex(): number { return this.ammo; }
  elevationValue(): number { return this.elevation; }

  update(self: SelfState | null, speed: number, fps: number, alive: boolean, reloadL: number, reloadR: number, trophies = 0): void {
    if (self) {
      (this.el.gold as HTMLElement).textContent = `${self.doubloons}`;
      // soft-cap counter: "☀ 3.2k/5k" of the full-rate daily hold; goes amber+"FULL"-tinted past the cap
      const de = self.dailyEarned ?? 0, cap = ECONOMY.DAILY_EARN_CAP;
      (this.el.dailyearnt as HTMLElement).textContent = `${fmtK(Math.min(de, cap * 4))}/${fmtK(cap)}`;
      (this.el.dailyearn as HTMLElement).classList.toggle('full', de >= cap);
      (this.el.trophies as HTMLElement).textContent = `${trophies}`;
      const cargo = this.el.cargochip as HTMLElement;
      if (self.gold > 0) { cargo.style.display = 'flex'; (this.el.cargo as HTMLElement).textContent = `${self.gold}`; }
      else cargo.style.display = 'none';
      if (self.kills !== this.lastKills || self.deaths !== this.lastDeaths) { // dirty-check: avoid per-frame DOM writes
        this.lastKills = self.kills; this.lastDeaths = self.deaths;
        (this.el.kd as HTMLElement).textContent = `⚔ ${self.kills} · ☠ ${self.deaths}`;
      }
      (this.el.statline as HTMLElement).textContent = `spd ${speed.toFixed(1)} · ${fps | 0} fps`;
      (this.el.rigwarn as HTMLElement).style.display = self.riggingDamage > 0.05 ? 'inline-block' : 'none';
      const grappling = self.grappleFactor > 0.01;
      const gi = this.el.grappleind as HTMLElement;
      gi.style.display = grappling ? 'inline-block' : 'none';
      if (grappling) gi.textContent = this.grappleRole === 'plundering' ? '⛓ PLUNDERING' : this.grappleRole === 'boarded' ? '⛓ BOARDED!' : '⛓ GRAPPLE';
      if (this.grappleRole === 'boarded') {
        const escape = Math.max(0, Math.min(1, 1 - self.grappleFactor / GRAPPLE.DRAG));
        (this.el.strugglefill as HTMLElement).style.width = `${(escape * 100) | 0}%`;
      }
      // Ammo economy: every shot type shows its live count now (round included — it's finite).
      const stk = this.ammo === AmmoType.CHAIN ? self.chainStock : this.ammo === AmmoType.GRAPE ? self.grapeStock : self.roundStock;
      this.setAmmoLabel(stk);
      this.updatePowder(self);
    }
    this.reloadCue(this.el.fireL as HTMLElement, reloadL, true);
    this.reloadCue(this.el.fireR as HTMLElement, reloadR, false);
    (this.el.respawn as HTMLElement).style.display = alive ? 'none' : 'flex';
  }

  private rlReadyL = true; private rlReadyR = true;
  /** Reload feedback on a FIRE button: a cyan ring fills around it as it reloads (--rl 0->1), then a green
   *  "ready" pulse on the rising edge. The divisor adapts to the ACTUAL reload time (class/ammo multipliers
   *  make it >CANNON.RELOAD) by latching the peak reload seen since the shot, so the ring always spans 0→full. */
  private reloadCue(el: HTMLElement, reload: number, left: boolean): void {
    const ready = reload <= 0.05;
    let max = left ? this.maxReloadL : this.maxReloadR;
    if (reload > max) { max = reload; if (left) this.maxReloadL = reload; else this.maxReloadR = reload; } // latch the just-fired peak
    el.style.setProperty('--rl', String(ready ? 1 : Math.max(0, 1 - reload / Math.max(0.001, max))));
    const wasReady = left ? this.rlReadyL : this.rlReadyR;
    if (ready && !wasReady) { el.classList.remove('ready'); void el.offsetWidth; el.classList.add('ready'); }
    if (!ready) el.classList.remove('ready');
    if (left) this.rlReadyL = ready; else this.rlReadyR = ready;
  }

  setHp(hp01: number): void {
    const v = Math.max(0, Math.min(1, hp01));
    const hp = this.el.hp as HTMLElement;
    if (v < this.hp01 - 0.001) { const w = this.el.hpwrap as HTMLElement; w.classList.remove('hit'); void w.offsetWidth; w.classList.add('hit'); }
    this.hp01 = v;
    hp.style.width = `${v * 100}%`;
    hp.classList.toggle('full', v >= 0.985);
    hp.classList.toggle('low', v > 0 && v < 0.3);
    (this.el.hppct as HTMLElement).textContent = `${Math.round(v * 100)}%`;
    (this.el.bail as HTMLElement).classList.toggle('needs', v < 0.92);
  }
  setRespawn(seconds: number): void { (this.el.respawnt as HTMLElement).textContent = Math.ceil(seconds).toString(); }

  /** Flash a red "under raid" ping on the minimap for a few seconds (paired with a toast). */
  pingRaid(x: number, y: number): void { this.raidPing = { x, y, until: performance.now() + 8000 }; }

  /** Flash a purple Cursed-Treasure sighting on the minimap (the persistent marker is drawn from
   *  MinimapData.cursed as long as the hoard is out; this is the initial attention-grab). */
  pingCursed(x: number, y: number): void { this.cursedPing = { x, y, until: performance.now() + 12000 }; }

  /** Drop a teal clan-mate ping marker on the minimap for a few seconds. */
  pingClan(x: number, y: number): void {
    this.clanPings.push({ x, y, until: performance.now() + 6000 });
    if (this.clanPings.length > 8) this.clanPings.shift();
  }

  /** Drive the treasure-hoist bar (0..1). 0 hides it; >0 shows the winch filling. */
  setHoist(p: number): void {
    const bar = this.el.hoistbar as HTMLElement | undefined;
    if (!bar) return;
    if (p > 0.001 && p < 0.999) {
      bar.classList.add('on');
      (this.el.hoistfill as HTMLElement).style.width = Math.round(p * 100) + '%';
    } else {
      bar.classList.remove('on');
    }
  }

  /** Unified progress bar for hoisting treasure OR diving a wreck (2b). `caption` null hides it. */
  setActivityBar(p: number, caption: string | null, warn: string): void {
    const bar = this.el.hoistbar as HTMLElement | undefined;
    if (!bar) return;
    if (caption && p > 0.001 && p < 0.999) {
      bar.classList.add('on');
      (bar.querySelector('.cap') as HTMLElement).textContent = caption;
      (bar.querySelector('.warn') as HTMLElement).textContent = warn;
      (this.el.hoistfill as HTMLElement).style.width = Math.round(p * 100) + '%';
    } else {
      bar.classList.remove('on');
    }
  }

  toast(msg: string): void {
    const t = this.el.toast as HTMLElement;
    t.textContent = msg; t.style.opacity = '1';
    setTimeout(() => { t.style.opacity = '0'; }, 1400);
  }

  /** Renown changed (sink/death/raid) → a rising "+10 🏆 Renown" / "−6 🏆 Renown" floater so the cause of the
   *  🏆 counter's move is legible in the moment. `delta` is the ACTUAL server change (already floored at 0, so
   *  a death near zero truthfully shows the smaller loss). No-op on a zero delta. */
  renownFloat(delta: number): void {
    if (!delta) return;
    const el = this.el.renownfloat as HTMLElement;
    const gain = delta > 0;
    el.textContent = `${gain ? '+' : '−'}${Math.abs(delta)} 🏆 Renown`;
    el.classList.toggle('gain', gain);
    el.classList.toggle('loss', !gain);
    el.classList.remove('go'); void el.offsetWidth; el.classList.add('go');
  }

  /** 🎣 The catch reveal (v52): a tier-tinted card with the species art (96px), its flavor rarity and the buff
   *  it grants. Upper-centre — never over the bottom controls — and auto-fades via the CSS animation. Legendary
   *  (Leviathan's Roe) gets the gold aura + the springier, longer entrance. Species-only (guarded; the caller
   *  keeps the plain toast for a pearl/fragment/generic catch). No wire — the species id rides EV_FISH_CATCH. */
  fishReveal(species: number): void {
    const rar = fishSpeciesRarity(species);
    const m = materialById(species);
    const el = this.el.fishreveal as HTMLElement | undefined;
    if (!rar || !m || !el) return;
    const legendary = rar === 'legendary';
    const hex = '#' + (FISH_RARITY_COLOR[rar] >>> 0).toString(16).padStart(6, '0');
    const TIER: Record<FishRarity, string> = { common: 'Common Catch', uncommon: 'Uncommon Catch', rare: 'Rare Catch', legendary: 'Legendary Catch' };
    const buff = buffOf(species);
    el.style.setProperty('--fr-col', hex);
    el.style.setProperty('--fr-glow', hex);
    el.style.setProperty('--fr-dur', legendary ? '2.3s' : rar === 'common' ? '1.4s' : '1.75s');
    el.innerHTML =
      `<div class="fr-aura"></div>` +
      `<div class="fr-card">` +
        `<img class="fr-img" src="${m.icon}" alt="" decoding="async" />` +
        `<div class="fr-tier">${TIER[rar]}</div>` +
        `<div class="fr-name">${m.name}</div>` +
        (buff?.label ? `<div class="fr-sub">${buff.label} · sip at sea</div>` : '') +
      `</div>`;
    el.classList.toggle('leg', legendary);
    el.classList.remove('go'); void el.offsetWidth; el.classList.add('go');
  }

  /** 🤿 Itemized loot reveal (Onda diving-polish): a parchment manifest of what a haul added to the hold —
   *  one cell per material (56px raster icon + ×qty + name), staggered pop-ins, auto-fades (~3.4s). Generic
   *  (title/sub/items) so any future bundle-of-loot moment reuses it; wreck diving is the first caller. The
   *  card speaks the .pg-frame/.pg-paper parchment language (never dark glass). Pointer-events:none — pure
   *  presentation, the caller stays in charge of sounds/haptics. No-op on an empty item list. */
  lootReveal(title: string, sub: string, items: readonly { icon: string | null; name: string; qty: number }[]): void {
    const el = this.el.lootreveal as HTMLElement | undefined;
    if (!el || items.length === 0) return;
    const cells = items.slice(0, 6).map((it, i) =>
      `<div class="lr-item" style="animation-delay:${(0.12 + i * 0.14).toFixed(2)}s">` +
        (it.icon ? `<img class="lr-img" src="${it.icon}" alt="" decoding="async" />` : '<div class="lr-img lr-ph">📦</div>') +
        `<div class="lr-qty">×${it.qty}</div><div class="lr-name">${it.name}</div></div>`).join('');
    el.innerHTML = `<div class="pg-frame"><div class="pg-paper lr-pad">` +
      `<div class="lr-title">${title}</div><div class="lr-sub">${sub}</div><div class="lr-row">${cells}</div></div></div>`;
    el.classList.remove('go'); void el.offsetWidth; el.classList.add('go');
  }

  private bottleNoteTimer = 0;
  /** Reveal a fished-up bottle's note on a parchment card (wave 3.1). kind: 0 lore, 1 hint, 2 coin, 3 fragment. */
  bottleNote(kind: number, text: string): void {
    const el = this.el.bottlenote as HTMLElement;
    const head = kind === 2 ? 'A Purse of Coins' : kind === 3 ? 'A Torn Map Scrap' : kind === 1 ? "A Sailor's Rumor" : 'Message in a Bottle';
    (this.el.bottlenotebody as HTMLElement).textContent = text;
    (el.querySelector('.bn-hd') as HTMLElement).textContent = head;
    (el.querySelector('.bn-seal') as HTMLElement).textContent = kind === 2 ? '🪙' : kind === 3 ? '🧩' : '✉';
    el.classList.toggle('coin', kind === 2);
    el.classList.add('go');
    clearTimeout(this.bottleNoteTimer);
    this.bottleNoteTimer = window.setTimeout(() => el.classList.remove('go'), 8000);
  }

  /** YOUR kill — a big gold banner with the bounty + who you sank. */
  killBanner(victimName: string, gold: number): void {
    const el = this.el.killbanner as HTMLElement;
    el.innerHTML = `<div class="kb-t">☠ SHIP SUNK!</div><div class="kb-g">+${gold} 🪙 · ${hudEsc(victimName)}</div>`;
    el.classList.remove('go'); void el.offsetWidth; el.classList.add('go');
  }

  /** YOU pulled off a raid — a big banner with the haul, flashed as you break away with the loot. */
  plunderBanner(gold: number): void {
    const el = this.el.killbanner as HTMLElement; // reuses the killbanner slot + pop animation
    el.innerHTML = `<div class="kb-t">🏴‍☠️ PLUNDERED!</div><div class="kb-g">+${gold} 🪙</div>`;
    el.classList.remove('go'); void el.offsetWidth; el.classList.add('go');
  }

  /** Wave 36 → v37: the map border turned your ship around — an EPIC flat-earth moment (pirate tone). A
   *  dedicated banner (not the kill slot) so it can run ~6.5 s with a fade in/out, paired with a momentary
   *  screen vignette. main.ts adds a light camera shake + a heavier sound and gates it behind a cooldown so a
   *  held-into-the-edge run can't spam it. Text kept to the owner's two-line joke. */
  edgeBanner(): void {
    const vig = this.el.edgevignette as HTMLElement;
    const el = this.el.edgebanner as HTMLElement;
    el.innerHTML = `<div class="eb-t">⚓ EDGE OF THE FLAT EARTH!</div><div class="eb-g">Turns out the maps were right — coming about!</div>`;
    vig.classList.remove('go'); el.classList.remove('go'); void el.offsetWidth;
    vig.classList.add('go'); el.classList.add('go');
  }

  private annShowTimer: ReturnType<typeof setTimeout> | null = null;
  private annHideTimer: ReturnType<typeof setTimeout> | null = null;
  /** ⚓ Captain's Orders (v46): show the owner's broadcast BANNER for `durationMs`, then fade it. `level`
   *  (0 info / 1 warn / 2 alert) tints the frame + kicker. Text is set via textContent (XSS-safe). A fresh
   *  broadcast replaces any showing one. PvP-first: it always auto-dismisses, never blocks input (pointer-none). */
  announceBanner(text: string, level: number, durationMs: number): void {
    const el = this.el.announce as HTMLElement | undefined;
    if (!el) return;
    const lvl = level === 2 ? 'alert' : level === 1 ? 'warn' : 'info';
    el.classList.remove('info', 'warn', 'alert');
    el.classList.add(lvl);
    el.textContent = '';
    const hd = document.createElement('div'); hd.className = 'an-hd'; hd.textContent = "⚓ Captain's Orders";
    const t = document.createElement('div'); t.className = 'an-t'; t.textContent = text;
    el.append(hd, t);
    if (this.annShowTimer) { clearTimeout(this.annShowTimer); this.annShowTimer = null; }
    if (this.annHideTimer) { clearTimeout(this.annHideTimer); this.annHideTimer = null; }
    void el.offsetWidth; // restart the fade-in even if it was already up
    el.classList.add('show');
    const dur = Math.max(2000, Math.min(30000, durationMs || 8000));
    this.annShowTimer = setTimeout(() => {
      el.classList.remove('show');
      this.annHideTimer = setTimeout(() => { el.textContent = ''; }, 450);
    }, dur);
  }

  /** Wave 3.10: show the carried repair-plank count on the BAIL button (a carpenter's supply). */
  setRepairPlanks(n: number): void {
    const b = this.el.plankbadge as HTMLElement;
    b.classList.toggle('on', n > 0);
    if (n > 0) b.textContent = `🪚${n}`;
  }

  /** Wave 3.6: your own WANTED badge (shown when your bounty crosses the threshold). */
  setWanted(bounty: number, threshold: number): void {
    const el = this.el.wantedbadge as HTMLElement;
    const on = bounty >= threshold;
    el.classList.toggle('on', on);
    if (on) el.textContent = `☠ WANTED · ${fmtK(bounty)}`;
  }

  /** Wave 3.6: a map-wide alert that a captain has become WANTED — the whole sea should hunt them. */
  wantedBanner(name: string, bounty: number): void {
    const el = this.el.killbanner as HTMLElement;
    el.innerHTML = `<div class="kb-t" style="color:#ff9a90">☠ WANTED: ${hudEsc(name)}</div><div class="kb-g" style="color:#ffb3ab">${fmtK(bounty)} 🪙 on their head</div>`;
    el.classList.remove('go'); void el.offsetWidth; el.classList.add('go');
  }

  /** A kill line (killer ⚔ victim) in the top feed; `mine` highlights your own. Auto-expires. */
  killFeed(killerName: string, victimName: string, mine: boolean): void {
    const feed = this.el.killfeed as HTMLElement;
    const line = document.createElement('div');
    line.className = 'kf' + (mine ? ' mine' : '');
    line.innerHTML = `<span class="k">${hudEsc(killerName)}</span> ⚔ <span class="v">${hudEsc(victimName)}</span>`;
    feed.appendChild(line);
    while (feed.childElementCount > 4) feed.removeChild(feed.firstChild!);
    setTimeout(() => { line.style.opacity = '0'; setTimeout(() => line.remove(), 500); }, 5000);
  }

  private hmPool: HTMLElement[] = [];
  /** Screen hit-marker (chevron X) at a projected point — white for a normal hit, gold for a rake. */
  hitMarker(sx: number, sy: number, rake: boolean): void {
    const holder = this.el.hitmarks as HTMLElement;
    let m = this.hmPool.find((e) => !e.classList.contains('go'));
    if (!m) {
      if (this.hmPool.length >= 12) m = this.hmPool[0]!; // cap the pool; recycle the oldest
      else {
        m = document.createElement('div'); m.className = 'hitmark';
        m.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke-width="2.6" stroke-linecap="round"><path d="M6 6l4 4M18 6l-4 4M6 18l4-4M18 18l-4-4"/></svg>';
        m.addEventListener('animationend', () => m!.classList.remove('go'));
        holder.appendChild(m); this.hmPool.push(m);
      }
    }
    (m.querySelector('svg') as SVGElement).setAttribute('stroke', rake ? '#ffd24a' : '#ffffff');
    m.style.left = `${sx}px`; m.style.top = `${sy}px`;
    m.classList.remove('go'); void m.offsetWidth; m.classList.add('go');
  }

  /**
   * The ship's log — a brass speed + wind gauge (frentes 2 + 3a; owner: "medidor de velocidade bem feito,
   * estilo Sea of Thieves" + "correntes de vento com mais feedback"). One canvas, drawn every frame:
   *   • a 270° outer arc = current SPEED (0 → max), tinted by point of sail (green running … red in irons),
   *   • a thin inner arc = SAIL trim (how much canvas is out) — so "sails full + slow + red" reads as beating,
   *   • a rim PENNANT pointing where the wind blows RELATIVE TO YOUR BOW (top = wind astern/running),
   *   • a big eased KNOTS readout + the point-of-sail name; ANCHORED swaps to a clear ⚓ state.
   * Wind data comes from the snapshot header (windDir) + the predicted heading — the exact terms the sim's
   * `windFactor` uses, so the label can never disagree with the speed the ship actually gets. Client-only.
   */
  drawSpeedo(speed: number, sail: number, windDir: number, heading: number, anchored: boolean, topSpeed: number = SHIP.MAX_SPEED): void {
    const cv = this.el.speedo as HTMLCanvasElement | undefined; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const S = 104, c = S / 2, TAU = Math.PI * 2;
    ctx.setTransform(2, 0, 0, 2, 0, 0); // 208px backing store → crisp at the 100–112px CSS size
    ctx.clearRect(0, 0, S, S);
    // ease the readouts so the number glides and the pennant swings (never snaps)
    this.speedoKn += (Math.max(0, speed) - this.speedoKn) * 0.18;
    const rel = wrapPi(windDir - heading);
    if (!this.speedoWindInit) { this.speedoWind = rel; this.speedoWindInit = true; }
    else this.speedoWind = wrapPi(this.speedoWind + wrapPi(rel - this.speedoWind) * 0.16);
    const align = Math.cos(this.speedoWind);       // +1 running downwind … −1 in irons (the sim's term)
    const pos = pointOfSail(align);
    // Frente 3 — the arc fills toward THIS hull's rated top speed (class × Speed upgrade), not the raw base.
    // So a Speed buy visibly moves the redline, and a low reading in bad wind reads as "sail better", not
    // "my upgrade did nothing". `topKn` is the ceiling shown as the redline number.
    const topKn = msToKnots(topSpeed);
    const frac = anchored ? 0 : Math.min(1, this.speedoKn / Math.max(1, topSpeed));
    if (!anchored) this.explainWind(speed); // one-time (persisted) "wind is your engine" nudge, first time moving

    ctx.save(); ctx.translate(c, c);
    // dial face + brass rim
    const grd = ctx.createRadialGradient(0, -8, 5, 0, 0, c);
    grd.addColorStop(0, 'rgba(22,38,56,.96)'); grd.addColorStop(1, 'rgba(6,14,24,.98)');
    ctx.fillStyle = grd; ctx.beginPath(); ctx.arc(0, 0, c - 2, 0, TAU); ctx.fill();
    ctx.lineWidth = 2.4; ctx.strokeStyle = 'rgba(232,192,116,.85)'; ctx.beginPath(); ctx.arc(0, 0, c - 3, 0, TAU); ctx.stroke();
    ctx.lineWidth = 1; ctx.strokeStyle = 'rgba(232,192,116,.22)'; ctx.beginPath(); ctx.arc(0, 0, c - 6.5, 0, TAU); ctx.stroke();

    const A0 = Math.PI * 0.75, SWEEP = Math.PI * 1.5, R = c - 7;
    ctx.lineCap = 'round';
    // inner SAIL-trim arc (cool blue)
    const Rs = R - 6.5;
    ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(120,180,220,.16)'; ctx.beginPath(); ctx.arc(0, 0, Rs, A0, A0 + SWEEP); ctx.stroke();
    ctx.strokeStyle = 'rgba(95,190,235,.7)'; ctx.beginPath(); ctx.arc(0, 0, Rs, A0, A0 + SWEEP * Math.max(0, Math.min(1, sail))); ctx.stroke();
    // outer SPEED arc (point-of-sail colour)
    ctx.lineWidth = 4; ctx.strokeStyle = 'rgba(255,255,255,.08)'; ctx.beginPath(); ctx.arc(0, 0, R, A0, A0 + SWEEP); ctx.stroke();
    if (!anchored && frac > 0.001) {
      ctx.save(); ctx.shadowColor = pos.color; ctx.shadowBlur = 6;
      ctx.strokeStyle = pos.color; ctx.beginPath(); ctx.arc(0, 0, R, A0, A0 + SWEEP * frac); ctx.stroke(); ctx.restore();
    }
    // redline tick at the arc's end = THIS hull's rated top speed (the ceiling upgrades push outward)
    { const ea = A0 + SWEEP, cx = Math.cos(ea), cy = Math.sin(ea);
      ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(255,120,90,.9)';
      ctx.beginPath(); ctx.moveTo(cx * (R - 3.5), cy * (R - 3.5)); ctx.lineTo(cx * (R + 2.5), cy * (R + 2.5)); ctx.stroke(); }
    // bow tick (top = your heading) — anchors "the pennant is relative to YOUR bow"
    ctx.fillStyle = 'rgba(242,215,155,.9)'; ctx.beginPath(); ctx.moveTo(0, -R + 1); ctx.lineTo(-3, -R + 6.5); ctx.lineTo(3, -R + 6.5); ctx.closePath(); ctx.fill();
    // wind pennant on the rim — bearing of the wind's flow RELATIVE to the bow (top = astern/running)
    const wa = -Math.PI / 2 + this.speedoWind;
    ctx.save(); ctx.translate(Math.cos(wa) * (R - 3.5), Math.sin(wa) * (R - 3.5)); ctx.rotate(wa);
    ctx.fillStyle = anchored ? 'rgba(150,170,190,.6)' : pos.color;
    ctx.beginPath(); ctx.moveTo(5.5, 0); ctx.lineTo(-3, -4.2); ctx.lineTo(-3, 4.2); ctx.closePath(); ctx.fill(); ctx.restore();

    // centre readout
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    if (anchored) {
      ctx.fillStyle = '#ffe6a8'; ctx.font = 'bold 21px system-ui, sans-serif'; ctx.fillText('⚓', 0, -7);
      ctx.fillStyle = '#ffcf8a'; ctx.font = '800 8.5px system-ui, sans-serif'; ctx.fillText('ANCHORED', 0, 13);
    } else {
      ctx.fillStyle = '#eef6ff'; ctx.font = '800 23px system-ui, sans-serif'; ctx.fillText(formatKnots(this.speedoKn), 0, -6);
      // "kn · top NN" — the unit + this hull's rated ceiling, so the captain sees headroom (and that an
      // upgrade raised the top) instead of guessing whether the grind did anything.
      ctx.fillStyle = 'rgba(255,150,120,.7)'; ctx.font = '700 8px system-ui, sans-serif'; ctx.fillText(`kn · top ${topKn.toFixed(0)}`, 0, 10);
      ctx.fillStyle = pos.color; ctx.font = '800 8.5px system-ui, sans-serif'; ctx.fillText(pos.label, 0, 25);
    }
    ctx.restore();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  drawMinimap(d: MinimapData): void {
    this.lastSelf.x = d.selfX; this.lastSelf.y = d.selfY; // seeds the plot-FX bearing at tap time
    // ⚑ Armada markers: cache for gesture hit-testing; a pin removed/scrubbed remotely folds its open tag
    this.clanMarks = d.clanMarks ?? [];
    this.canModMarks = !!d.canModerateMarks;
    this.inClanNow = !!d.inClan;
    if (this.markInfoId != null && !this.clanMarks.some((m) => m.id === this.markInfoId)) this.closeMarkOverlays();
    // cache home + reflect it on the HOME button (shown once we know where home is; lit while sailing there)
    if (d.homeBase !== undefined) this.homeBase = d.homeBase;
    { const hb = this.el.homebtn as HTMLElement | undefined;
      if (hb) { hb.classList.toggle('shown', !!this.homeBase);
        const bound = !!(this.homeBase && this.waypoint &&
          (this.waypoint.x - this.homeBase.x) ** 2 + (this.waypoint.y - this.homeBase.y) ** 2 < 1);
        hb.classList.toggle('active', bound); } }
    this.renderChart(this.minimapCtx, 1, d);                 // the corner glance
    if (this.bigmapOpen && this.bigmapCtx) {                 // + the full-world overlay (M / 🗺): same draw, scaled up
      const bc = this.el.bigmapcanvas as HTMLCanvasElement;
      this.renderChart(this.bigmapCtx, bc.width / 132, d);
    }
  }

  /** Draw the whole nautical CHART into `ctx` at uniform scale `sc` (minimap = 1; the full-world overlay blows
   *  it up). The heavy, static half of the chart — aged parchment, sea hachure, hand-drawn island silhouettes,
   *  ports, the compass rose — is baked ONCE into an offscreen `chartBase()` (per target pixel size, keyed by a
   *  world-data signature) and blitted each frame; only the live markers (self, ships, course, treasure, base
   *  flag, wind) redraw. `setTransform(sc)` authors every live glyph in the same 132-unit layout, so the big
   *  chart is a crisp magnification of the exact same markers. Function is 100% preserved from the old chart —
   *  only the look changes. */
  private renderChart(ctx: CanvasRenderingContext2D, sc: number, d: MinimapData): void {
    const S = 132, k = S / WORLD_SIZE, TAU = Math.PI * 2;
    const w = Math.max(1, Math.round(S * sc));
    const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
    const fontReady = typeof document !== 'undefined' && !!document.fonts && (() => { try { return document.fonts.check('700 12px Cinzel'); } catch { return false; } })();
    // rebuild the baked base only when the world (islands/ports/shoals/lanes) or the font actually changes
    const sig = `${d.islands.length}:${d.outposts.length}:${d.shoals?.length ?? 0}:${d.islands[0]?.id ?? 0}:${Math.round((d.islands[0]?.x ?? 0))}:${d.tradeLanes?.length ?? 0}:${fontReady ? 1 : 0}`;
    const base = this.chartBase(w, sig, d);

    ctx.save();
    ctx.setTransform(sc, 0, 0, sc, 0, 0);
    ctx.clearRect(0, 0, S, S);
    this.roundedChartPath(ctx, S, 0); ctx.clip();
    ctx.drawImage(base, 0, 0, S, S);              // the whole aged-parchment chart, one blit
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    const now = performance.now();

    // territory control — faint sepia tint + tag per controlled region (mine mossy-green, rivals rust)
    if (d.territory && d.territory.size) {
      const g = TERRITORY.GRID, cs = S / g; ctx.textAlign = 'center';
      for (const [rid, tag] of d.territory) {
        const rx = (rid % g) * cs, ry = Math.floor(rid / g) * cs, mine = tag === d.myTag;
        ctx.fillStyle = mine ? 'rgba(70,140,90,.12)' : 'rgba(150,66,32,.10)'; ctx.fillRect(rx, ry, cs, cs);
        ctx.fillStyle = mine ? 'rgba(40,92,52,.7)' : 'rgba(122,54,26,.62)'; ctx.font = `700 ${6 / Math.sqrt(sc)}px Cinzel, Georgia, serif`;
        ctx.fillText('[' + tag + ']', rx + cs / 2, ry + 8 / Math.sqrt(sc) + 3);
      }
      ctx.textAlign = 'left';
    }

    // CASH-IN course — carrying a chest → a bold gold "SELL" ring on the nearest port + a dashed run to it.
    if (d.cashInAt) {
      const cx2 = d.cashInAt.x * k, cy2 = d.cashInAt.y * k, sx = d.selfX * k, sy = d.selfY * k, cph = (now % 1100) / 1100;
      ctx.save();
      ctx.strokeStyle = 'rgba(198,148,42,.72)'; ctx.lineWidth = 1.4; ctx.setLineDash([3, 3]);
      ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(cx2, cy2); ctx.stroke(); ctx.setLineDash([]);
      ctx.strokeStyle = `rgba(222,172,60,${(1 - cph) * 0.95})`; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(cx2, cy2, 4 + cph * 9, 0, TAU); ctx.stroke();
      ctx.strokeStyle = 'rgba(210,160,50,.95)'; ctx.lineWidth = 2.2; ctx.beginPath(); ctx.arc(cx2, cy2, 5.5, 0, TAU); ctx.stroke();
      const spx = 6.2 / Math.sqrt(sc);
      ctx.textAlign = 'center'; ctx.font = `900 ${spx}px Cinzel, Georgia, serif`;
      ctx.lineWidth = Math.max(0.6, spx * 0.18); ctx.strokeStyle = 'rgba(247,236,208,.85)'; ctx.strokeText('SELL', cx2, cy2 - 10.5);
      ctx.fillStyle = '#7a4e12'; ctx.fillText('SELL', cx2, cy2 - 10.5);
      ctx.restore(); ctx.textAlign = 'left';
    }
    // Swap the chart hint while carrying so the always-visible header speaks the loop out loud (the "1 line").
    { const mh = (this.el.mapwrap as HTMLElement | undefined)?.querySelector('.map-hint') as HTMLElement | undefined;
      if (mh) { const want = d.cashInAt ? '🪙 sell at the lit port' : 'tap to sail';
        if (mh.textContent !== want) mh.textContent = want; mh.classList.toggle('selling', !!d.cashInAt); } }

    // treasure chests — X marks the spot: the nearest actionable few as red ink X's (closest pulses), everything
    // past a useful radius collapses into faint dashed "treasure-waters" rings so the overview survives.
    if (d.chests && d.chests.length) {
      const NEAR_R = 1100, NEAR_CAP = 10, ZONE_CELL = 1300;
      const near: { x: number; y: number; d2: number }[] = [];
      const zones = new Map<number, { sx: number; sy: number; n: number }>();
      for (const c of d.chests) {
        const dx = c.x - d.selfX, dy = c.y - d.selfY, d2 = dx * dx + dy * dy;
        if (d2 <= NEAR_R * NEAR_R) near.push({ x: c.x, y: c.y, d2 });
        else { const key = Math.floor(c.x / ZONE_CELL) * 100 + Math.floor(c.y / ZONE_CELL);
          const z = zones.get(key); if (z) { z.sx += c.x; z.sy += c.y; z.n++; } else zones.set(key, { sx: c.x, sy: c.y, n: 1 }); }
      }
      ctx.save(); ctx.strokeStyle = 'rgba(150,64,42,.32)'; ctx.lineWidth = 1; ctx.setLineDash([2, 2]);
      for (const z of zones.values()) { const zx = (z.sx / z.n) * k, zy = (z.sy / z.n) * k, rr = 2.4 + Math.min(3, z.n * 0.5);
        ctx.beginPath(); ctx.arc(zx, zy, rr, 0, TAU); ctx.stroke(); }
      ctx.restore();
      near.sort((a, b) => a.d2 - b.d2);
      const ph = (now % 1400) / 1400, pulse = 0.55 + 0.45 * Math.sin(ph * Math.PI * 2);
      for (let i = 0; i < near.length && i < NEAR_CAP; i++) { const c = near[i]!, x = c.x * k, y = c.y * k;
        const sz = i === 0 ? 2.5 + pulse * 0.9 : 2.2, a = i === 0 ? 0.5 + pulse * 0.5 : 0.85;
        ctx.strokeStyle = 'rgba(28,14,4,.5)'; ctx.lineWidth = 2.4;
        ctx.beginPath(); ctx.moveTo(x - sz, y - sz); ctx.lineTo(x + sz, y + sz); ctx.moveTo(x + sz, y - sz); ctx.lineTo(x - sz, y + sz); ctx.stroke();
        ctx.strokeStyle = `rgba(190,42,36,${a})`; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(x - sz, y - sz); ctx.lineTo(x + sz, y + sz); ctx.moveTo(x + sz, y - sz); ctx.lineTo(x - sz, y + sz); ctx.stroke(); }
    }
    // drifting bottles — small green glints (a lucky find)
    if (d.bottles) { const ph = 0.5 + 0.5 * Math.sin(now / 500);
      for (const b of d.bottles) { const x = b.x * k, y = b.y * k;
        ctx.fillStyle = `rgba(72,150,92,${0.45 + 0.4 * ph})`; ctx.beginPath(); ctx.arc(x, y, 1.7, 0, TAU); ctx.fill(); } }
    // Eye of the Storm — a soft calm ring to steer into
    if (d.eye) { const ph = 0.5 + 0.5 * Math.sin(now / 700);
      ctx.save(); ctx.strokeStyle = `rgba(58,148,168,${0.42 + 0.34 * ph})`; ctx.lineWidth = 1.4; ctx.setLineDash([3, 3]);
      ctx.beginPath(); ctx.arc(d.eye.x * k, d.eye.y * k, 8, 0, TAU); ctx.stroke(); ctx.restore(); }
    // the White Whale — a pulsing spout blip while the hunt is on
    if (d.whale) { const ph = 0.5 + 0.5 * Math.sin(now / 220); const wx = d.whale.x * k, wy = d.whale.y * k;
      ctx.save(); ctx.shadowColor = 'rgba(90,140,180,.9)'; ctx.shadowBlur = 7;
      ctx.fillStyle = `rgba(56,90,132,${0.72 + 0.28 * ph})`; ctx.beginPath(); ctx.arc(wx, wy, 3.2, 0, TAU); ctx.fill();
      ctx.restore(); ctx.shadowBlur = 0; }
    // v54 Wraithlight ghost-sight — cold spectral wisps at the SERVER-given bearing+distance from self:
    // 👻 a Ghost Fleet whisper (kind 1) or a rich wreck's glow (kind 0). Only present while the draught burns.
    if (d.spectral) { const ph = 0.5 + 0.5 * Math.sin(now / 420);
      for (const sp of d.spectral) {
        const x = (d.selfX + Math.cos(sp.bearing) * sp.dist) * k, y = (d.selfY + Math.sin(sp.bearing) * sp.dist) * k;
        ctx.save(); ctx.shadowColor = 'rgba(140,235,200,.9)'; ctx.shadowBlur = 6;
        if (sp.kind === 0) { // rich wreck: a small glowing diamond
          ctx.fillStyle = `rgba(120,225,185,${0.55 + 0.35 * ph})`;
          ctx.beginPath(); ctx.moveTo(x, y - 3.4); ctx.lineTo(x + 3.4, y); ctx.lineTo(x, y + 3.4); ctx.lineTo(x - 3.4, y); ctx.closePath(); ctx.fill();
        } else {             // ghost ship: a pale flickering wisp ring
          ctx.strokeStyle = `rgba(160,240,210,${0.35 + 0.45 * ph})`; ctx.lineWidth = 1.3; ctx.setLineDash([2.5, 2.5]);
          ctx.beginPath(); ctx.arc(x, y, 3.6, 0, TAU); ctx.stroke();
        }
        ctx.restore(); ctx.shadowBlur = 0;
      } }
    // active treasure-map dig — a bold red X to steer to
    if (d.digX) { const x = d.digX.x * k, y = d.digX.y * k; ctx.save();
      ctx.strokeStyle = 'rgba(28,14,4,.6)'; ctx.lineWidth = 3.4;
      ctx.beginPath(); ctx.moveTo(x - 3.4, y - 3.4); ctx.lineTo(x + 3.4, y + 3.4); ctx.moveTo(x + 3.4, y - 3.4); ctx.lineTo(x - 3.4, y + 3.4); ctx.stroke();
      ctx.strokeStyle = 'rgba(200,42,36,.98)'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(x - 3.4, y - 3.4); ctx.lineTo(x + 3.4, y + 3.4); ctx.moveTo(x + 3.4, y - 3.4); ctx.lineTo(x - 3.4, y + 3.4); ctx.stroke(); ctx.restore(); }
    // a wider "sight" ring around you (client-only cue): spyglass/wraithlight oil × the Lookout's Spyglass charm
    const sightMul = d.sightMul ?? 1;
    if (sightMul > 1) { const sr = AOI_RADIUS * sightMul * k;
      ctx.save(); ctx.strokeStyle = 'rgba(58,148,168,.34)'; ctx.lineWidth = 1; ctx.setLineDash([2, 3]);
      ctx.beginPath(); ctx.arc(d.selfX * k, d.selfY * k, sr, 0, TAU); ctx.stroke(); ctx.restore(); }

    // other ships — allies mossy-green, LADEN enemies gold+pulse, WANTED red+pulse, everyone else enemy-red
    const blip = sightMul > 1 ? 2.7 : 2.1;
    for (const s of d.ships) { const x = s.x * k, y = s.y * k;
      const ally = d.allyIds?.has(s.id), laden = (s.flags & ShipFlag.LADEN) !== 0, wanted = (s.flags & ShipFlag.WANTED) !== 0;
      ctx.fillStyle = 'rgba(28,14,4,.55)'; ctx.beginPath(); ctx.arc(x, y, blip + 1.1, 0, TAU); ctx.fill();
      if (wanted && !ally) { const ph = (now % 700) / 700; ctx.strokeStyle = `rgba(200,42,36,${(1 - ph) * 0.95})`; ctx.lineWidth = 1.8; ctx.beginPath(); ctx.arc(x, y, 2.5 + ph * 6, 0, TAU); ctx.stroke(); }
      else if (laden && !ally) { const ph = (now % 900) / 900; ctx.strokeStyle = `rgba(212,162,52,${(1 - ph) * 0.9})`; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(x, y, 2.5 + ph * 5, 0, TAU); ctx.stroke(); }
      ctx.fillStyle = ally ? 'rgba(52,150,92,.98)' : wanted ? 'rgba(200,42,36,1)' : laden ? 'rgba(212,162,52,.98)' : 'rgba(190,52,42,.95)';
      ctx.beginPath(); ctx.arc(x, y, wanted ? blip + 0.6 : blip, 0, TAU); ctx.fill(); }

    // tap-to-sail VOYAGE — the old-chart "dotted voyage line" marching toward a rubber-stamped red
    // treasure-X (circled in hand-drawn ink). While plotFx is live the X lands with a stamp overshoot.
    if (this.waypoint) {
      const wx = this.waypoint.x * k, wy = this.waypoint.y * k, sx = d.selfX * k, sy = d.selfY * k;
      const fx = this.plotFx;
      const ft = fx ? now - fx.t0 : Infinity;          // ms since the stamp hit the paper
      ctx.save();
      // dotted ink course — threads the quartermaster's live waypoints (it rounds islands); the X sits on
      // the LAST one, the water-projected mark (tap an island → the X lands on the safe standoff)
      const pts: { x: number; y: number }[] = [{ x: sx, y: sy }];
      const route = this.getRoute?.();
      if (route && route.length) for (const p of route) pts.push({ x: p.x * k, y: p.y * k });
      else pts.push({ x: wx, y: wy });
      const end = pts[pts.length - 1]!;
      const lineA = Math.min(1, ft / 260);             // the ink dries in over the first beat
      ctx.strokeStyle = `rgba(96,42,26,${0.82 * lineA})`; ctx.lineWidth = 1.7;
      ctx.setLineDash([0.1, 4.2]); ctx.lineDashOffset = -((now / 85) % 4.3); // dots march toward the mark
      ctx.beginPath(); ctx.moveTo(pts[0]!.x, pts[0]!.y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i]!.x, pts[i]!.y);
      ctx.stroke(); ctx.setLineDash([]);
      // the destination: X marks the spot — stamped (scale overshoot + settle) while the FX runs
      let stampS = 1, stampA = 1, stampR = 0;
      if (fx) {
        const p = Math.min(1, ft / 230);
        stampS = 1 + (1 - p) * (1 - p) * 1.9;          // falls onto the paper
        stampA = 0.12 + 0.88 * p;
        stampR = (1 - p) * -0.38;                      // un-twists as it lands
        if (ft > 230 && ft < 460) stampS = 1 - 0.1 * Math.sin(((ft - 230) / 230) * Math.PI); // settle squash
      }
      ctx.save(); ctx.translate(end.x, end.y); ctx.rotate(stampR); ctx.scale(stampS, stampS); ctx.globalAlpha = stampA;
      ctx.strokeStyle = 'rgba(120,32,24,.85)'; ctx.lineWidth = 1.05;   // hand-drawn ink circle (two loose arcs)
      ctx.beginPath(); ctx.arc(0, 0, 5.6, 0.35, 5.9); ctx.stroke();
      ctx.beginPath(); ctx.arc(0.4, -0.3, 5.0, 3.5, 8.55); ctx.stroke();
      const xs = 3.1;                                                   // the red treasure X, dark under-ink first
      ctx.lineCap = 'round';
      ctx.strokeStyle = 'rgba(28,14,4,.6)'; ctx.lineWidth = 3.3;
      ctx.beginPath(); ctx.moveTo(-xs, -xs); ctx.lineTo(xs, xs); ctx.moveTo(xs, -xs); ctx.lineTo(-xs, xs); ctx.stroke();
      ctx.strokeStyle = 'rgba(190,42,36,.96)'; ctx.lineWidth = 1.9;
      ctx.beginPath(); ctx.moveTo(-xs, -xs); ctx.lineTo(xs, xs); ctx.moveTo(xs, -xs); ctx.lineTo(-xs, xs); ctx.stroke();
      ctx.restore();
      // quiet red-ink heartbeat so the live mark stays findable at a glance
      const wp = (now % 1200) / 1200;
      ctx.strokeStyle = `rgba(150,50,36,${(1 - wp) * 0.5})`; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.arc(end.x, end.y, 4.5 + wp * 6.5, 0, TAU); ctx.stroke();
      ctx.restore();
    }
    // the plot-stamp FX dressing (ink ripple + splatter + a compass-needle swing onto the new bearing)
    if (this.plotFx) {
      const ft = now - this.plotFx.t0;
      if (ft > 1150) this.plotFx = null;
      else {
        const px = this.plotFx.x * k, py = this.plotFx.y * k;
        ctx.save();
        // ink ripple: two rings soaking outward into the paper
        for (const [d0, w0] of [[0, 1.7], [150, 1.1]] as [number, number][]) {
          const rt = (ft - d0) / 760;
          if (rt > 0 && rt < 1) {
            const e = 1 - (1 - rt) * (1 - rt);
            ctx.strokeStyle = `rgba(74,40,18,${(1 - rt) * 0.5})`; ctx.lineWidth = w0 * (1 - rt * 0.5);
            ctx.beginPath(); ctx.arc(px, py, 2.5 + e * 13, 0, TAU); ctx.stroke();
          }
        }
        // ink splatter: six deterministic specks kicked out as the stamp lands
        if (ft > 200) {
          const st = Math.min(1, (ft - 200) / 640);
          let sd = ((Math.round(px * 7) * 2654435761 + Math.round(py * 7) * 40503) >>> 0) || 1;
          const rnd = (): number => { sd = (sd * 1664525 + 1013904223) >>> 0; return sd / 4294967296; };
          ctx.fillStyle = `rgba(120,30,22,${(1 - st) * 0.75})`;
          for (let i = 0; i < 6; i++) {
            const a = rnd() * TAU, rr = (3.6 + rnd() * 4.4) * (0.5 + st * 0.5), r0 = 0.45 + rnd() * 0.5;
            ctx.beginPath(); ctx.arc(px + Math.cos(a) * rr, py + Math.sin(a) * rr, r0 * (1 - st * 0.55), 0, TAU); ctx.fill();
          }
        }
        // compass ping: a gold needle on the rose swings onto the plotted bearing, flashes, fades
        { const rg = this.roseGeom(S, sc > 2);
          const sw = Math.min(1, ft / 680), e = 1 - Math.pow(1 - sw, 3);
          const ang = this.plotFx.bearing + (1 - e) * 2.6;
          const alpha = ft < 680 ? 0.95 : Math.max(0, 0.95 * (1 - (ft - 680) / 420));
          if (alpha > 0.01) {
            ctx.save(); ctx.translate(rg.cx, rg.cy); ctx.globalAlpha = alpha;
            ctx.strokeStyle = 'rgba(224,176,74,.6)'; ctx.lineWidth = 1.1;   // settling flash ring
            ctx.beginPath(); ctx.arc(0, 0, rg.R * (0.55 + 0.45 * e), 0, TAU); ctx.stroke();
            ctx.rotate(ang);
            ctx.fillStyle = '#e0b04a'; ctx.strokeStyle = 'rgba(80,52,14,.9)'; ctx.lineWidth = 0.6;
            ctx.beginPath(); ctx.moveTo(rg.R * 0.86, 0); ctx.lineTo(rg.R * 0.16, -rg.R * 0.15); ctx.lineTo(rg.R * 0.16, rg.R * 0.15); ctx.closePath(); ctx.fill(); ctx.stroke();
            ctx.fillStyle = 'rgba(46,31,12,.9)';                              // dark counterweight tail
            ctx.beginPath(); ctx.moveTo(-rg.R * 0.5, 0); ctx.lineTo(rg.R * 0.16, -rg.R * 0.1); ctx.lineTo(rg.R * 0.16, rg.R * 0.1); ctx.closePath(); ctx.fill();
            ctx.restore(); ctx.globalAlpha = 1;
          }
        }
        ctx.restore();
      }
    }

    // YOUR BASE — a planted GOLDEN PENNANT with a soft glow, so a lost captain always finds (and, via HOME,
    // sails to) their harbour. Chart is whole-world so it's on-screen; a defensive inset-clamp + arrow covers a
    // base authored on the world border.
    if (this.homeBase) {
      // Clamp well clear of the ornate frame (M) so a border-authored base still shows its whole flag, never a
      // pennant jammed under the wood band. An arrow points to the true spot when the marker had to be pulled in.
      const M = 16, rawx = this.homeBase.x * k, rawy = this.homeBase.y * k;
      const hx = clamp(rawx, M, S - M), hy = clamp(rawy, M, S - M), clamped = hx !== rawx || hy !== rawy;
      const hp = 0.5 + 0.5 * Math.sin(now / 900);
      ctx.save();
      ctx.fillStyle = `rgba(230,182,72,${0.12 + 0.10 * hp})`; ctx.beginPath(); ctx.arc(hx, hy, 8 + hp * 1.3, 0, TAU); ctx.fill();
      ctx.strokeStyle = `rgba(210,160,50,${0.5 + 0.3 * hp})`; ctx.lineWidth = 1.2; ctx.beginPath(); ctx.arc(hx, hy, 7.5, 0, TAU); ctx.stroke();
      ctx.translate(hx, hy);
      ctx.strokeStyle = 'rgba(40,26,10,.95)'; ctx.lineWidth = 1.4; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(0, 5.5); ctx.lineTo(0, -8.5); ctx.stroke();                 // flagpole
      const wv = Math.sin(now / 300) * 1.1;                                                    // waving pennant
      ctx.beginPath(); ctx.moveTo(0, -8.5); ctx.lineTo(7 + wv, -6); ctx.lineTo(0, -3.2); ctx.closePath();
      const fg = ctx.createLinearGradient(0, -8, 7, -4); fg.addColorStop(0, '#f4dca0'); fg.addColorStop(1, '#d9a63e');
      ctx.fillStyle = fg; ctx.fill(); ctx.lineWidth = 0.8; ctx.strokeStyle = 'rgba(92,60,16,.9)'; ctx.stroke();
      ctx.fillStyle = 'rgba(40,26,10,.9)'; ctx.beginPath(); ctx.arc(0, 5.5, 1.6, 0, TAU); ctx.fill();
      if (clamped) { ctx.rotate(Math.atan2(rawy - hy, rawx - hx)); ctx.fillStyle = '#e6b54a';
        ctx.beginPath(); ctx.moveTo(12, 0); ctx.lineTo(6, -3.5); ctx.lineTo(6, 3.5); ctx.closePath(); ctx.fill(); }
      ctx.restore();
      if (sc > 1) {                                                                          // spell it out on the big chart
        const fpx = 6.6 / Math.sqrt(sc);                                                      // sublinear: ~15px on the big chart
        ctx.save(); ctx.textAlign = 'center'; ctx.font = `900 ${fpx}px Cinzel, Georgia, serif`;
        const tw = ctx.measureText('YOUR BASE').width, mg = 10.5;
        const lx = clamp(hx, mg + tw / 2, S - mg - tw / 2), ly = hy < S / 2 ? hy + 11 : hy - 10;
        ctx.lineWidth = Math.max(0.6, fpx * 0.16); ctx.strokeStyle = 'rgba(247,236,208,.75)'; ctx.strokeText('YOUR BASE', lx, ly);
        ctx.fillStyle = '#7a4e12'; ctx.fillText('YOUR BASE', lx, ly); ctx.restore();
      }
    }

    // SELF — the hero of the chart: a gold ship-arrow with a dark hull and a soft glow, always on top.
    ctx.save(); ctx.translate(d.selfX * k, d.selfY * k);
    const sp = 0.5 + 0.5 * Math.sin(now / 600);
    ctx.fillStyle = `rgba(60,150,170,${0.12 + 0.10 * sp})`; ctx.beginPath(); ctx.arc(0, 0, 7 + sp * 1.6, 0, TAU); ctx.fill();
    ctx.strokeStyle = `rgba(44,120,140,${0.5 + 0.3 * sp})`; ctx.lineWidth = 1.3; ctx.beginPath(); ctx.arc(0, 0, 7, 0, TAU); ctx.stroke();
    ctx.rotate(d.selfHeading);
    ctx.fillStyle = 'rgba(30,20,8,.9)'; ctx.beginPath(); ctx.arc(0, 0, 5, 0, TAU); ctx.fill();
    ctx.beginPath(); ctx.moveTo(8, 0); ctx.lineTo(-4, -4.6); ctx.lineTo(-1.4, 0); ctx.lineTo(-4, 4.6); ctx.closePath();
    const ag = ctx.createLinearGradient(-4, 0, 8, 0); ag.addColorStop(0, '#f4dca0'); ag.addColorStop(1, '#e8b74a');
    ctx.fillStyle = ag; ctx.fill(); ctx.lineWidth = 1; ctx.strokeStyle = 'rgba(60,38,10,.95)'; ctx.stroke();
    ctx.restore();

    // live WIND — a gold pennant needle riding the compass rose, pointing downwind
    if (d.windDir !== undefined) { const rg = this.roseGeom(S, sc > 2);
      ctx.save(); ctx.translate(rg.cx, rg.cy); ctx.rotate(d.windDir);
      ctx.fillStyle = 'rgba(210,160,50,.92)'; ctx.strokeStyle = 'rgba(92,60,16,.8)'; ctx.lineWidth = 0.6;
      ctx.beginPath(); ctx.moveTo(rg.R * 0.92, 0); ctx.lineTo(rg.R * 0.55, -2); ctx.lineTo(rg.R * 0.55, 2); ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.restore();
    }

    // "base under raid" ping — a pulsing red ring at the base for a few seconds
    if (this.raidPing) {
      const nowp = performance.now();
      if (nowp < this.raidPing.until) {
        const px = this.raidPing.x * k, py = this.raidPing.y * k, ph = (nowp % 900) / 900;
        ctx.strokeStyle = `rgba(210,60,44,${(1 - ph) * 0.9})`; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(px, py, 3 + ph * 9, 0, TAU); ctx.stroke();
        ctx.fillStyle = 'rgba(210,60,44,.95)'; ctx.beginPath(); ctx.arc(px, py, 2.5, 0, TAU); ctx.fill();
      } else this.raidPing = null;
    }
    // Cursed Treasure — a persistent pulsing purple diamond while the hoard is out
    if (d.cursed) {
      const px = d.cursed.x * k, py = d.cursed.y * k, ph = 0.5 + 0.5 * Math.sin(now / 260);
      ctx.save(); ctx.translate(px, py); ctx.rotate(Math.PI / 4);
      ctx.fillStyle = `rgba(150,66,200,${0.6 + 0.4 * ph})`; ctx.shadowColor = 'rgba(150,66,200,.9)'; ctx.shadowBlur = 6;
      ctx.fillRect(-3, -3, 6, 6); ctx.restore(); ctx.shadowBlur = 0;
    }
    if (this.cursedPing) {
      const nowp = performance.now();
      if (nowp < this.cursedPing.until) {
        const px = this.cursedPing.x * k, py = this.cursedPing.y * k, ph = (nowp % 1100) / 1100;
        ctx.strokeStyle = `rgba(160,78,210,${(1 - ph) * 0.9})`; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(px, py, 3 + ph * 12, 0, TAU); ctx.stroke();
      } else this.cursedPing = null;
    }
    // ⚑ ARMADA CHART MARKERS (v54) — the clan's persistent intel pins, inked in the Armada's teal like the
    // rally/ping family. Corner glance: a quiet wax dot (discreet — the overview must survive). Big chart:
    // a full hand-stamped seal (loose double ink ring + glyph) big enough to tap; the pin whose paper tag
    // is open gets a slow heartbeat ring so tag↔pin read as one thing.
    if (d.clanMarks) {
      for (const mk of d.clanMarks) {
        const px = mk.x * k, py = mk.y * k;
        if (sc <= 1.5) { // minimap: a small teal wax-seal dot with a dark under-ink rim
          ctx.fillStyle = 'rgba(20,12,4,.55)'; ctx.beginPath(); ctx.arc(px, py, 2.4, 0, TAU); ctx.fill();
          ctx.fillStyle = 'rgba(46,150,112,.95)'; ctx.beginPath(); ctx.arc(px, py, 1.8, 0, TAU); ctx.fill();
          ctx.fillStyle = 'rgba(240,230,196,.8)'; ctx.beginPath(); ctx.arc(px - 0.5, py - 0.6, 0.55, 0, TAU); ctx.fill();
          continue;
        }
        ctx.save(); ctx.translate(px, py);
        // wax pool + hand-drawn double ring (two loose arcs — same ink habit as the voyage X's circle)
        ctx.fillStyle = 'rgba(52,160,120,.16)'; ctx.beginPath(); ctx.arc(0, 0, 5.3, 0, TAU); ctx.fill();
        ctx.strokeStyle = 'rgba(18,72,52,.9)'; ctx.lineWidth = 1.0;
        ctx.beginPath(); ctx.arc(0, 0, 5.3, 0.45, 6.0); ctx.stroke();
        ctx.beginPath(); ctx.arc(0.35, -0.25, 4.7, 3.3, 8.6); ctx.stroke();
        this.drawMarkGlyph(ctx, mk.icon, 3.1);
        ctx.fillStyle = 'rgba(244,236,206,.5)'; ctx.beginPath(); ctx.arc(-1.7, -1.9, 0.7, 0, TAU); ctx.fill(); // wax sheen
        if (this.markInfoId === mk.id) { const ph = (now % 1200) / 1200;
          ctx.strokeStyle = `rgba(46,150,112,${(1 - ph) * 0.8})`; ctx.lineWidth = 1.2;
          ctx.beginPath(); ctx.arc(0, 0, 6 + ph * 5.5, 0, TAU); ctx.stroke(); }
        ctx.restore();
      }
    }
    // pending placement (glyph picker open): a pulsing ink spot where the stamp will land
    if (sc > 1.5 && this.markSel) {
      const px = this.markSel.x * k, py = this.markSel.y * k, ph = (now % 900) / 900;
      ctx.strokeStyle = `rgba(30,102,74,${(1 - ph) * 0.85})`; ctx.lineWidth = 1.3;
      ctx.beginPath(); ctx.arc(px, py, 2.5 + ph * 7, 0, TAU); ctx.stroke();
      ctx.fillStyle = 'rgba(30,102,74,.85)'; ctx.beginPath(); ctx.arc(px, py, 1.6, 0, TAU); ctx.fill();
    }
    // clan rally flag
    if (d.rally) { const rx = d.rally.x * k, ry = d.rally.y * k;
      ctx.strokeStyle = 'rgba(52,160,120,.85)'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(rx, ry + 3); ctx.lineTo(rx, ry - 6); ctx.stroke();
      ctx.fillStyle = 'rgba(52,160,120,.95)'; ctx.beginPath(); ctx.moveTo(rx, ry - 6); ctx.lineTo(rx + 5, ry - 4); ctx.lineTo(rx, ry - 2); ctx.closePath(); ctx.fill(); }
    // clan pings (teal, fading)
    const nowc = performance.now();
    for (const pg of this.clanPings) { if (nowc >= pg.until) continue; const px = pg.x * k, py = pg.y * k, ph = (nowc % 800) / 800;
      ctx.strokeStyle = `rgba(60,170,140,${(1 - ph) * 0.9})`; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(px, py, 2 + ph * 7, 0, TAU); ctx.stroke(); }
    if (this.clanPings.length && this.clanPings[this.clanPings.length - 1]!.until < nowc) this.clanPings = this.clanPings.filter((p) => nowc < p.until);

    // ornate rope-and-brass FRAME last, so it caps every marker at the edge
    this.drawChartFrame(ctx, S);
    ctx.restore();
  }

  /** Trace a rounded-rect path inset from the S×S chart square (nested radius shrinks with the inset so the
   *  wood band, rope and clip corners all nest cleanly). Hand-rolled arcTo (no roundRect dependency). */
  private roundedChartPath(ctx: CanvasRenderingContext2D, S: number, inset: number): void {
    const r = Math.max(2, 8 - inset), a = inset, b = S - inset;
    ctx.beginPath();
    ctx.moveTo(a + r, a); ctx.lineTo(b - r, a); ctx.arcTo(b, a, b, a + r, r);
    ctx.lineTo(b, b - r); ctx.arcTo(b, b, b - r, b, r);
    ctx.lineTo(a + r, b); ctx.arcTo(a, b, a, b - r, r);
    ctx.lineTo(a, a + r); ctx.arcTo(a, a, a + r, a, r); ctx.closePath();
  }

  /** Stroke one Armada-mark glyph centred on the current origin (the pin seal), `r` = glyph half-extent in
   *  chart units. The canvas twins of MARK_GLYPHS' SVGs — skull / anchor / chest / fish / eye / cross —
   *  drawn as dark-teal ink strokes so the stamp reads hand-pressed, never emoji. */
  private drawMarkGlyph(ctx: CanvasRenderingContext2D, icon: number, r: number): void {
    const TAU = Math.PI * 2;
    ctx.save();
    ctx.strokeStyle = 'rgba(16,66,48,.95)'; ctx.fillStyle = 'rgba(16,66,48,.95)';
    ctx.lineWidth = Math.max(0.5, r * 0.18); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    switch (icon) {
      case 0: // ☠ DANGER — skull over crossbones
        ctx.beginPath(); ctx.arc(0, -r * 0.25, r * 0.6, 0, TAU); ctx.stroke();
        ctx.beginPath(); ctx.arc(-r * 0.26, -r * 0.32, r * 0.13, 0, TAU); ctx.fill();
        ctx.beginPath(); ctx.arc(r * 0.26, -r * 0.32, r * 0.13, 0, TAU); ctx.fill();
        ctx.beginPath(); ctx.moveTo(-r * 0.72, r * 0.42); ctx.lineTo(r * 0.72, r * 0.95);
        ctx.moveTo(r * 0.72, r * 0.42); ctx.lineTo(-r * 0.72, r * 0.95); ctx.stroke();
        break;
      case 1: // ⚓ RALLY — fouled anchor
        ctx.beginPath(); ctx.arc(0, -r * 0.72, r * 0.2, 0, TAU); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(0, -r * 0.52); ctx.lineTo(0, r * 0.72);
        ctx.moveTo(-r * 0.42, -r * 0.24); ctx.lineTo(r * 0.42, -r * 0.24); ctx.stroke();
        ctx.beginPath(); ctx.arc(0, r * 0.12, r * 0.62, Math.PI * 0.12, Math.PI * 0.88); ctx.stroke();
        break;
      case 2: // 💰 LOOT — sea chest
        ctx.strokeRect(-r * 0.68, -r * 0.08, r * 1.36, r * 0.82);
        ctx.beginPath(); ctx.arc(0, -r * 0.08, r * 0.68, Math.PI, TAU); ctx.stroke();
        ctx.beginPath(); ctx.arc(0, r * 0.3, r * 0.14, 0, TAU); ctx.fill();
        break;
      case 3: // 🐟 FISHING — fish
        ctx.beginPath(); ctx.moveTo(-r * 0.8, 0);
        ctx.quadraticCurveTo(-r * 0.1, -r * 0.62, r * 0.42, 0);
        ctx.quadraticCurveTo(-r * 0.1, r * 0.62, -r * 0.8, 0); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(r * 0.42, 0); ctx.lineTo(r * 0.82, -r * 0.38);
        ctx.moveTo(r * 0.42, 0); ctx.lineTo(r * 0.82, r * 0.38); ctx.stroke();
        ctx.beginPath(); ctx.arc(-r * 0.45, -r * 0.1, r * 0.1, 0, TAU); ctx.fill();
        break;
      case 4: // 👁 SIGHTED — spyglass eye
        ctx.beginPath(); ctx.moveTo(-r * 0.82, 0);
        ctx.quadraticCurveTo(0, -r * 0.78, r * 0.82, 0);
        ctx.quadraticCurveTo(0, r * 0.78, -r * 0.82, 0); ctx.stroke();
        ctx.beginPath(); ctx.arc(0, 0, r * 0.28, 0, TAU); ctx.stroke();
        ctx.beginPath(); ctx.arc(0, 0, r * 0.1, 0, TAU); ctx.fill();
        break;
      default: // ✕ MARK — a plain cross
        ctx.lineWidth = Math.max(0.6, r * 0.24);
        ctx.beginPath(); ctx.moveTo(-r * 0.55, -r * 0.55); ctx.lineTo(r * 0.55, r * 0.55);
        ctx.moveTo(r * 0.55, -r * 0.55); ctx.lineTo(-r * 0.55, r * 0.55); ctx.stroke();
        break;
    }
    ctx.restore();
  }

  /** Compass-rose geometry (position + radius) in the 132-unit layout, sat in the bottom-right corner. Shared
   *  by the baked rose and the live wind needle so they line up exactly. `ornate` = the blown-up chart. */
  private roseGeom(S: number, ornate: boolean): { cx: number; cy: number; R: number } {
    const R = ornate ? 17 : 11, m = R * 1.45 + 6;
    return { cx: S - m, cy: S - m, R };
  }

  /** The aged-parchment TEXTURE for the chart. Delegates to the SHARED generator in parchment.ts (moved there
   *  verbatim) so the DOM menus (shipyard cards, Captain's Opportunities) paint on the exact same paper — one
   *  generator, never a divergent copy. Cached locally so the chart blit costs a lookup, not a regen. */
  private parchmentTex(): HTMLCanvasElement {
    return (this._parch ??= parchmentTexture());
  }

  /** The baked STATIC chart layer (parchment + sea + islands + ports + rose) at target pixel size `w`, cached
   *  per size and rebuilt only when the world signature changes. Reuses its canvas object across rebuilds. */
  private chartBase(w: number, sig: string, d: MinimapData): HTMLCanvasElement {
    const cached = this._chartBase.get(w);
    if (cached && cached.sig === sig) return cached.canvas;
    const canvas = cached?.canvas ?? document.createElement('canvas');
    canvas.width = canvas.height = w;
    this.drawChartStatic(canvas.getContext('2d')!, w, d);
    this._chartBase.set(w, { canvas, sig });
    return canvas;
  }

  /** Paint the whole static chart into offscreen `p` at pixel size `w`: aged sea over parchment, faint lat/long
   *  + trade-wind rhumbs, shoals, hand-drawn island silhouettes (ink coastline + sand shading), ports as ink
   *  anchors / the Capital as a gold medallion, and the compass rose. `u` = base-units→px; `k` = world→px. */
  private drawChartStatic(p: CanvasRenderingContext2D, w: number, d: MinimapData): void {
    const S = 132, u = w / S, k = w / WORLD_SIZE, TAU = Math.PI * 2;
    const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
    p.clearRect(0, 0, w, w);
    p.drawImage(this.parchmentTex(), 0, 0, w, w);
    // aged sea: a cool wash + gentle wavy sea-lines over the paper
    p.fillStyle = 'rgba(70,104,120,.10)'; p.fillRect(0, 0, w, w);
    p.strokeStyle = 'rgba(62,96,112,.10)'; p.lineWidth = Math.max(0.5, 0.55 * u);
    for (let yy = 6 * u; yy < w; yy += 8 * u) { p.beginPath();
      for (let xx = 0; xx <= w; xx += 5 * u) { const oy = yy + Math.sin(xx * 0.05 / u) * 1.1 * u; if (xx === 0) p.moveTo(xx, oy); else p.lineTo(xx, oy); }
      p.stroke(); }
    // lat/long grid — faint sepia
    p.strokeStyle = 'rgba(74,52,24,.12)'; p.lineWidth = Math.max(0.5, 0.55 * u);
    for (let i = 1; i < 6; i++) { const q = (i / 6) * w; p.beginPath(); p.moveTo(q, 0); p.lineTo(q, w); p.moveTo(0, q); p.lineTo(w, q); p.stroke(); }
    // trade-wind rhumbs — dashed sepia currents linking the ports
    if (d.tradeLanes && d.tradeLanes.length) { p.save(); p.strokeStyle = 'rgba(74,52,24,.28)'; p.lineWidth = Math.max(0.5, 0.8 * u); p.setLineDash([3 * u, 3 * u]);
      for (const l of d.tradeLanes) { p.beginPath(); p.moveTo(l.ax * k, l.ay * k); p.lineTo(l.bx * k, l.by * k); p.stroke(); } p.restore(); }
    // fishing shoals — quiet muted-teal ripples so a spot is discoverable (ambient, not a ping)
    if (d.shoals) { p.strokeStyle = 'rgba(58,112,122,.34)'; p.lineWidth = Math.max(0.5, 0.65 * u);
      for (const sh of d.shoals) { const x = sh.x * k, y = sh.y * k;
        p.beginPath(); p.arc(x, y, 1.9 * u, 0.6, 2.5); p.stroke(); p.beginPath(); p.arc(x, y, 1.1 * u, 3.6, 5.6); p.stroke(); } }
    // ISLANDS — hand-drawn landmasses: paper shadow, cream surf, shaded sand body, inked coastline, hill mark
    p.lineJoin = 'round';
    for (let idx = 0; idx < d.islands.length; idx++) { const isl = d.islands[idx]!;
      const cx = isl.x * k, cy = isl.y * k, seed = (isl.id ?? idx) + 1;
      const r = clamp(2.4 * u + (isl.radius ? isl.radius * k * 0.8 : 1.2 * u), 2.4 * u, 7.5 * u);
      p.save(); p.translate(0.6 * u, 0.9 * u); this.islandPath(p, cx, cy, r, seed); p.fillStyle = 'rgba(58,40,16,.14)'; p.fill(); p.restore(); // shadow
      this.islandPath(p, cx, cy, r * 1.14, seed); p.fillStyle = 'rgba(233,222,186,.5)'; p.fill();                                   // surf halo
      const gsand = p.createRadialGradient(cx - r * 0.3, cy - r * 0.35, r * 0.15, cx, cy, r * 1.1);
      gsand.addColorStop(0, '#e2cd8f'); gsand.addColorStop(0.62, '#c9a763'); gsand.addColorStop(1, '#a9853f');
      this.islandPath(p, cx, cy, r, seed); p.fillStyle = gsand; p.fill();                                                            // sand body
      p.lineWidth = Math.max(0.6, 1.0 * u); p.strokeStyle = 'rgba(58,40,16,.85)'; this.islandPath(p, cx, cy, r, seed); p.stroke();   // coastline
      if (r > 3.6 * u) {                                                                                                             // interior hills on bigger isles
        p.strokeStyle = 'rgba(74,52,24,.32)'; p.lineWidth = Math.max(0.5, 0.6 * u); this.islandPath(p, cx, cy, r * 0.58, seed); p.stroke();
        p.strokeStyle = 'rgba(58,40,16,.55)'; p.lineWidth = Math.max(0.5, 0.7 * u); p.beginPath();
        p.moveTo(cx - r * 0.3, cy + r * 0.08); p.lineTo(cx - r * 0.06, cy - r * 0.32); p.lineTo(cx + r * 0.08, cy - r * 0.04); p.lineTo(cx + r * 0.32, cy - r * 0.36); p.stroke();
      }
    }
    // PORTS — the Capital as a gold star medallion, others as ink anchors on a thin ring; names in Cinzel
    p.textAlign = 'center'; p.textBaseline = 'middle';
    for (const op of d.outposts) { const ox = op.x * k, oy = op.y * k, cap = !!op.capital, rr = Math.max(3 * u, op.radius * k);
      if (cap) {
        const gg = p.createRadialGradient(ox, oy, 0, ox, oy, rr * 1.35); gg.addColorStop(0, 'rgba(240,208,128,.5)'); gg.addColorStop(1, 'rgba(240,208,128,0)');
        p.fillStyle = gg; p.beginPath(); p.arc(ox, oy, rr * 1.35, 0, TAU); p.fill();
        p.strokeStyle = 'rgba(150,110,40,.9)'; p.lineWidth = Math.max(1, 1.6 * u); p.beginPath(); p.arc(ox, oy, rr, 0, TAU); p.stroke();
        this.drawStar(p, ox, oy, 8, rr * 0.66, rr * 0.28, '#e6b54a', 'rgba(90,60,16,.9)', u);
      } else {
        p.strokeStyle = 'rgba(58,40,16,.7)'; p.lineWidth = Math.max(0.8, 1.2 * u); p.beginPath(); p.arc(ox, oy, rr, 0, TAU); p.stroke();
        this.drawAnchor(p, ox, oy, rr * 0.95, 'rgba(48,32,12,.92)', u);
      }
      if (op.name) { const fs = (cap ? 7.5 : 6.2) * Math.sqrt(u); p.font = `${cap ? 900 : 700} ${fs}px Cinzel, Georgia, serif`;
        const tw = p.measureText(op.name).width, margin = 10.5 * u;                       // keep the name inside the frame
        const lx = clamp(ox, margin + tw / 2, w - margin - tw / 2), ty = Math.max(margin + fs * 0.6, oy - rr - 2.6 * u);
        p.lineWidth = Math.max(1, fs * 0.16); p.strokeStyle = 'rgba(247,236,208,.68)'; p.strokeText(op.name, lx, ty); // soft paper halo, not a sticker
        p.fillStyle = cap ? '#7a4e12' : 'rgba(54,37,15,.95)'; p.fillText(op.name, lx, ty); }
    }
    p.textAlign = 'left';
    // compass ROSE, bottom-right
    const rg = this.roseGeom(S, w > 300);
    this.drawCompassRose(p, rg.cx * u, rg.cy * u, rg.R * u, w > 300, u);
  }

  /** A smooth, deterministic hand-drawn island blob: 12 seeded-wobble vertices traced through their midpoints
   *  with quadratics. Same seed → identical path, so shadow/surf/fill/coast passes register exactly. */
  private islandPath(p: CanvasRenderingContext2D, cx: number, cy: number, r: number, seed: number): void {
    const N = 12; let s = (seed * 2654435761) >>> 0; const rnd = (): number => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
    const pts: [number, number][] = [];
    for (let i = 0; i < N; i++) { const a = (i / N) * Math.PI * 2, rr = r * (0.72 + rnd() * 0.52); pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr * 0.94]); }
    const mid = (i: number): [number, number] => [(pts[i]![0] + pts[(i + 1) % N]![0]) / 2, (pts[i]![1] + pts[(i + 1) % N]![1]) / 2];
    const m0 = mid(N - 1); p.beginPath(); p.moveTo(m0[0], m0[1]);
    for (let i = 0; i < N; i++) { const m = mid(i); p.quadraticCurveTo(pts[i]![0], pts[i]![1], m[0], m[1]); }
    p.closePath();
  }

  /** A small ink ANCHOR glyph (ring, shank, stock, curved flukes) centred at (cx,cy), visual radius ~s. */
  private drawAnchor(p: CanvasRenderingContext2D, cx: number, cy: number, s: number, color: string, u: number): void {
    const TAU = Math.PI * 2; p.save(); p.translate(cx, cy); p.strokeStyle = color; p.lineWidth = Math.max(0.7, 1.05 * u); p.lineCap = 'round';
    const top = -s * 0.9, bot = s * 0.85;
    p.beginPath(); p.arc(0, top, s * 0.24, 0, TAU); p.stroke();                                     // ring
    p.beginPath(); p.moveTo(0, top + s * 0.24); p.lineTo(0, bot); p.stroke();                        // shank
    p.beginPath(); p.moveTo(-s * 0.5, top + s * 0.62); p.lineTo(s * 0.5, top + s * 0.62); p.stroke(); // stock
    p.beginPath(); p.moveTo(-s * 0.62, bot * 0.55); p.quadraticCurveTo(-s * 0.62, bot, 0, bot); p.quadraticCurveTo(s * 0.62, bot, s * 0.62, bot * 0.55); p.stroke(); // flukes
    p.beginPath(); p.moveTo(-s * 0.62, bot * 0.55); p.lineTo(-s * 0.82, bot * 0.32); p.moveTo(s * 0.62, bot * 0.55); p.lineTo(s * 0.82, bot * 0.32); p.stroke();     // fluke tips
    p.restore();
  }

  /** A filled star (used for the Capital medallion). `points` spikes between `outer`/`inner` radii. */
  private drawStar(p: CanvasRenderingContext2D, cx: number, cy: number, points: number, outer: number, inner: number, fill: string, stroke: string, u: number): void {
    p.beginPath();
    for (let i = 0; i < points * 2; i++) { const a = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2, rr = i % 2 ? inner : outer, x = cx + Math.cos(a) * rr, y = cy + Math.sin(a) * rr; if (i) p.lineTo(x, y); else p.moveTo(x, y); }
    p.closePath(); p.fillStyle = fill; p.fill(); p.lineWidth = Math.max(0.5, 0.8 * u); p.strokeStyle = stroke; p.stroke();
  }

  /** An ornate wind ROSE: paper disc, two ink rings, (ornate:) 72 degree ticks + Cinzel cardinals + a gilt N
   *  topper, a 16-point two-tone star and a gold hub. Compact form (minimap) drops the ticks/letters. */
  private drawCompassRose(p: CanvasRenderingContext2D, cx: number, cy: number, R: number, ornate: boolean, u: number): void {
    const TAU = Math.PI * 2; p.save(); p.globalAlpha = ornate ? 0.92 : 0.82;
    const disc = p.createRadialGradient(cx, cy, 0, cx, cy, R * 1.15);
    disc.addColorStop(0, 'rgba(238,226,192,.55)'); disc.addColorStop(1, 'rgba(238,226,192,0)');
    p.fillStyle = disc; p.beginPath(); p.arc(cx, cy, R * 1.15, 0, TAU); p.fill();
    p.strokeStyle = 'rgba(58,40,16,.7)'; p.lineWidth = Math.max(0.6, 0.9 * u); p.beginPath(); p.arc(cx, cy, R, 0, TAU); p.stroke();
    p.lineWidth = Math.max(0.4, 0.6 * u); p.beginPath(); p.arc(cx, cy, R * 0.82, 0, TAU); p.stroke();
    if (ornate) { p.strokeStyle = 'rgba(58,40,16,.5)';
      for (let i = 0; i < 72; i++) { const a = (i / 72) * TAU, long = i % 9 === 0, r0 = R * (long ? 0.82 : 0.9);
        p.lineWidth = long ? Math.max(0.6, 0.9 * u) : Math.max(0.3, 0.5 * u);
        p.beginPath(); p.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0); p.lineTo(cx + Math.cos(a) * R, cy + Math.sin(a) * R); p.stroke(); } }
    const pt = (ang: number, len: number, halfw: number): void => {
      const tx = cx + Math.cos(ang) * len, ty = cy + Math.sin(ang) * len, perp = ang + Math.PI / 2;
      const lx = cx + Math.cos(perp) * halfw, ly = cy + Math.sin(perp) * halfw, rx = cx - Math.cos(perp) * halfw, ry = cy - Math.sin(perp) * halfw;
      p.beginPath(); p.moveTo(tx, ty); p.lineTo(lx, ly); p.lineTo(cx, cy); p.closePath(); p.fillStyle = 'rgba(245,234,204,.96)'; p.fill();
      p.beginPath(); p.moveTo(tx, ty); p.lineTo(rx, ry); p.lineTo(cx, cy); p.closePath(); p.fillStyle = 'rgba(46,31,12,.9)'; p.fill();
      p.beginPath(); p.moveTo(tx, ty); p.lineTo(lx, ly); p.lineTo(cx, cy); p.lineTo(rx, ry); p.closePath();
      p.lineWidth = Math.max(0.35, 0.45 * u); p.strokeStyle = 'rgba(46,31,12,.65)'; p.stroke();
    };
    for (let i = 0; i < 4; i++) { const a = -Math.PI / 2 + Math.PI / 4 + i * (Math.PI / 2); pt(a, R * 0.58, R * 0.12); } // intercardinals
    for (const a of [-Math.PI / 2, 0, Math.PI / 2, Math.PI]) pt(a, R * 0.92, R * 0.16);                                  // cardinals
    p.beginPath(); p.arc(cx, cy, Math.max(1, R * 0.11), 0, TAU); p.fillStyle = '#e0b04a'; p.fill();
    p.lineWidth = Math.max(0.3, 0.5 * u); p.strokeStyle = 'rgba(80,52,14,.9)'; p.stroke();
    if (ornate) {
      p.fillStyle = 'rgba(46,31,12,.92)'; p.textAlign = 'center'; p.textBaseline = 'middle'; p.font = `700 ${Math.max(6, 7.5 * u)}px Cinzel, Georgia, serif`;
      const lr = R * 1.16;
      for (const [ch, a] of [['N', -Math.PI / 2], ['E', 0], ['S', Math.PI / 2], ['W', Math.PI]] as [string, number][]) {
        const x = cx + Math.cos(a) * lr, y = cy + Math.sin(a) * lr;
        p.lineWidth = Math.max(1, 2 * u); p.strokeStyle = 'rgba(240,228,196,.9)'; p.strokeText(ch, x, y); p.fillStyle = 'rgba(46,31,12,.92)'; p.fillText(ch, x, y); }
      p.save(); p.translate(cx, cy - R * 0.92 - R * 0.13); p.rotate(Math.PI / 4); p.fillStyle = '#d9a63e'; p.fillRect(-R * 0.08, -R * 0.08, R * 0.16, R * 0.16); p.restore();
    } else {
      p.fillStyle = 'rgba(46,31,12,.85)'; p.textAlign = 'center'; p.textBaseline = 'middle'; p.font = `700 ${Math.max(5, 5.5 * u)}px Cinzel, Georgia, serif`;
      p.fillText('N', cx, cy - R * 1.18);
    }
    p.restore();
  }

  /** The chart's ornate FRAME (drawn last, over the markers): an inset wood band, a twisted gold rope and four
   *  brass corner studs — all authored in the 132-unit layout so it scales with the chart. */
  private drawChartFrame(ctx: CanvasRenderingContext2D, S: number): void {
    const TAU = Math.PI * 2; ctx.save(); ctx.setLineDash([]);
    const bw = 5.5;
    const wg = ctx.createLinearGradient(0, 0, S, S); wg.addColorStop(0, '#4a3016'); wg.addColorStop(0.5, '#6b4522'); wg.addColorStop(1, '#3a2410');
    this.roundedChartPath(ctx, S, 0); ctx.lineWidth = bw * 2; ctx.strokeStyle = wg; ctx.stroke();              // wood band (clip shows inner half)
    this.roundedChartPath(ctx, S, bw * 0.5); ctx.lineWidth = 0.8; ctx.strokeStyle = 'rgba(180,130,70,.4)'; ctx.stroke(); // top highlight
    this.roundedChartPath(ctx, S, bw); ctx.lineWidth = 1; ctx.strokeStyle = 'rgba(20,10,2,.5)'; ctx.stroke();  // inner shadow
    const rp = bw + 1.5;
    this.roundedChartPath(ctx, S, rp); ctx.lineWidth = 2.6; ctx.strokeStyle = '#c8a05a'; ctx.stroke();          // rope base
    this.roundedChartPath(ctx, S, rp); ctx.setLineDash([2.4, 2.4]); ctx.lineWidth = 2.8; ctx.strokeStyle = 'rgba(92,60,24,.55)'; ctx.stroke(); ctx.setLineDash([]); // coils
    this.roundedChartPath(ctx, S, bw + 3.5); ctx.lineWidth = 0.6; ctx.strokeStyle = 'rgba(58,40,16,.4)'; ctx.stroke(); // ink neat-line (classic chart double border)
    const ci = bw + 1.5;
    for (const [cxp, cyp] of [[ci, ci], [S - ci, ci], [S - ci, S - ci], [ci, S - ci]] as [number, number][]) {
      const g = ctx.createRadialGradient(cxp - 1, cyp - 1, 0, cxp, cyp, 4); g.addColorStop(0, '#f0d48a'); g.addColorStop(0.6, '#c99a3a'); g.addColorStop(1, '#7a5416');
      ctx.beginPath(); ctx.arc(cxp, cyp, 3.4, 0, TAU); ctx.fillStyle = g; ctx.fill();
      ctx.lineWidth = 0.8; ctx.strokeStyle = 'rgba(50,32,8,.8)'; ctx.stroke();
      ctx.beginPath(); ctx.arc(cxp, cyp, 1.2, 0, TAU); ctx.fillStyle = 'rgba(60,38,10,.7)'; ctx.fill();
    }
    ctx.restore();
  }
}

function hudEsc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] ?? c));
}

/** The Sea Journal "is there anything new?" gate — the feed now lives ONLY in the Captain's Book (Ship's
 *  Log chapter), which uses this signature to light the 📖 MENU dot. A stable signature of the feed
 *  (size + newest + oldest visible line) that any fresh deed shifts but a plain re-render does not. */
export function journalSignature(entries: { kind: number; text: string }[]): string {
  return entries.length ? `${entries.length}|${entries[0]?.text ?? ''}|${entries[entries.length - 1]?.text ?? ''}` : '';
}
/** The feed has unread content when it is non-empty AND its signature differs from the last-opened one. */
export function journalHasNew(sig: string, seen: string): boolean {
  return sig !== '' && sig !== seen;
}

/** Compact number for HUD chips: 4200 -> "4.2k", 900 -> "900". */
function fmtK(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(n < 10000 ? 1 : 0).replace(/\.0$/, '')}k` : `${Math.round(n)}`;
}
